import type {
  ICreateClaimBodyV2,
  IListStylesQueryV2,
  IUpdateClaimBodyV2,
} from '@api/types/claims';
import type { IBulkCreateDppBody } from '@api/types/dpp';
import { fetch as fetchRaw } from '@lib/fetch';
import { getDefinedUrlSearchParams } from '@lib/util';
import type {
  AcceptUserCompanyTermsBody,
  AdminAllowedOrigin,
  AdminAllowedOriginsListQuery,
  AdminAllowedOriginsListResponse,
  AdminArchivedCompanyDocumentsListResponse,
  AdminAvailableCompaniesQuery,
  AdminAvailableCompaniesResponse,
  AdminBulkDeleteAllowedOriginsBody,
  AdminBulkRestoreCompanyDocumentsBody,
  AdminBulkRestoreCompanyDocumentsResponse,
  AdminCollaborationStatus,
  AdminCollaborationStatusesListQuery,
  AdminCollaborationStatusesListResponse,
  AdminCompaniesListByPlatformFeatureIdQuery,
  AdminCompaniesListQuery,
  AdminCompaniesNetworkResponse,
  AdminCompanyDocumentsListQuery,
  AdminCompanyDocumentsListResponse,
  AdminCreateAllowedOriginBody,
  AdminCreateCollaborationStatusBody,
  AdminCreateFacilityProcessBody,
  AdminCreateRelationshipTypeBody,
  AdminCreateUserBody,
  AdminCreateUserResponse,
  AdminCreateWebshopBody,
  AdminDevelopmentTagFeaturesQuery,
  AdminDevelopmentTagFeaturesResponse,
  AdminDevelopmentTagsResponse,
  AdminFacilityProcess,
  AdminFacilityProcessesListQuery,
  AdminFacilityProcessRow,
  AdminProductionProcess,
  AdminProductionProcessesListQuery,
  AdminProductionProcessRow,
  AdminRelationshipType,
  AdminRelationshipTypesListResponse,
  AdminTracingOrdersQuery,
  AdminTracingOrdersResponse,
  AdminUpdateAllowedOriginBody,
  AdminUpdateCollaborationStatusBody,
  AdminUpdateFacilityProcessBody,
  AdminUpdateProductionProcessBody,
  AdminUpdateRelationshipTypeBody,
  AdminUpdateWebshopBody,
  AdminUserGetResponse,
  AdminUserImpersonateQuery,
  AdminUserImpersonateResponse,
  AdminUserPatchBody,
  AdminUserPatchResponse,
  AdminUsersBulkPatchBody,
  AdminUsersBulkPatchResponse,
  AdminUsersListByCompanyResponse,
  AdminUsersListQuery,
  AdminUsersListToImpersonateQuery,
  AdminUserUpdateBody,
  AdminUserUpdateResponse,
  AdminWebshop,
  AdminWebshopConstantsResponse,
  AdminWebshopListRow,
  AdminWebshopsListQuery,
  AssetAccessControlResponse,
  AttachmentTemplateMismatch,
  BulkArchiveCompanyDocumentsBody,
  BulkCompanyProfileItem,
  BulkCreateCompanyDocumentsBody,
  BulkCreateCompanyDocumentsResponse,
  BulkOrderDocumentsBody,
  BulkPatchOrdersBody,
  BulkRefreshCalculationBody,
  CheckAttachmentTemplatesBody,
  CheckOrderNumbersBody,
  CollaborationStatusesListQuery,
  CollaborationStatusesListResponse,
  CompaniesListQuery,
  CompanyBulkUnlinkBody,
  CompanyDocumentDetail,
  CompanyLinkBody,
  CompanyLinkResponse,
  CompanyProfileResponse,
  CompanyTechInsightsResponse,
  CompanyToSwitchRow,
  CompanyUnlinkBody,
  CreateCompanyBody,
  CreateCompanyResponse,
  CreateNetworkGraphLinkBody,
  CreateOrderTypeBody,
  CreateUserBody,
  CreateUserCompanyBody,
  CreateUserCompanyResponse,
  CreateUserResponse,
  DetachPurchaseOrderBody,
  DownloadOrderDocumentsQuery,
  FacilityProcessesDropdownResponse,
  GetTracingNodeDetailQuery,
  ImpactAreaListItem,
  ImpactAreasHierarchyResponse,
  LinkSalesOrderBody,
  ListCompanyDocumentsQuery,
  ListDataPointAnswersQuery,
  ListDataPointAnswersResponse,
  ListOrderParticipantsBody,
  ListTemplatesQuery,
  ListTemplatesResponse,
  ListUserCompaniesByCompanyIdsQuery,
  ListUserCompaniesResponse,
  MakeCompanyDocumentPublicBody,
  MeResponse,
  NetworkCountriesResponse,
  NetworkGraph,
  NetworkGraphCreated,
  NetworkGraphLink,
  NetworkGraphQuery,
  OrderParticipant,
  PatchCompanyDocumentBody,
  PatchedOrder,
  PatchNetworkGraphNodesBody,
  PatchPlatformFeatureBody,
  PatchPreferencesBody,
  PatchPreferencesResponseView,
  PatchPurchaseOrderBody,
  PatchPurchaseOrderResponse,
  PatchSalesOrderBody,
  PatchSalesOrderResponse,
  PatchTemplateAnswerBody,
  PlatformFeaturesListQuery,
  RelationshipTypesListQuery,
  RelationshipTypesListResponse,
  RemoveOriginBody,
  ShareCompanyDocumentBody,
  ShareCompanyDocumentResponse,
  ToggleArchiveOrdersBody,
  TracingNode,
  TracingNodeDetail,
  TracingOrderType,
  UpdateUserBody,
  UpdateUserCompanyBody,
  UpdateUserCompanyResponse,
  UserCompanyByCompanyIdRow,
  UserDetailResponse,
  UserPreferencesResultView,
  UsersListQuery,
  UsersListResponse,
} from '@retracedgmbh/modules';
import type {
  AppNotificationRow,
  MarkNotificationsBody,
  NotificationsListQuery,
} from '@retracedgmbh/modules/app-notifications/contracts';
import type { AuditTypesListQuery } from '@retracedgmbh/modules/audits/contracts';
import type {
  AdminApiKeysListQuery,
  AdminCreateApiKeyBody,
  AdminDeleteApiKeyBody,
  ApiKeysListQuery,
  CreateApiKeyBody,
  DeleteApiKeyBody,
} from '@retracedgmbh/modules/authentication/contracts';
import type {
  AdminBadge,
  AdminUpdateBadgeBody,
  BadgeListItem,
} from '@retracedgmbh/modules/badges/contracts';
import type {
  AdminBulkRemoveC2cDefaultQuestionsBody,
  AdminC2cDefaultQuestionsListQuery,
  AdminC2cDefaultQuestionsListResponse,
  AdminCreateC2cDefaultQuestionBody,
  AdminUpdateC2cDefaultQuestionBody,
  C2cBulkRemoveQuestionsBody,
  C2cCompanyAnswersBody,
  C2cCompanyAnswersResponse,
  C2cCompanyQuestionsListQuery,
  C2cCompanyQuestionsListResponse,
  C2cCreateQuestionBody,
  C2cQuestion,
  C2cQuestionsListQuery,
  C2cQuestionsListResponse,
  C2cUpdateQuestionBody,
} from '@retracedgmbh/modules/c2c-custom-questions/contracts';
import type {
  BulkUpdateActionsRequest,
  LinkActionsRequest,
  LinkActionsResponse,
  UnlinkActionsRequest,
  UnlinkActionsResponse,
} from '@retracedgmbh/modules/capa/contracts';
import type {
  AdminCertificationRulesListQuery,
  AdminCertificationRulesListResponse,
  AdminCreateCertificationRuleBody,
  AdminUpdateCertificationRuleBody,
  CertificationRule,
} from '@retracedgmbh/modules/certification-rules/contracts';
import type {
  AddClaimCommentBody,
  AdminAddClaimCommentBody,
  AdminClaimIdsBody,
  AdminClaimsListQuery,
  AdminCreateClaimBody,
  ClaimIdsBody,
  ClaimsListQuery,
  ClaimVersionsQuery,
  ListClaimCommentsQuery,
} from '@retracedgmbh/modules/claims/contracts';
import type {
  AdminClassificationDataFieldPatchBody,
  AdminClassificationDataFieldsListQuery,
  AdminClassificationDataFieldsListResponse,
  AdminClassificationEntitiesResponse,
  ClassificationDataField,
} from '@retracedgmbh/modules/classification-data-fields/contracts';
import type {
  AdminClassificationImpactAreaCreateBody,
  AdminClassificationImpactAreaPatchBody,
  AdminClassificationImpactAreasBulkDeleteBody,
  AdminClassificationImpactAreasListQuery,
  AdminClassificationImpactAreasListResponse,
  AdminImpactAreaDataFieldsResponse,
  ClassificationImpactArea,
} from '@retracedgmbh/modules/classification-impact-areas/contracts';
import type {
  CreateMaterialMappingsBody,
  CreateProductTypeMappingsBody,
  DeleteMaterialMappingsBody,
  DeleteProductTypeMappingsBody,
  MaterialMappingsListQuery,
  ProductTypeMappingsListQuery,
  UpdateMaterialMappingBody,
  UpdateProductTypeMappingBody,
} from '@retracedgmbh/modules/constants-mapping/contracts';
import type {
  AuditImpactAreasQuery,
  AuditMetricsQuery,
  CertificationExportBody,
  CertificationMetricsQuery,
  TracingDocumentsListQuery,
  TracingDocumentsQuery,
  TracingExportBody,
  TracingMetricsQuery,
} from '@retracedgmbh/modules/dashboard-analytics/contracts';
import type {
  AdminCreateFiberProductionFactorsBody,
  AdminCreateFiberProductionProcessBodyInput,
  AdminFiberCocModel,
  AdminFiberCocModelsQuery,
  AdminFiberCocModelsResponse,
  AdminFiberProductionFactor,
  AdminFiberProductionFactorQuery,
  AdminFiberProductionFactorsQueryInput,
  AdminFiberProductionFactorsResponse,
  AdminFiberProductionProcess,
  AdminFiberProductionProcessesQuery,
  AdminFiberProductionProcessesResponse,
  AdminFiberSetting,
  AdminPatchFiberProductionFactorBody,
  AdminPatchFiberProductionProcessBody,
  AdminUpdateFiberCocModelBody,
  AdminUpdateFiberProductionProcessBody,
  AdminUpdateFiberSettingBody,
} from '@retracedgmbh/modules/fiber/contracts';
import type { HelpArticle } from '@retracedgmbh/modules/help-articles/contracts';
// Contracts subpath only — never the module root from a frontend package.
import type {
  CreateOrderTagBody,
  OrderTag,
} from '@retracedgmbh/modules/order-hub/contracts';
import type {
  AdminCreateMaterialBody,
  AdminCreateProductCategoryBody,
  AdminCreateProductCategoryFieldBody,
  AdminMaterial,
  AdminMaterialRow,
  AdminMaterialsListQuery,
  AdminPatchMaterialBody,
  AdminPatchProductCategoryBody,
  AdminPatchProductCategoryFieldBody,
  AdminPatchTagIdentificationBody,
  AdminProductCategoriesListQuery,
  AdminProductCategoriesListResponse,
  AdminProductCategory,
  AdminProductCategoryField,
  AdminProductCategoryFieldsListQuery,
  AdminProductCategoryFieldsListResponse,
  AdminSupplyChain,
  AdminSupplyChainsListQuery,
  AdminSupplyChainsListResponse,
  AdminTagIdentificationRow,
  AdminTagIdentificationsListQuery,
  AdminTagIdentificationsListResponse,
  AdminUpdateMaterialBody,
  AdminUpdateProductCategoryBody,
  AdminUpdateProductCategoryFieldBody,
  CompanyProductsQuery,
  CompanyProductsResponse,
  CreatedNetworkProductsQuery,
  CreateProductPlacementBody,
  CreateProductPropertyBody,
  MaterialsListQuery,
  ProductCategoriesListQueryInput,
  ProductCategoryFieldsListResponse,
  ProductCategoryOption,
  ProductPlacement,
  ProductPlacementsListResponse,
  ProductPropertiesListResponse,
  ProductProperty,
  ProductSuppliersAndVendorsResponse,
  ProductType,
  ProductTypesListQueryInput,
  SupplyChain,
  SupplyChainsListQuery,
  SupplyChainsListResponse,
} from '@retracedgmbh/modules/products/contracts';
import type {
  AdminDeleteQuestionTemplatesBody,
  AdminQuestionAssetLinksListQuery,
  AdminQuestionAssetLinksListResponse,
  AdminQuestionnaireTemplatesListQuery,
  AdminQuestionTemplate,
  AdminQuestionTemplateCategoriesListResponse,
  AdminQuestionTemplateCategory,
  AdminQuestionTemplatesListQuery,
  AdminQuestionTemplatesListResponse,
  AdminSaveQuestionTemplateBodyInput,
  AdminSaveQuestionTemplateCategoryBodyInput,
  BulkArchiveQuestionnairesBody,
  BulkCreateQuestionnaireRequestsBody,
  BulkDeleteQuestionnaireRequestsBody,
  BulkDeleteQuestionnairesBody,
  BulkDeleteQuestionnaireTemplatesBody,
  BulkUnarchiveQuestionnairesBody,
  CompanyDataPointAnswer,
  CreateQuestionnaireBody,
  CreateQuestionnaireRequestAuditBody,
  CreateQuestionnaireRequestAuditResponse,
  CreateQuestionnaireRequestCertificateBody,
  CreateQuestionnaireRequestCertificateResponse,
  CreateQuestionnaireRequestDocumentsBody,
  CreateQuestionnaireRequestDocumentsResponse,
  CreateQuestionnaireTemplateBody,
  CreateQuestionResponseBody,
  ExportQuestionnaireBody,
  QuestionnaireBuilderDetail,
  QuestionnaireDataPointList,
  QuestionnaireDataPointTemplateGroupList,
  QuestionnaireRequestAssetFileResponse,
  QuestionnaireRequestAssetsQuery,
  QuestionnaireRequestAssetsResponse,
  QuestionnaireRequestDetail,
  QuestionnaireRequestsCompanyListQuery,
  QuestionnaireRequestsListQuery,
  QuestionnairesListQuery,
  RemindQuestionnaireRequestsBody,
  ToggleAdminQuestionnaireTemplateBody,
  UpdateQuestionnaireBuilderBody,
  UpdateQuestionnaireRequestStatusBody,
  UpdateQuestionnaireRequestsDueDateBody,
  UpdateQuestionResponseStatusBody,
} from '@retracedgmbh/modules/questionnaires/contracts';
import type {
  AssessmentBlueprintByCompanyRow,
  AssessmentBlueprintDetailResource,
  AssessmentBlueprintHistoryEntry,
  AssessmentBlueprintListRow,
  AssessmentBlueprintsByCompanyListQuery,
  AssessmentBlueprintsListQuery,
  AssessmentDetailResource,
  AssessmentDetailRow,
  AssessmentHistoryRow,
  AssessmentMitigation,
  AssessmentNoteWithRiskTitle,
  BlueprintCompanyPrioritizationRow,
  BlueprintCompanyRow,
  BulkArchiveAssessmentBlueprintsBody,
  BulkCreateBlueprintCompaniesBody,
  CalculateAssessmentBody,
  CalculateByStandardAssessmentBody,
  CertificationOption,
  CertificationRow,
  CopyRuleTemplatesBody,
  CreateAssessmentBlueprintBody,
  CreateAssessmentNoteBody,
  CreateMitigationBody,
  CreateRiskBody,
  CreateRiskGrossBody,
  CreateRuleTemplateBody,
  GetAssessmentDetailsQuery,
  GetAssessmentHistoryQuery,
  GetAssessmentLevelNotesQuery,
  GetBlueprintCompaniesQuery,
  GetBlueprintPrioritizationDetailsQuery,
  GetPrioritizationDetailsQuery,
  GetRiskAuditTypesQuery,
  GetRiskProfilePrioritizationDetailsQuery,
  ListAssessmentMitigationsQuery,
  ListAssessmentNotesQuery,
  ListCertificationsQuery,
  ListRiskGrossQuery,
  ListRiskImpactAreasQuery,
  MatchedRulesResponse,
  PrioritizationDetailRow,
  RiskAuditTypeRow,
  RiskDatapointRow,
  RiskGrossListItem,
  RiskImpactAreaRow,
  RiskLandscapeCompaniesListQuery,
  RiskLandscapeCompanyRow,
  RiskLandscapeCountriesListQuery,
  RiskLandscapeCountryRow,
  RiskLandscapeListQuery,
  RiskLandscapeListRow,
  RiskLandscapeMapCountry,
  RiskLandscapeMapQuery,
  RiskLandscapeResource,
  RiskListRow,
  RiskMetricRow,
  RiskProfilePrioritizationRow,
  RiskScoresResource,
  RisksListQuery,
  RuleTemplateListRow,
  RuleTemplateResource,
  RuleTemplateRowResource,
  RuleTemplatesListQuery,
  StandardAssessment,
  UpdateAssessmentBlueprintBody,
  UpdateRiskBody,
  UpdateRiskGrossBody,
  UpdateRuleTemplateBody,
} from '@retracedgmbh/modules/risk-analysis/contracts';
import type {
  AdminCreateStandardBodyInput,
  AdminStandard,
  AdminUpdateStandardBodyInput,
  StandardOption,
  StandardsListQueryInput,
} from '@retracedgmbh/modules/standards/contracts';
import type {
  BulkDeleteTracingDocumentsBody,
  ListMyTracingDocumentsQuery,
  ListMyTracingDocumentsResponse,
  RemoveNodeDocumentBody,
  ReplacedTracingDocument,
  ReplaceTracingDocumentBody,
  TracingDocumentPurchaseOrder,
  TracingDocumentStatistic,
} from '@retracedgmbh/modules/tracing-documents/contracts';
import type {
  AdminConfiguredCompanyIdsQuery,
  AdminConfiguredCompanyIdsResponse,
  AdminDynamicCompanyTabsBody,
  AdminDynamicCompanyTabsConfig,
} from '@retracedgmbh/modules/ux-configs/contracts';
import type {
  IAction,
  IActionHistory,
  IActionPatch,
  ICapFinding,
  ICapPlan,
  IListActionsResponse,
} from '@views/capa-v2/types';
import { backend } from '../services';

export type ApiResponse<T> = Promise<{
  data: { data: T };
  error: Error;
  status: number;
}>;

const fetch = (url, ...args) => fetchRaw([backend.url, url], ...args);

// Builds the `?updateLinkedActions=` suffix for the endpoints that can push a
// plan change down to its actions. Empty string when the caller has no answer
// to send, so those requests stay byte-identical to before.
const updateLinkedActionsQuery = (updateLinkedActions?: boolean): string =>
  updateLinkedActions === undefined
    ? ''
    : `?updateLinkedActions=${updateLinkedActions}`;

// Throws a real Error (preserving the request id for ErrorToast and the
// machine-readable `code` for structured-error handling, e.g. the amfori
// read-only guard) when the API returned an error response, so mutateAsync
// rejects instead of resolving with an error payload. Returns the response
// unchanged on success.
function throwIfApiError<
  T extends {
    error?: { message?: string; code?: string; reqId?: string } | null;
  },
>(response: T, fallbackMessage: string): T {
  if (response.error) {
    throw Object.assign(new Error(response.error.message || fallbackMessage), {
      code: response.error.code,
      reqId: response.error.reqId,
    });
  }
  return response;
}

export function requestCode(body) {
  return fetch('/authentication/request-code', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function login(body) {
  return fetch('/authentication/login', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** A page of the caller's company's keys — `{ data, meta: { …, totalCount } }`. */
export function listApiKeys(queryParams: Partial<ApiKeysListQuery>) {
  return fetch(`/api-keys?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/** Mints a key for the caller's company. The response is `{ data: CreateApiKeyResponse }` —
 *  the raw key is in it, and is never returned again. */
export function createApiKey(body: CreateApiKeyBody) {
  return fetch('/api-keys', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** A page of the named company's keys, for an operator — `{ data, meta: { …, totalCount } }`. */
export function adminListApiKeys(queryParams: Partial<AdminApiKeysListQuery>) {
  return fetch(`/admin/api-keys?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/** An operator mints a key for the company in the body — `{ data: CreateApiKeyResponse }`. */
export function adminCreateApiKey(body: AdminCreateApiKeyBody) {
  return fetch('/admin/api-keys', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** An operator revokes a key of the company in the body. 204, no body. */
export function adminDeleteApiKey(body: AdminDeleteApiKeyBody) {
  return fetch('/admin/api-keys', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/** Revokes the caller's company's key with that label. 204, no body. */
export function deleteApiKey(body: DeleteApiKeyBody) {
  return fetch('/api-keys', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function authenticationByDomain(email: string) {
  const domain = email.split('@')[1];
  if (!domain) throw new Error('Invalid email address');
  return fetch(
    `/authentication/single-sign-on/${encodeURIComponent(domain)}?login_hint=${encodeURIComponent(email)}`,
    {
      method: 'GET',
    },
  );
}

/**
 * Whether the domain must sign in with SSO and through which path — `native`
 * once Retraced staff switched its organization off Ory.
 */
export function fetchSsoStatus(domain: string) {
  return fetch(
    `/authentication/sso-status?domain=${encodeURIComponent(domain)}`,
    { method: 'GET' },
  );
}

export function exchangeSsoCode(code: string) {
  return fetch(`/authentication/sso-code?code=${encodeURIComponent(code)}`, {
    method: 'GET',
  });
}

export function logout() {
  return fetch('/authentication/logout', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function refreshToken(token: string) {
  return fetchRaw([backend.url, '/authentication/refresh-token'], {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({}),
  });
}

export function adminFilesList(body) {
  return fetch('/admin/files/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function adminFilesGetById(id) {
  return fetch(`/admin/files/${id}`, {
    method: 'GET',
  });
}

export function adminFilesAIProcessingJobsList(body) {
  return fetch('/admin/files/ai-processing-jobs/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function uploadURLPost(body) {
  return fetch('/system/files/', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function styleDocumentsBulkCreate(fileIds: string[]) {
  return fetch('/style-documents', {
    method: 'POST',
    body: JSON.stringify(fileIds.map(fileId => ({ fileId }))),
  });
}

export function styleDocumentsSummary() {
  return fetch('/style-documents/summary', { method: 'GET' });
}

export function styleDocumentsPendingReview(params) {
  const qs = new URLSearchParams();
  if (params?.page !== undefined) qs.set('page', String(params.page));
  if (params?.count !== undefined) qs.set('count', String(params.count));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetch(`/style-documents/pending-review${suffix}`, { method: 'GET' });
}

export interface AcceptReviewItem {
  id: string;
  fileId: string;
  userDocumentType: {
    documentType: string;
    userModified: boolean;
  };
  userStyles: Array<{
    styleId: string;
    userAdded: boolean;
  }>;
}

export function acceptStyleDocumentAiResults(items: AcceptReviewItem[]) {
  return fetch('/style-document-ai-results', {
    method: 'PATCH',
    body: JSON.stringify(items),
  });
}

export const getCertificatesDrilldownData = body => {
  return fetch('/dashboard-analytics/certification-drilldown/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function adminChatWithFile(body) {
  return fetch('/admin/files/chat-with-file', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function adminFilesAiProcessingJobsCreate(body) {
  return fetch('/admin/files/ai-processing-jobs/create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const getAuditsDrilldownData = body => {
  return fetch('/dashboard-analytics/audits-metrics/drill-down/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function getEmailBounceTrackingList(body) {
  return fetch('/system/emails/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getEmailBounceTrackingListDetail(id) {
  return fetch(`/system/emails/${id}`, {
    method: 'GET',
  });
}

export function slcpAssessmentsArchive(body) {
  return fetch('/slcp/assessments/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function auditTypesList(queryParams: Partial<AuditTypesListQuery>) {
  return fetch(`/audit-types?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function adminAuditTypesList(queryParams: Partial<AuditTypesListQuery>) {
  return fetch(`/admin/audit-types?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function slcpAssessmentsList(queryParams) {
  return fetch(`/slcp/assessments?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function questionnairesList(
  queryParams: Partial<QuestionnairesListQuery>,
) {
  return fetch(`/questionnaires?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Saves an admin badge edit — the edit drawer and the translations drawer on
 * the badges page. `name`, `description` and `claimingRequirements` are
 * required; the localized fields and `translations` keep their stored value
 * when left out, and other keys are ignored. Answers `{ data, metadata }`; the
 * retired legacy route sent the bare badge.
 */
export function adminBadgeUpdate(id: string, body: AdminUpdateBadgeBody) {
  return fetch(`/admin/badges/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminBadge>;
}

/**
 * Every catalogue badge, in catalogue order — the badges directory and the
 * badges management table. Unpaginated, and without `claimingRequirements`
 * (read one badge for that). Answers `{ data, metadata }`; the retired legacy
 * route sent a bare array.
 */
export function badgesList() {
  return fetch('/badges', {
    method: 'GET',
  }) as ApiResponse<BadgeListItem[]>;
}

/**
 * One catalogue badge — the badge detail page, the badges edit and
 * translations drawers, and the claim-a-badge modals on the product and style
 * pages. Answers `{ data, metadata }` and 404 `invalid_badge_id` for an
 * unknown id; the retired legacy route sent the bare badge.
 */
export function badgeGet(id: string) {
  return fetch(`/badges/${encodeURIComponent(id)}`, {
    method: 'GET',
  }) as ApiResponse<AdminBadge>;
}

/**
 * A page of the standards catalogue — the standards management table, the
 * standards directory, the certificate standard pickers and the
 * certified-materials order-line standards. Each row is the full standard.
 * Replaces `api.standardsGet`, whose legacy route answered `{ data, meta }`;
 * this answers `{ data, metadata: { success, pagination } }` — adapt it for
 * `useTable` with `toTablePaginationMeta`.
 */
export function standardsList(
  queryParams: Partial<StandardsListQueryInput>,
): Promise<{
  data: {
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    data: AdminStandard[];
  };
  error: Error;
  status: number;
}> {
  return fetch(`/standards?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Every standard as `{ id, name, type }` — the claim and badge pickers.
 * Replaces `api.standardsGet({ isDropDown: true })`. Answers
 * `{ data, metadata }`.
 */
export function standardsDropdown() {
  return fetch('/standards/dropdown', {
    method: 'GET',
  }) as ApiResponse<StandardOption[]>;
}

/**
 * One catalogue standard — the standards edit and translations drawers, the
 * standard detail page and the claim forms' `standardId` preload. Answers
 * `{ data, metadata }` and 404 `invalid_standard_id` for an unknown id; the
 * retired legacy route sent the bare standard and an empty 404.
 */
export function standardGet(id: string) {
  return fetch(`/standards/${encodeURIComponent(id)}`, {
    method: 'GET',
  }) as ApiResponse<AdminStandard>;
}

/**
 * Creates a catalogue standard — "new standard" in the standards drawer.
 * `name`, `description` and `type` are required; `logoFileId` and
 * `providerName` may be `null` but not `''`. Answers 201 with
 * `{ data, metadata }`; the retired legacy route sent the bare standard.
 */
export function adminStandardCreate(body: AdminCreateStandardBodyInput) {
  return fetch('/admin/standards', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminStandard>;
}

/**
 * Saves a standard edit — the standards drawer. `name` and `description` are
 * required; a `type` is ignored, and translations left out are kept. Answers
 * `{ data, metadata }`; the retired legacy route sent the bare standard.
 */
export function adminStandardUpdate(
  id: string,
  body: AdminUpdateStandardBodyInput,
) {
  return fetch(`/admin/standards/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminStandard>;
}

/**
 * The assets an admin question template can link to: certifications, company
 * document types and audit types as `{ id, name, type }` — the asset-link
 * options in the admin question-template editor. Answers `{ data, metadata }`,
 * so callers read the list off `response.data.data`; the retired legacy route
 * sent the bare array.
 */
export function adminQuestionAssetLinksList(
  queryParams: Partial<AdminQuestionAssetLinksListQuery> = {},
) {
  return fetch(
    `/admin/questionnaires/question-asset-links?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as ApiResponse<AdminQuestionAssetLinksListResponse>;
}

/**
 * The assets a questionnaire question can link to — the same rows as
 * `adminQuestionAssetLinksList`, behind the questionnaire read permission
 * instead of an admin one, for the questionnaire builder's asset pickers.
 * Answers `{ data, metadata }`, so callers read the list off
 * `response.data.data`; the retired `/v3/assessment-forms/question-asset-link`
 * sent the bare array.
 */
export function questionAssetLinksList() {
  return fetch('/questionnaires/question-asset-links', {
    method: 'GET',
  }) as ApiResponse<AdminQuestionAssetLinksListResponse>;
}

/**
 * The admin question-template categories as a tree: the roots, each with its
 * sub-categories under `children` — the categories tab and the category
 * pickers on the question-templates pages. Answers `{ data, metadata }`, so
 * callers read the tree off `response.data.data`; the retired legacy route
 * sent `{ categories, questions }`.
 */
export function adminQuestionTemplateCategoriesList() {
  return fetch('/admin/questionnaires/question-template-categories', {
    method: 'GET',
  }) as ApiResponse<AdminQuestionTemplateCategoriesListResponse>;
}

/**
 * Creates a question-template category — "new category" in the category
 * editor. Answers 201 with `{ data, metadata }`; the retired legacy upsert
 * stored the category and then answered 404.
 */
export function adminQuestionTemplateCategoryCreate(
  body: AdminSaveQuestionTemplateCategoryBodyInput,
) {
  return fetch('/admin/questionnaires/question-template-categories', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminQuestionTemplateCategory>;
}

/**
 * Replaces a question-template category's name, position, icon and parent —
 * saving an existing category in the category editor. Answers
 * `{ data, metadata }`.
 */
export function adminQuestionTemplateCategoryUpdate(
  id: string,
  body: AdminSaveQuestionTemplateCategoryBodyInput,
) {
  return fetch(
    `/admin/questionnaires/question-template-categories/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  ) as ApiResponse<AdminQuestionTemplateCategory>;
}

/**
 * One page of the admin question-template library — the question list.
 * Answers `{ data, metadata: { pagination } }`; the retired legacy route
 * answered `{ data, meta }`.
 */
export function adminQuestionTemplatesList(
  queryParams: Partial<AdminQuestionTemplatesListQuery>,
) {
  return fetch(
    `/admin/questionnaires/question-templates?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as ApiResponse<AdminQuestionTemplatesListResponse>;
}

/**
 * One question of the admin question-template library with its options — the
 * question editor. Answers `{ data, metadata }`, so callers read the question
 * off `response.data.data`; the retired legacy route sent it bare.
 */
export function adminQuestionTemplateGet(id: string) {
  return fetch(
    `/admin/questionnaires/question-templates/${encodeURIComponent(id)}`,
    { method: 'GET' },
  ) as ApiResponse<AdminQuestionTemplate>;
}

/**
 * Creates a question template with its options — saving a new question in the
 * question editor. Answers 201 with `{ data, metadata }`.
 */
export function adminQuestionTemplateCreate(
  body: AdminSaveQuestionTemplateBodyInput,
) {
  return fetch('/admin/questionnaires/question-templates', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminQuestionTemplate>;
}

/**
 * Replaces a question template and its options — saving an existing question
 * in the question editor. Answers `{ data, metadata }`.
 */
export function adminQuestionTemplateUpdate(
  id: string,
  body: AdminSaveQuestionTemplateBodyInput,
) {
  return fetch(
    `/admin/questionnaires/question-templates/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  ) as ApiResponse<AdminQuestionTemplate>;
}

/**
 * Deletes question templates and their options — the bulk delete on the
 * question-template list. 204, no body; ids that match nothing are ignored.
 */
export function adminQuestionTemplatesBulkDelete(
  body: AdminDeleteQuestionTemplatesBody,
) {
  return fetch('/admin/questionnaires/question-templates', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/**
 * One category of the admin question-template library — the category editor on
 * the question-templates page. Answers `{ data, metadata }`, so callers read the
 * category off `response.data.data`; the retired legacy route sent it bare.
 */
export function adminQuestionTemplateCategoryGet(id: string) {
  return fetch(
    `/admin/questionnaires/question-template-categories/${encodeURIComponent(id)}`,
    { method: 'GET' },
  ) as ApiResponse<AdminQuestionTemplateCategory>;
}

/**
 * Retraced-admin template list. Distinct from `questionnairesList` above even
 * though both reach the same query: this one is gated on
 * `admin:questionnaire:read`, seeded for `RETRACED_ADMIN` only.
 */
export function adminQuestionnaireTemplatesList(
  queryParams: Partial<AdminQuestionnaireTemplatesListQuery>,
) {
  return fetch(
    `/admin/questionnaires/templates?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * One template with its sections and questions — the read that hydrates the
 * Retraced-admin template builder. Answers `{ metadata, data }`, so callers read
 * the detail off `response.data.data`.
 *
 * The admin twin of `questionnaireBuilder` below: same payload, same
 * company-scoped lookup, but gated on `admin:questionnaire:read`. `templates`
 * names the surface, not a filter — the route applies no `is_template`
 * predicate.
 */
export function adminQuestionnaireTemplateDetail(templateId: string) {
  return fetch(`/admin/questionnaires/templates/${templateId}`, {
    method: 'GET',
  }) as ApiResponse<QuestionnaireBuilderDetail>;
}

/**
 * Creates an empty draft template as the Retraced admin. The admin twin of
 * `createQuestionnaireTemplate` below — same write, same service — but gated on
 * `admin:questionnaire:create`, which only `RETRACED_ADMIN` holds. Templates
 * made here are therefore Retraced-owned and start inactive.
 *
 * Answers `{ metadata, data }`, so the caller reads the new id off
 * `response.data.data.id`. The retired legacy route sent the entity bare.
 */
export function adminQuestionnaireTemplateCreate(
  body: CreateQuestionnaireTemplateBody,
) {
  return fetch('/admin/questionnaires/templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * The save behind the Retraced-admin template builder. The admin twin of
 * `questionnaireBuilderSave` below — same body, same service — but gated on
 * `admin:questionnaire:write`, which only `RETRACED_ADMIN` holds.
 *
 * `sections` and `questions` are the complete desired content, not a patch:
 * rows absent from the payload are deleted along with their answers and
 * comments. `status` is not settable and a template always stores a null
 * `dueDate`, so the `status`/`dueDate` the shared builder puts in the payload
 * are inert here.
 *
 * Answers `{ metadata, data }` where the retired legacy route sent the entity
 * bare — harmless, because the builder's save handler reads only the status and
 * the error.
 */
export function adminQuestionnaireTemplateUpdate(
  templateId: string,
  body: UpdateQuestionnaireBuilderBody,
) {
  return fetch(`/admin/questionnaires/templates/${templateId}`, {
    body: JSON.stringify(body),
    method: 'PUT',
  }) as ApiResponse<QuestionnaireBuilderDetail>;
}

/**
 * Publishes (`isActive: 1`) or hides (`0`) a Retraced-owned template in the
 * shared pool — the switch in the admin templates table. 204, no body.
 *
 * There is no tenant twin: only `admin:questionnaire:write` reaches this, and a
 * company-owned template answers 404. Nothing else on the template is writable
 * here; the name and content go through `adminQuestionnaireTemplateUpdate` above.
 *
 * `isActive` is the `0 | 1` the column stores, not a boolean — the retired legacy
 * route accepted any number and wrote it verbatim, this one rejects anything else.
 */
export function adminQuestionnaireTemplateToggle(
  templateId: string,
  body: ToggleAdminQuestionnaireTemplateBody,
) {
  return fetch(`/admin/questionnaires/templates/${templateId}`, {
    body: JSON.stringify(body),
    method: 'PATCH',
  });
}

/**
 * Clones a Retraced-owned template into a new inactive draft named "Copy of …",
 * carrying its sections and questions across — the copy icon in the admin
 * templates table. The admin twin of `questionnaireSaveAsTemplate` below: same
 * clone, same service, but gated on `admin:questionnaire:create`, which only
 * `RETRACED_ADMIN` holds. A company-owned template answers 403.
 *
 * Answers `{ metadata, data }`, so the caller reads the new id off
 * `response.data.data.id`. The retired legacy route sent the entity bare —
 * reading `response.data.id` here navigates to `.../undefined/edit`.
 */
export function adminQuestionnaireTemplateCopy(templateId: string) {
  return fetch(`/admin/questionnaires/templates/${templateId}/copy`, {
    method: 'POST',
  });
}

/**
 * Permanently deletes Retraced-owned questionnaire templates — the bulk delete
 * on the admin templates table and the delete action inside the admin template
 * builder. The admin twin of `questionnaireTemplatesBulkDelete` below: same
 * all-or-nothing hard delete, same service, but gated on
 * `admin:questionnaire:delete`, which only `RETRACED_ADMIN` holds, where the
 * tenant right is also held by `company-admin` and `company-creator`.
 *
 * All-or-nothing: one id that is missing, is not a template, or belongs to
 * another company answers 404 and nothing is deleted. 204, no body — callers
 * read only the status.
 */
export function adminQuestionnaireTemplatesBulkDelete(
  body: BulkDeleteQuestionnaireTemplatesBody,
) {
  return fetch('/admin/questionnaires/templates', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function createQuestionnaire(body: CreateQuestionnaireBody) {
  return fetch('/questionnaires', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Creates an empty draft questionnaire template.
 *
 * Separate from `createQuestionnaire`: a template forces `DRAFT`, carries no
 * due date or clone source, and — unlike a questionnaire — its name is not
 * checked for uniqueness.
 */
export function createQuestionnaireTemplate(
  body: CreateQuestionnaireTemplateBody,
) {
  return fetch('/questionnaires/templates', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function questionnairesBulkDelete(body: BulkDeleteQuestionnairesBody) {
  return fetch('/questionnaires', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/**
 * Permanently deletes questionnaire templates owned by the caller's company.
 * All-or-nothing, and a Retraced-owned template is refused unless the caller is
 * a Retraced admin. 204, no body.
 */
export function questionnaireTemplatesBulkDelete(
  body: BulkDeleteQuestionnaireTemplatesBody,
) {
  return fetch('/questionnaires/templates', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/**
 * Removes the selected companies' answer requests from a questionnaire.
 * Answers the companies already submitted are deleted permanently. 204, no body.
 */
export function questionnaireRequestsBulkDelete(
  questionnaireId: string,
  body: BulkDeleteQuestionnaireRequestsBody,
) {
  return fetch(`/questionnaires/${questionnaireId}/requests`, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/**
 * Reminds the selected companies that their answers are still outstanding.
 * An empty `requestIds` reminds every request on the questionnaire. 204, no body.
 */
export function questionnaireRequestsReminder(
  questionnaireId: string,
  body: RemindQuestionnaireRequestsBody,
) {
  return fetch(`/questionnaires/${questionnaireId}/requests/reminder`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Moves the deadline on the selected companies' answer requests. 204, no body.
 *
 * `dueDate` is a required `YYYY-MM-DD` string. Callers that pass `?? null` when
 * their date input is empty have always been 400ing — the legacy body schema
 * declared `{type:'string', format:'date'}`, which null could never satisfy —
 * and clearing a due date is not a supported action: `DueDateAction` keeps Save
 * disabled until a day is picked.
 */
export function questionnaireRequestsDueDate(
  questionnaireId: string,
  body: UpdateQuestionnaireRequestsDueDateBody,
) {
  return fetch(`/questionnaires/${questionnaireId}/requests/due-date`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Moves one answer request through the review lifecycle — the responder's
 * "Submit for review" and the reviewer's status popover.
 *
 * Answers 200 with `{data: <updated request>}`. Both call sites ignore the row
 * and refetch instead, but it is kept on the wire for API-key clients.
 *
 * Only the questionnaire's owner may set anything other than `IN_REVIEW`; the
 * answering company gets 403 if it tries. Submitting with a compulsory question
 * unanswered is a 422.
 */
export function questionnaireRequestStatus(
  questionnaireId: string,
  requestId: string,
  body: UpdateQuestionnaireRequestStatusBody,
) {
  return fetch(
    `/questionnaires/${questionnaireId}/requests/${requestId}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
}

export function questionnairesBulkArchive(body: BulkArchiveQuestionnairesBody) {
  return fetch('/questionnaires/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function questionnairesBulkUnarchive(
  body: BulkUnarchiveQuestionnairesBody,
) {
  return fetch('/questionnaires/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function questionnairesExport(
  questionnaireId: string,
  body: ExportQuestionnaireBody,
) {
  return fetch(`/questionnaires/${questionnaireId}/export`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function questionnaireSaveAsTemplate(questionnaireId: string) {
  return fetch(`/questionnaires/${questionnaireId}/save-as-template`, {
    method: 'POST',
  });
}

/**
 * The questionnaire with its sections and questions — the read that hydrates
 * the builder and preview screens. Answers `{ metadata, data }`, so callers
 * read the detail off `response.data.data`.
 */
export function questionnaireBuilder(questionnaireId: string) {
  return fetch(`/questionnaires/${questionnaireId}/builder`, {
    method: 'GET',
  }) as ApiResponse<QuestionnaireBuilderDetail>;
}

/**
 * Every data point a questionnaire question can be built from, flat. Replaces the
 * legacy `supplierquestionnaire.getDataPoints` wrapper that hit
 * `supplier-questionnaire-service/v1/data-points`.
 *
 * Answers `{ metadata, data }` — the legacy route sent a bare array, so callers
 * read the rows off `response.data.data` where they used to read `response.data`.
 *
 * Definitions carry `retracedId`, **not** `id`. Use
 * `questionnaireDataPointTemplates` if you need `id`.
 */
export function questionnaireDataPoints() {
  return fetch('/questionnaires/data-points', {
    method: 'GET',
  }) as ApiResponse<QuestionnaireDataPointList>;
}

/**
 * The same data points grouped into one entry per profile template — the shape
 * the builder sidebar tree renders. Replaces the legacy
 * `supplierquestionnaire.getDataPointsByTemplates` wrapper, which hit the same
 * URL with `?groupByTemplates=true`; that flag is now its own route.
 *
 * `category` is the template id the group is keyed by, `name` its label. Each
 * data point additionally carries `id`, an alias of `retracedId`. Answers
 * `{ metadata, data }`.
 */
export function questionnaireDataPointTemplates() {
  return fetch('/questionnaires/data-points/templates', {
    method: 'GET',
  }) as ApiResponse<QuestionnaireDataPointTemplateGroupList>;
}

/**
 * One data point's value from a company's saved profile — the read behind the
 * answer page's "use values from your company profile" pre-fill. Replaces the
 * legacy `supplierquestionnaire.getCompanyDataPointAnswer` wrapper that hit
 * `supplier-questionnaire-service/v1/data-points/:dataPointId/companies/:companyId`;
 * that was the last route on the legacy `/v1/data-points` prefix.
 *
 * Answers `{ metadata, data }`, so callers read the value off
 * `response.data.data.value` where they used to read `response.data.value`. The
 * payload is now just `{ dataPointId, value }` — the legacy route also leaked the
 * profile's own key columns, which nothing read.
 *
 * `value` is the stored blob verbatim (commonly `{ value, unit, year }`), or `null`
 * when no profile carries the data point. An unrecognised `dataPointId` is a 404
 * `data_point_not_found`; a company with no profile *from the caller's
 * perspective* is a 404 `company_not_found`. Callers fan this out one call per
 * question, so tolerate a per-call failure rather than failing the whole batch.
 */
export function companyDataPointAnswer(dataPointId: string, companyId: string) {
  return fetch(
    `/questionnaires/data-points/${dataPointId}/companies/${companyId}`,
    { method: 'GET' },
  ) as ApiResponse<CompanyDataPointAnswer>;
}

/**
 * One answer request merged with the questionnaire it answers, plus every
 * question and the responder's saved answer — the read behind the responder's
 * answer page and the reviewer's per-company panel. Answers `{ metadata, data }`,
 * so callers read the detail off `response.data.data`.
 *
 * Mind which half of the merge wins: `id`, `status` and `dueDate` are the
 * questionnaire's, while the request's own are `questionnaireRequestId` and
 * `requestStatus`.
 */
export function questionnaireRequestDetail(requestId: string) {
  return fetch(`/questionnaires/requests/${requestId}`, {
    method: 'GET',
  }) as ApiResponse<QuestionnaireRequestDetail>;
}

/**
 * Creates an audit as the answer to an AUDIT question, on the company the request
 * is *about*.
 *
 * Replaces `auditCreate` for this flow. That endpoint created the audit but then
 * shared it with the subject company through a connection-checked path a
 * delegated answering party can never satisfy — the share threw, the endpoint
 * answered 200, and the factory never got access to its own audit.
 */
export function questionnaireRequestAuditCreate(
  requestId: string,
  body: CreateQuestionnaireRequestAuditBody,
) {
  return fetch(`/questionnaires/requests/${requestId}/audits`, {
    body: JSON.stringify(body),
    method: 'POST',
  }) as ApiResponse<CreateQuestionnaireRequestAuditResponse>;
}

/**
 * The assets held by the company an answer request is *about* — the candidate
 * list behind every asset-link question, plus the per-type detail the answer form
 * renders once one is linked.
 *
 * Replaces the three per-type list calls this page used to make. Those reached
 * certificates through the caller's own network and audits through per-asset
 * sharing rows, so an answering party answering on another company's behalf saw
 * empty pickers and a blank form where its saved answer should have been.
 */
export function questionnaireRequestAssets(
  requestId: string,
  bucket: QuestionnaireRequestAssetsQuery['bucket'],
) {
  return fetch(
    `/questionnaires/requests/${requestId}/assets?bucket=${bucket}`,
    { method: 'GET' },
  ) as ApiResponse<QuestionnaireRequestAssetsResponse>;
}

/**
 * A time-limited download URL for one asset's file, resolved through the request.
 *
 * Takes the **asset** id: the object key is resolved server-side from an asset the
 * request is allowed to reach, so this cannot be pointed at another company's
 * file the way the removed `GET /v3/files/:fileUrl` could.
 */
export function questionnaireRequestAssetFile(
  requestId: string,
  assetId: string,
  bucket: QuestionnaireRequestAssetsQuery['bucket'],
  part?: 'report' | 'correctiveActionPlan',
) {
  const partQuery = part ? `&part=${part}` : '';
  return fetch(
    `/questionnaires/requests/${requestId}/assets/${assetId}/file?bucket=${bucket}${partQuery}`,
    { method: 'GET' },
  ) as ApiResponse<QuestionnaireRequestAssetFileResponse>;
}

/**
 * Creates a certificate as the answer to a CERTIFICATE question, on the company
 * the request is *about*.
 *
 * Replaces `claimCreate` for this flow. That endpoint inserted the claim and
 * copied its file into private storage before reading it back through a
 * network-scoped query, so an answering party answering on another company's
 * behalf got a 404 *after* the write — leaving a certificate nothing could see,
 * and one more of them on every retry.
 */
export function questionnaireRequestCertificateCreate(
  requestId: string,
  body: CreateQuestionnaireRequestCertificateBody,
) {
  return fetch(`/questionnaires/requests/${requestId}/certificates`, {
    body: JSON.stringify(body),
    method: 'POST',
  }) as ApiResponse<CreateQuestionnaireRequestCertificateResponse>;
}

/**
 * Creates documents as the answer to a DOCUMENTS question, filed on the company
 * the request is *about*.
 *
 * Replaces `companyDocumentBulkCreate` for this flow. That endpoint takes the
 * target `companyId` in its body and then requires it to be in the caller's own
 * network — which an answering party answering on another company's behalf never
 * is, so every upload answered 404. This route derives the company from the
 * request instead, which is why the body carries no company id at all: not the
 * subject, and not the brand to share with.
 */
export function questionnaireRequestDocumentsCreate(
  requestId: string,
  body: CreateQuestionnaireRequestDocumentsBody,
) {
  return fetch(`/questionnaires/requests/${requestId}/documents`, {
    body: JSON.stringify(body),
    method: 'POST',
  }) as ApiResponse<CreateQuestionnaireRequestDocumentsResponse>;
}

/**
 * The comments left on one question of one answer request — the thread behind
 * the comment section on the answer and review pages. Answers the legacy
 * `{ data, meta }` envelope, so callers read the rows off `response.data.data`.
 *
 * Note the argument order: `(questionId, requestId)`, matching the path. The
 * legacy `supplierquestionnaire.questionnaireFormQuestionCommentsGet` this
 * replaces took them the other way round.
 *
 * The comment author is on the `updatedBy*` fields, not `createdBy*` — the
 * create path writes the whole audit block on insert, and
 * `createdByUserName` is never populated.
 */
export function questionCommentsList(questionId: string, requestId: string) {
  return fetch(
    `/questionnaires/questions/${questionId}/requests/${requestId}/comments`,
    { method: 'GET' },
  );
}

/**
 * Files a comment on one question of one answer request.
 *
 * Same path as `questionCommentsList` above — the legacy route took `questionId`
 * and `requestId` in the body, and they moved onto the path so create and read
 * address the same collection. The body is just the comment text.
 *
 * Answers `{ metadata, data }`, so the created comment is on
 * `response.data.data` — unlike the list above, which kept the legacy
 * `{ data, meta }` envelope.
 */
export function questionCommentCreate(
  questionId: string,
  requestId: string,
  body: { questionComment: string },
) {
  return fetch(
    `/questionnaires/questions/${questionId}/requests/${requestId}/comments`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/**
 * Rewrites the text of one comment in that same thread.
 *
 * The legacy route addressed the comment by its id alone
 * (`PATCH /v1/questions/comments/:id`); `questionId` and `requestId` are on the
 * path now and are checked — a comment that is not on that question and request
 * answers 404, the same code an unknown comment does.
 *
 * Only the comment's author may edit it; anyone else gets 403
 * `cannot_update_question_comment`, a colleague at the same company included.
 *
 * Answers **200** with `{ metadata, data }`. The legacy route hard-coded `201`
 * on a PATCH — callers that branch on the status need the 200.
 */
export function questionCommentUpdate(
  questionId: string,
  requestId: string,
  commentId: string,
  body: { questionComment: string },
) {
  return fetch(
    `/questionnaires/questions/${questionId}/requests/${requestId}/comments/${commentId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/**
 * Removes one comment from that same thread — the last of the four comment
 * operations to leave the legacy service.
 *
 * Addressed like the edit above: the legacy route took the comment id alone
 * (`DELETE /v1/questions/comments/:id`), and `questionId` / `requestId` are on
 * the path now and are checked — a comment that is not on that question and
 * request answers 404, the same code an unknown comment does.
 *
 * Only the comment's author may delete it; anyone else gets 403
 * `cannot_delete_question_comment`, a colleague at the same company included.
 *
 * Answers **204** with an empty body, unchanged from the legacy route — callers
 * that branch on the status need no edit. The delete is permanent.
 */
export function questionCommentDelete(
  questionId: string,
  requestId: string,
  commentId: string,
) {
  return fetch(
    `/questionnaires/questions/${questionId}/requests/${requestId}/comments/${commentId}`,
    { method: 'DELETE' },
  );
}

/**
 * Saves a batch of answers to one questionnaire answer request — the write behind
 * the answer form's autosave, the "not available" toggle, the auto-fill from the
 * company profile and the SLCP prefill.
 *
 * The legacy route was a flat `POST /v1/responses` that carried `requestId` on
 * every item of `payload`. It is on the path now: no caller ever mixed requests in
 * one batch, and hoisting it means the caller's access to the request is checked
 * once rather than re-verified per answer.
 *
 * Two things worth knowing at the call site:
 *
 * - **Send `status`.** Omit it and the answer is filed as `IN_REVIEW`, which puts a
 *   half-typed draft in the reviewer's queue. Every autosave should send
 *   `NOT_SUBMITTED`.
 * - **`verificationMeta` is top-level only.** The legacy service overwrote any
 *   per-item value with the top-level one, so a per-item `verificationMeta` has
 *   never been stored and is not accepted.
 *
 * Answers 201 with an empty body. Re-answering the same question updates the
 * stored answer rather than adding a second one, so re-submitting is a silent
 * no-op by design.
 */
export function questionResponseCreate(
  requestId: string,
  body: CreateQuestionResponseBody,
) {
  return fetch(`/questionnaires/requests/${requestId}/responses`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Records a reviewer's verdict on one answer — the write behind the Approve and
 * Deny buttons.
 *
 * Replaces the legacy `questionnaireApproveQuestionAnswer` /
 * `questionnaireDenyQuestionAnswer` pair, which were the same `PATCH` with the
 * status hard-coded inside each wrapper. The status is now the caller's to pass, so
 * one function covers both buttons.
 *
 * Only the company that **sent** the request may review its answers; the company
 * that was asked gets 403 `cannot_update_response_status`. Answers 204 with an
 * empty body, so there is nothing to read back — patching a question the supplier
 * never answered is a silent no-op that also responds 204.
 */
export function questionResponseStatusUpdate(
  requestId: string,
  questionId: string,
  body: UpdateQuestionResponseStatusBody,
) {
  return fetch(
    `/questionnaires/requests/${requestId}/questions/${questionId}/response`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/**
 * The builder's save. `sections` and `questions` are the questionnaire's
 * complete desired content — anything the payload omits is deleted — and the
 * response is the saved questionnaire in the same shape `questionnaireBuilder`
 * returns.
 */
export function questionnaireBuilderSave(
  questionnaireId: string,
  body: UpdateQuestionnaireBuilderBody,
) {
  return fetch(`/questionnaires/${questionnaireId}/builder`, {
    body: JSON.stringify(body),
    method: 'PUT',
  }) as ApiResponse<QuestionnaireBuilderDetail>;
}

export function questionnaireRequestsList(
  questionnaireId: string,
  queryParams: Partial<QuestionnaireRequestsListQuery>,
) {
  return fetch(
    `/questionnaires/${questionnaireId}/requests?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Answer requests scoped to the calling company.
 *
 * Without `companyId` this is the caller's inbox — every request addressed to
 * them. With it, the requests the caller sent to that one company, limited to
 * questionnaires the caller owns.
 */
export function questionnaireRequestsByCompany(
  queryParams: Partial<QuestionnaireRequestsCompanyListQuery>,
) {
  return fetch(
    `/questionnaires/requests?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Invites companies to answer a questionnaire — one answer request per item.
 *
 * Answers 204 with an empty body, or 409 `request_already_exist` when any
 * selected company already has a live request for this questionnaire. Callers
 * branch on `response.status` rather than the body.
 */
export function questionnaireRequestsBulkCreate(
  questionnaireId: string,
  body: BulkCreateQuestionnaireRequestsBody,
) {
  return fetch(`/questionnaires/${questionnaireId}/requests`, {
    body: JSON.stringify(body),
    method: 'POST',
  });
}

export function slcpAssessmentsUnarchive(body) {
  return fetch('/slcp/assessments/archive', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function adminFilesStatistics() {
  return fetch('/admin/files/statistics', {
    method: 'GET',
  });
}

export const getStylesListData = body => {
  return fetch('/styles/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const checkStyleCodes = (body: {
  /** Omit `codeId` to match the value under any base type. */
  codes: Array<{ codeId?: string; codeValue: string }>;
  companyId: string;
  isCreatedForNetwork?: boolean;
  /** Defaults to `'style'` server-side. */
  productLevel?: 'style' | 'variant' | 'sku';
  /** Off drops `codes` from each match; `matchedCodes` is unaffected. */
  includeAllCodes?: boolean;
}) => {
  return fetch('/styles/check-codes', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const adminGetStylesListData = body => {
  return fetch('/admin/styles/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function styleCreate(body) {
  return fetch('/styles', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getStyleTypesList(body) {
  return fetch('/style-types/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getStyleData(id) {
  return fetch(`/styles/${id}`, {
    method: 'GET',
  });
}

export function styleUpdate(id, body) {
  return fetch(`/styles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function bulkCreateDpp(body: IBulkCreateDppBody) {
  return fetch('/dpp', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getStyleTracingOrder(id) {
  return fetch(`/styles/${id}/traced-orders`, {
    method: 'GET',
  });
}

export function getStylePropertiesList(body) {
  return fetch('/style-properties/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createStyleProperty(body) {
  return fetch('/style-properties', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Styles Archive/Unarchive
export function archiveStyles(body: { ids: string[] }) {
  return fetch('/styles/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unarchiveStyles(body: {
  ids: string[];
  shouldUnarchiveChildren?: boolean;
}) {
  return fetch('/styles/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Variants Archive/Unarchive
export function archiveVariants(body: { ids: string[] }) {
  return fetch('/style-variants/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unarchiveVariants(body: {
  ids: string[];
  shouldUnarchiveChildren?: boolean;
}) {
  return fetch('/style-variants/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// SKUs Archive/Unarchive
export function archiveSkus(body: { ids: string[] }) {
  return fetch('/variant-skus/archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unarchiveSkus(body: { ids: string[] }) {
  return fetch('/variant-skus/unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface BulkUpdateStylesPayload {
  styleIds: string[];
  collection?: Array<{ value: string }>;
  isStoryActive?: boolean;
  isComponent?: boolean;
}

export interface BulkUpdateStylesResponse {
  updatedCount: number;
  variantsUpdatedCount: number;
  skusUpdatedCount: number;
  errors?: Array<{
    styleId: string;
    styleName: string;
    error: string;
  }>;
}

export const bulkUpdateStyles = async (
  payload: BulkUpdateStylesPayload,
): Promise<BulkUpdateStylesResponse> => {
  return fetch('/styles/bulk-update', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export function getStyleCertifications(id, queryParams) {
  return fetch(
    `/styles/${id}/certifications?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getStyleDocuments(id, queryParams) {
  return fetch(
    `/styles/${id}/style-documents?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getStyleOrderDocuments(id, queryParams) {
  return fetch(
    `/styles/${id}/style-order-documents?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function createStyleDocument(id, body) {
  return fetch(`/styles/${id}/style-documents`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bulkArchiveStyleDocuments(id, ids, isArchived) {
  return fetch(`/styles/${id}/style-documents/bulk-archive`, {
    method: 'POST',
    body: JSON.stringify({ ids, isArchived }),
  });
}

export function bulkDeleteStyleDocuments(id, ids) {
  return fetch(`/styles/${id}/style-documents/bulk-delete`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export function bulkDownloadStyleDocuments(id, fileIds) {
  return fetch(`/styles/${id}/style-documents/download`, {
    method: 'POST',
    body: JSON.stringify({ fileIds }),
  });
}

export function bulkDownloadStyleOrderDocuments(id, fileIds) {
  return fetch(`/styles/${id}/style-order-documents/download`, {
    method: 'POST',
    body: JSON.stringify({ fileIds }),
  });
}

export function getEntityTypeFieldValues(queryParams) {
  return fetch(
    `/style-type-field-values/entity?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function upsertEntityTypeFieldValues(body) {
  return fetch('/style-type-field-values/upsert', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteBatch(id) {
  return fetch(`/batches/${id}`, {
    method: 'DELETE',
  });
}

export function createStyleLink(body) {
  return fetch('/style-links/link', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const getVariantsListData = body => {
  return fetch('/style-variants/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function getVariantData(id) {
  return fetch(`/style-variants/${id}`, {
    method: 'GET',
  });
}

export function getSkuData(id) {
  return fetch(`/variant-skus/${id}`, {
    method: 'GET',
  });
}

export const getVariantSKUsListData = body => {
  return fetch('/variant-skus/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function variantCreate(body) {
  return fetch('/style-variants', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function variantUpdate(id, body) {
  return fetch(`/style-variants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function skuCreate(body) {
  return fetch('/variant-skus', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function skuUpdate(id, body) {
  return fetch(`/variant-skus/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export const getBatchesListData = body => {
  return fetch('/batches/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function getBatchData(id) {
  return fetch(`/batches/${id}`, {
    method: 'GET',
  });
}

export function batchCreate(body) {
  return fetch('/batches', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function batchUpdate(id, body) {
  return fetch(`/batches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function batchBomUpdate(id, body) {
  return fetch(`/batches/${id}/bom`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function batchEntityUpdate(id, body) {
  return fetch(`/batches/${id}/entity`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function materialHeaderCreate(body) {
  return fetch('/material-headers', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export function materialHeaderUpdate(id, body) {
  return fetch(`/material-headers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function getMaterialHeader(id) {
  return fetch(`/material-headers/${id}`, {
    method: 'GET',
  });
}

export function getMaterialHeadersList(body) {
  return fetch('/material-headers/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteMaterialHeader(body) {
  return fetch('/material-headers/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getMaterialLinesList(body) {
  return fetch('/material-lines/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function materialLineCreate(body) {
  return fetch('/material-lines', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function materialLineUpdate(id, body) {
  return fetch(`/material-lines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteMaterialLine(body) {
  return fetch('/material-lines/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomHeaderCreate(body) {
  return fetch('/bom-headers', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomHeaderUpdate(id, body) {
  return fetch(`/bom-headers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function getBomHeader(id) {
  return fetch(`/bom-headers/${id}`, {
    method: 'GET',
  });
}

export function getBomHeadersList(body) {
  return fetch('/bom-headers/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function deleteBomHeader(body) {
  return fetch('/bom-headers/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getBomLinesList(body) {
  return fetch('/bom-lines/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomLineCreate(body) {
  return fetch('/bom-lines', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomLineUpdate(id, body) {
  return fetch(`/bom-lines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export function deleteBomLine(body) {
  return fetch('/bom-lines/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const getAssessmentSuggestedCapas = (id, body) => {
  return fetch(`/slcp/assessments/${id}/suggested-capas`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function filesAIProcessingJobsList(body) {
  return fetch('/files/ai-processing-jobs/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function filesAiProcessingJobsCreate(body) {
  return fetch('/files/ai-processing-jobs/create', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export const networkSlcpAssessmentsList = body => {
  return fetch('/slcp/assessments/network-list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

// BOM Placements endpoints
export function getBomPlacementsList(body) {
  return fetch('/bom-placements/list', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bomPlacementCreate(body) {
  return fetch('/bom-placements', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelCapaWorkflow(body) {
  return fetch('/capa/process/cancel', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function aiFeedback(body: {
  fileId: string;
  assetId: string;
  assetType: string;
  aiProcessType: string;
  vote?: 0 | 1;
  text?: string;
}) {
  return fetch('/ai-feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function capaImportActions(body) {
  return fetch('/capa/import-actions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createFinding(body) {
  return fetch('/capa/findings', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getMyCompanyFindings(queryParams) {
  return fetch(
    `/capa/findings/my-company?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getNetworkFindings(queryParams) {
  return fetch(
    `/capa/findings/network?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getFindingDetails(id: string): ApiResponse<ICapFinding | null> {
  return fetch(`/capa/findings/details/${id}`, {
    method: 'GET',
  });
}

export function patchFinding(id: string, body: Record<string, unknown>) {
  return fetch(`/capa/findings/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function getActionDetails(id) {
  return fetch(`/capa/actions/${id}`, {
    method: 'GET',
  });
}

export function getActionComments(actionId, queryParams) {
  return fetch(
    `/capa/actions/${actionId}/comments?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getActionHistories(
  actionId: string,
  queryParams: { page?: number; count?: number } = {},
): Promise<{
  data: {
    data: IActionHistory[];
    meta: {
      count: number;
      page: number;
      totalCount: number;
      pageSize: number;
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/capa/actions/${actionId}/histories?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function createActionComment(actionId, body) {
  return fetch(`/capa/actions/${actionId}/comments`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function createRemediationRequest(
  actionId: string,
  body: { message: string; attachmentIds: string[] },
) {
  return fetch(`/capa/actions/${actionId}/remediation-requests`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateRemediationRequest(
  actionId: string,
  body: { status: string; requestedChangeMessage?: string },
) {
  return fetch(`/capa/actions/${actionId}/remediation-requests`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function downloadRemediationFiles(actionId: string) {
  return fetch(
    `/capa/actions/${actionId}/remediation-requests/download-files`,
    {
      method: 'GET',
    },
  );
}

export function updateActionComment(actionId, commentId, body) {
  return fetch(`/capa/actions/${actionId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function archiveFindings(findingIds) {
  return fetch('/capa/findings/archive', {
    method: 'POST',
    body: JSON.stringify({ findingIds }),
  });
}

export function unarchiveFindings(findingIds) {
  return fetch('/capa/findings/unarchive', {
    method: 'POST',
    body: JSON.stringify({ findingIds }),
  });
}

export function getMyCompanyArchive(queryParams) {
  return fetch(
    `/capa/findings/my-company/archive?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getNetworkArchive(queryParams) {
  return fetch(
    `/capa/findings/network/archive?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export function getEntityTagsList(queryParams) {
  return fetch(`/entity-tags?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function createEntityTag(body) {
  return fetch('/entity-tags', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateEntityTag(id, body) {
  return fetch(`/entity-tags/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteEntityTag(id) {
  return fetch(`/entity-tags/${id}`, {
    method: 'DELETE',
  });
}

export function linkEntityTags(body) {
  return fetch('/entity-tags/link', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function exportStyles(body) {
  return fetch('/styles/export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function bulkImportStyles(body: {
  styles: unknown[];
  isComponent?: boolean;
}) {
  const response = await fetch('/styles/bulk-import', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (response.error) {
    throw new Error(response.error.message || 'Import failed');
  }
  return response.data;
}

export async function bomBulkImport(body: { fileId: string }) {
  const response = await fetch('/bom-import/bulk-import', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (response.error) {
    throw new Error(response.error.message || 'Import failed');
  }
  return response.data;
}

export function capaCreatePlan(body) {
  return fetch('/capa/caps', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getCapaCapsList(queryParams) {
  return fetch(`/capa/caps?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function getCapaCapsArchivedList(queryParams) {
  return fetch(`/capa/caps/archive?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

// Distinct audit types of the audits referenced as sources by the company's
// plans — used for the plans-list "Source" filter options.
export function getCapaCapsFilterSources() {
  return fetch('/capa/caps/filter-sources', {
    method: 'GET',
  });
}

// Same as above but for CA/PA actions — used for the actions-list "Source"
// filter options.
export function getCapaActionsFilterSources() {
  return fetch('/capa/actions/filter-sources', {
    method: 'GET',
  });
}

export function getCapaActionsList(queryParams: {
  count: number | string;
  page: number | string;
  sort?: string;
  filter: any; // Fix later
  quickFilter?: string;
}): Promise<{
  data: IListActionsResponse;
  error: Error;
  status: number;
}> {
  return fetch(`/capa/actions?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function getCapaActionsArchivedList(queryParams: {
  count: number;
  page: number;
  sort: string;
  filter: any; // Fix later
  quickFilter: string;
}): Promise<{
  data: IListActionsResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/capa/actions/archive?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

export async function archiveCapas(actionIds, includeLinkedActions = false) {
  return throwIfApiError(
    await fetch(`/capa/archive?includeLinkedActions=${includeLinkedActions}`, {
      method: 'POST',
      body: JSON.stringify({ actionIds }),
    }),
    'Failed to archive',
  );
}

export async function deleteCapas(actionIds, includeLinkedActions = false) {
  return throwIfApiError(
    await fetch(`/capa/delete?includeLinkedActions=${includeLinkedActions}`, {
      method: 'POST',
      body: JSON.stringify({ actionIds }),
    }),
    'Failed to delete',
  );
}

export async function unarchiveCapas(actionIds) {
  return throwIfApiError(
    await fetch('/capa/unarchive', {
      method: 'POST',
      body: JSON.stringify({ actionIds }),
    }),
    'Failed to unarchive',
  );
}

export function tracingAiSummaryBulkCreate(
  ids: string[],
  traceType: string,
  summaryTypes?: string[],
) {
  return fetch('/tracing-ai/bulk-summary', {
    method: 'POST',
    body: JSON.stringify({ ids, traceType, summaryTypes }),
  });
}

export function tracingAiSummaryCancel(
  tracingId: string,
  traceType: string,
  summaryTypes?: string[],
) {
  return fetch('/tracing-ai/summary/cancel', {
    method: 'POST',
    body: JSON.stringify({ tracingId, traceType, summaryTypes }),
  });
}

export function tracingAiDatasetGenerate(
  summaryId: string,
  type: 'doc_type' | 'doc_content' | 'sc_analysis',
) {
  return fetch(`/tracing-ai/${summaryId}/dataset?type=${type}`, {
    method: 'GET',
  });
}

export function tracingAiSummaryGetById(summaryId: string) {
  return fetch(`/tracing-ai/summary/${summaryId}`, {
    method: 'GET',
  });
}

export function tracingAiSummaryValidate(
  summaryId: string,
  body: {
    tracingType: string;
    tracingId: string;
    summaryType: 'docType' | 'docContent' | 'scAnalysis';
    tracingDocumentId?: string;
    linkId?: string;
  },
) {
  return fetch(`/tracing-ai/summary/${summaryId}/validate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function tracingAiSummaryClear(body: {
  tracingId: string;
  tracingOrder: 'PURCHASE_ORDER' | 'SALES_ORDER';
  summaryType: 'docType' | 'docContent' | 'scAnalysis';
}) {
  return fetch('/tracing-ai/admin/summary/clear', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function tracingAiWorkflowTraceTotal() {
  return fetch('/tracing-ai/summary/workflow-trace/total', {
    method: 'GET',
  });
}

export function getCapaPlanDetails(id: string): ApiResponse<ICapPlan | null> {
  return fetch(`/capa/caps/${id}`, {
    method: 'GET',
  });
}

export function updateCapaPlan(
  id: string,
  body,
  updateLinkedActions?: boolean,
) {
  return fetch(
    `/capa/caps/${id}${updateLinkedActionsQuery(updateLinkedActions)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
}

export function updateCapaAction(id: string, body: IActionPatch) {
  return fetch(`/capa/actions/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function linkCapaActions(
  id: string,
  body: LinkActionsRequest,
): ApiResponse<LinkActionsResponse> {
  return fetch(`/capa/actions/${id}/link`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function unlinkCapaActions(
  id: string,
  body: UnlinkActionsRequest,
): ApiResponse<UnlinkActionsResponse> {
  return fetch(`/capa/actions/${id}/unlink`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function bulkLinkCapaActionsToCap(
  body: BulkUpdateActionsRequest,
  updateLinkedActions?: boolean,
) {
  return fetch(
    `/capa/actions/bulk-link-cap${updateLinkedActionsQuery(updateLinkedActions)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
}

export async function bulkUpdateCapaSeverity(
  body: Array<{ id: string; severity: string }>,
) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-severity', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update severity',
  );
}

export async function bulkUpdateCapaImpactArea(
  body: Array<{ id: string; impactAreaId: string }>,
) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-impact-area', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update impact area',
  );
}

export async function bulkUpdateCapaTags(
  body: Array<{ id: string; tagIds: string[] }>,
) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-tags', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update tags',
  );
}

export async function bulkUpdateCapaAssignee(body: {
  actionIds: string[];
  add: Array<{ userId: string; companyId: string }>;
  remove: Array<{ userId: string; companyId: string }>;
}) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-assignee', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update assignee',
  );
}

export async function bulkUpdateCapaReporter(body: {
  actionIds: string[];
  add: Array<{ userId: string; companyId: string }>;
  remove: Array<{ userId: string; companyId: string }>;
}) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-reporter', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update reporters',
  );
}

export async function bulkUpdateCapaCollaborator(body: {
  actionIds: string[];
  add: string[];
  remove: string[];
}) {
  return throwIfApiError(
    await fetch('/capa/actions/bulk-collaborator', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
    'Failed to update collaborators',
  );
}

export function quickAddActions(body: {
  capId: string | null;
  assignees?: Array<{
    id: string;
    companyId: string;
  }>;
  impactAreaId: string | null;
  priority: string | null;
  title: string;
  companyId: string;
  auditFindingId?: string | null;
  type: 'CA' | 'PA';
}) {
  return fetch('/capa/actions/quick-add-actions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function linkUsersToCapa(body: {
  actionId: string;
  users: {
    userId: string;
    companyId: string;
  }[];
}) {
  return fetch('/capa/link-users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getSuggestedActions(
  capId: string,
): ApiResponse<{ findings: Array<ICapFinding & { actions: IAction[] }> }> {
  return fetch(`/capa/caps/${capId}/suggested-actions`, {
    method: 'GET',
  });
}

export function capaExport(body: {
  type: 'selected' | 'filtered';
  selected?: {
    entityType: Array<'CAP' | 'ACTION' | 'FINDING'>;
    entityIds: string[];
  };
  filters?: {
    responsibleCompanyIds?: string[];
    creationDateOfFinding?: string;
    sourceType?: string;
    sourceId?: string;
    countries?: string[];
    tierIds?: string[];
    vendorIds?: string[];
    actionStatuses?: string[];
  };
  columns?: {
    CAP?: string[];
    FINDING?: string[];
    ACTION?: string[];
  };
}) {
  return fetch('/capa/export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Every badge with a legacy product's proofs (`null` for a badge it has none
 * for). Replaces the legacy `productBadgesGet` wrapper that hit
 * `v3/products/:id/badges`, which answered the bare array; this answers
 * `{ data, metadata }`. Public, like legacy. 404 `not_found` for an unknown id.
 */
export function productBadgesList(productId: string) {
  return fetch(`/products/${encodeURIComponent(productId)}/badges`, {
    method: 'GET',
  });
}

/**
 * The companies on the caller's legacy products as suppliers or vendors, for
 * the product list's column filters. Replaces the legacy
 * `getProductsVendorsAndSuppliers` wrapper that hit
 * `v3/products/get-products-suppliers-vendors`, which answered the bare array;
 * this answers `{ data, metadata }`.
 */
export function productSuppliersAndVendorsList(): Promise<{
  data: {
    metadata: { success: boolean };
    data: ProductSuppliersAndVendorsResponse;
  };
  error: Error;
  status: number;
}> {
  return fetch('/products/suppliers-and-vendors', { method: 'GET' });
}

// Claims V2 endpoints (style-based)
export function claimCreate(body: ICreateClaimBodyV2) {
  return fetch('/claims', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * The certificates of the caller's network. Replaces the legacy
 * `GET /v3/claims` (since deleted), which `claimsGet`, `getAllClaims` and `companyBadgesGet`
 * all called. The body is still `{ data, meta }`.
 */
export function claimsList(queryParams: Partial<ClaimsListQuery>) {
  return fetch(`/claims?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/** One certificate of the caller's network; `data` is the claim. */
export function claimGet(id: string) {
  return fetch(`/claims/${id}`, {
    method: 'GET',
  });
}

export function claimUpdate(id: string, body: IUpdateClaimBodyV2) {
  return fetch(`/claims/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes (soft) one of the caller's own certificates. Replaces the legacy
 * `DELETE /v3/claims/:id` (`claimDelete`); answers 204, or 409 if another
 * company in the network owns the claim.
 */
export function claimDelete(id: string) {
  return fetch(`/claims/${id}`, {
    method: 'DELETE',
  });
}

/**
 * The comments on a certificate, as the caller's company may read them.
 * Replaces the legacy `GET /v3/claims/list-comments` (`claimCommentsGet`),
 * which answered a bare array and took a `companyId`; `data` is the array now.
 */
export function claimComments(queryParams: ListClaimCommentsQuery) {
  return fetch(
    `/claims/list-comments?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Submits a certificate for review: a DRAFT becomes REQUESTED. Replaces the
 * legacy `PUT /v3/claims/:id/request` (`claimRequestForApproval`); `data` is the
 * claim, and 409 means another live certificate holds its document number.
 */
export function claimRequest(id: string) {
  return fetch(`/claims/${id}/request`, {
    method: 'PUT',
    body: JSON.stringify({}),
  });
}

/**
 * Moves one of the caller's own certificates back to DRAFT. Replaces the
 * legacy `PUT /v3/claims/:id/to-draft` (`moveClaimToDraft`); `data` is the
 * claim, and 409 means another company owns it or its document number is held.
 */
export function claimToDraft(id: string) {
  return fetch(`/claims/${id}/to-draft`, {
    method: 'PUT',
    body: JSON.stringify({}),
  });
}

export function claimToArchive(id: string) {
  return fetch(`/claims/${id}/to-archive`, {
    method: 'PUT',
    body: JSON.stringify({}),
  });
}

/**
 * The expired versions of a certificate, its history, a page at a time, in the
 * platform's paginated envelope (`metadata.pagination`, `data`). Replaces the
 * legacy `GET /v3/claims/:id/list-expired-versions` (`claimsHistoryGet`),
 * which answered `{ data, meta }`.
 */
export function claimsExpiredVersions(
  claimId: string,
  queryParams: Partial<ClaimVersionsQuery>,
) {
  return fetch(
    `/claims/${claimId}/list-expired-versions?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * The other active versions of a certificate, a page at a time, in the
 * platform's paginated envelope (`metadata.pagination`, `data`). Replaces the
 * legacy `GET /v3/claims/:id/list-active-versions` (`claimsOtherActiveGet`),
 * which answered `{ data, meta }`.
 */
export function claimsActiveVersions(
  claimId: string,
  queryParams: Partial<ClaimVersionsQuery>,
) {
  return fetch(
    `/claims/${claimId}/list-active-versions?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Archives the caller's own certificates. Replaces the legacy
 * `PATCH /v3/claims/bulk-archive` (`claimBulkArchive`); `data.archivedClaimIds`
 * lists the ones archived. Answers 409 if any is another company's.
 */
export function claimsBulkArchive(body: ClaimIdsBody) {
  return fetch('/claims/bulk-archive', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function claimsExport(ids: string[] = []) {
  return fetch('/claims/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
}

export function exportAssessment({
  blueprintId,
  companyIds,
}: {
  blueprintId: string;
  companyIds?: string[];
}) {
  return fetch('/risk-analysis/exports/assessments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assessmentBlueprintId: blueprintId, companyIds }),
  });
}

/**
 * Replaces the legacy GET /v3/rule-templates behind
 * `api.riskAnalysisV3.getRiskAssessmentFormulas`.
 *
 * Same paginated envelope, so callers keep reading
 * `response.data.data`/`response.data.meta`. The route rejects params it does
 * not know, and `sort` must name a sortable column (see
 * `RuleTemplatesListSortColumns`).
 */
export function listRuleTemplates(
  queryParams: Partial<RuleTemplatesListQuery> = {},
) {
  return fetch(
    `/risk-analysis/rule-templates?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RuleTemplateListRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysis.getRiskAssessmentFormulaV3`
 * (legacy GET /v3/rule-templates/:id).
 *
 * The legacy route sent the template flat as the response body, so callers read
 * `response.data.<field>`. This route uses `reply.apiSuccess`, so the template is
 * one level deeper — callers must read `response.data.data.<field>`.
 */
export function getRuleTemplate(id: string) {
  return fetch(`/risk-analysis/rule-templates/${id}`, {
    method: 'GET',
  }) as Promise<{
    data: { data: RuleTemplateResource } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.copyAssessmentFormula`
 * (legacy POST /v3/rule-templates/copy).
 *
 * This route keeps the legacy flat `{message}` response body — see
 * `CopyRuleTemplatesResponseSchema` in `modules/risk-analysis` for why — so
 * callers keep reading `response.data.message` unchanged.
 */
export function copyRuleTemplates(body: CopyRuleTemplatesBody) {
  return fetch('/risk-analysis/rule-templates/copy', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysis.createRiskAssessmentFormulaV3`
 * (legacy POST /v3/rule-templates).
 *
 * This route keeps the legacy flat row response body — see
 * `CreateRuleTemplateResponseSchema` in `modules/risk-analysis` for why — so
 * callers keep reading `response.data.id` unchanged.
 */
export function createRuleTemplate(body: CreateRuleTemplateBody) {
  return fetch('/risk-analysis/rule-templates', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{
    data: RuleTemplateRowResource | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysis.updateRiskAssessmentFormulaV3`
 * (legacy PUT /v3/rule-templates/:id). An omitted field keeps its stored
 * value; the route rejects unknown top-level fields. The row comes back as
 * `response.data.data`; callers only check `status`.
 */
export function updateRuleTemplate(id: string, body: UpdateRuleTemplateBody) {
  return fetch(`/risk-analysis/rule-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.getRiskAssessmentBlueprint`
 * (legacy GET /v3/assessment-blueprints/:id).
 *
 * The legacy route sent the blueprint flat as the response body, so callers read
 * `response.data.<field>`. This route uses `reply.apiSuccess`, so the blueprint is
 * one level deeper — callers must read `response.data.data.<field>`.
 */
export function getAssessmentBlueprint(id: string) {
  return fetch(`/risk-analysis/assessment-blueprints/${id}`, {
    method: 'GET',
  }) as Promise<{
    data: { data: AssessmentBlueprintDetailResource } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getAssessment`
 * (legacy GET /v3/assessments/:id).
 *
 * The legacy route sent the assessment flat as the response body, so callers
 * read `response.data.<field>`. This route uses `reply.apiSuccess`, so the
 * assessment is one level deeper — callers must read `response.data.data.<field>`.
 */
export function getAssessment(id: string) {
  return fetch(`/risk-analysis/assessments/${id}`, {
    method: 'GET',
  }) as Promise<{
    data: { data: AssessmentDetailResource } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getStandardAssessmentScore`
 * (legacy GET /v3/companies/:id/assessments/standard).
 *
 * The legacy route sent `{netScore, status}` flat as the response body, so
 * callers read `response.data.netScore`. This route uses `reply.apiSuccess`,
 * so the score is one level deeper — callers must read
 * `response.data.data.netScore`.
 */
export function getStandardAssessmentScore(companyId: string) {
  return fetch(`/risk-analysis/companies/${companyId}/assessments/standard`, {
    method: 'GET',
  }) as Promise<{
    data: { data: StandardAssessment } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getAssessmentDetails`
 * (legacy GET /v3/assessments/:id/details).
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getAssessmentDetails(
  id: string,
  queryParams: Partial<GetAssessmentDetailsQuery>,
) {
  return fetch(
    `/risk-analysis/assessments/${id}/details?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: AssessmentDetailRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getAssessmentPrioritizationData`
 * (legacy GET /v3/assessments/:id/prioritization-details).
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getAssessmentPrioritizationDetails(
  id: string,
  queryParams: Partial<GetPrioritizationDetailsQuery>,
) {
  return fetch(
    `/risk-analysis/assessments/${id}/prioritization-details?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: PrioritizationDetailRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskProfilePrioritizationDetails`
 * (legacy GET /v3/risk-profiles/:companyId/blueprints/:blueprintId/prioritization-details).
 *
 * Same `reply.paginate` envelope — callers keep reading `response.data.data` /
 * `response.data.meta`. `sort` is `title` or `priorityScore`, optionally
 * `:ASC`/`:DESC`; any other `sort` is a 400.
 */
export function getRiskProfilePrioritizationDetails(
  companyId: string,
  blueprintId: string,
  queryParams: Partial<GetRiskProfilePrioritizationDetailsQuery>,
) {
  return fetch(
    `/risk-analysis/risk-profiles/${companyId}/blueprints/${blueprintId}/prioritization-details?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RiskProfilePrioritizationRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getBlueprintPrioritizationdata`
 * (legacy GET /v3/blueprint-companies/:id/prioritization-details).
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getBlueprintPrioritizationDetails(
  id: string,
  queryParams: Partial<GetBlueprintPrioritizationDetailsQuery>,
) {
  return fetch(
    `/risk-analysis/blueprint-companies/${id}/prioritization-details?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: BlueprintCompanyPrioritizationRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getBlueprintCompanies`
 * (legacy GET /v3/blueprint-companies/:id).
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getBlueprintCompanies(
  id: string,
  queryParams: Partial<GetBlueprintCompaniesQuery>,
) {
  return fetch(
    `/risk-analysis/blueprint-companies/${id}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: BlueprintCompanyRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getAssessmentHistory`
 * (legacy GET /v3/assessments/:id/history).
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getAssessmentHistory(
  id: string,
  queryParams: Partial<GetAssessmentHistoryQuery>,
) {
  return fetch(
    `/risk-analysis/assessments/${id}/history?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: AssessmentHistoryRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getAssessmentNotes`
 * (legacy GET /v3/assessments/:id/notes). Distinct from `getAssessmentRiskNotes`
 * above, which backs the unrelated `GET /v3/assessment-notes` endpoint
 * (assessmentId + riskId, no risk_title).
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getAssessmentNotes(
  assessmentId: string,
  queryParams: Partial<GetAssessmentLevelNotesQuery>,
) {
  return fetch(
    `/risk-analysis/assessments/${assessmentId}/notes?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: AssessmentNoteWithRiskTitle[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getAssessmentDatapointsDrawerData`
 * (legacy GET /v3/assessments/:id/rules/matched).
 *
 * The legacy route sent the matched-rules template flat as the response body,
 * so callers read `response.data.<field>`. This route uses `reply.apiSuccess`,
 * so the template is one level deeper — callers must read
 * `response.data.data.<field>`.
 */
export function getMatchedRules(
  assessmentId: string,
  queryParams: { triggerEvent: string; assets: string },
) {
  return fetch(
    `/risk-analysis/assessments/${assessmentId}/rules/matched?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: { data: MatchedRulesResponse } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getDatapointsDrawerData`
 * (legacy GET /v3/assessments/:id/datapoints/:riskId).
 *
 * Sibling of `getMatchedRules` — same response shape (`MatchedRulesResponse`),
 * same envelope change: the legacy route sent it flat, this route uses
 * `reply.apiSuccess`, so callers must read `response.data.data.<field>`.
 */
export function getRiskFulfilledRules(assessmentId: string, riskId: string) {
  return fetch(
    `/risk-analysis/assessments/${assessmentId}/datapoints/${riskId}`,
    { method: 'GET' },
  ) as Promise<{
    data: { data: MatchedRulesResponse } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskAssessmentAuditTypesV3` and
 * `api.riskAnalysisV3.getRiskAssessmentAuditTypesWithFindingsV3`
 * (legacy GET /v3/audit-types) — consolidated into one function, since the
 * new route takes `withFindings` as a query param directly rather than
 * needing a second wrapper.
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getRiskAuditTypes(
  queryParams: Partial<GetRiskAuditTypesQuery>,
) {
  return fetch(
    `/risk-analysis/audit-types?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RiskAuditTypeRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysis.getRiskAssessmentDataPointsV3`
 * (legacy GET /v3/datapoints).
 *
 * Unpaginated and `{id, name}` only — the legacy route's default `count` of 15
 * silently capped the trigger picker, since the caller never sent one. Callers
 * keep reading `response.data.data`.
 */
export function listRiskDatapoints() {
  return fetch('/risk-analysis/datapoints', { method: 'GET' }) as Promise<{
    data: { data: RiskDatapointRow[] } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces the legacy GET /v3/risk-gross behind
 * `api.riskAnalysisV3.getRiskGross` — gross indices newest first, or the
 * superseded ones with `filter={"isHistory":true}`.
 *
 * Same paginated envelope, so callers keep reading
 * `response.data.data`/`response.data.meta`.
 */
export function listRiskGross(queryParams: Partial<ListRiskGrossQuery>) {
  return fetch(
    `/risk-analysis/risk-gross?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RiskGrossListItem[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces the legacy GET /v3/mitigations/assessments/:id behind
 * `api.riskAnalysisV3.getRemarks` — an assessment's manual mitigations,
 * newest first.
 *
 * Same paginated envelope, so callers keep reading
 * `response.data.data`/`response.data.meta`.
 */
export function listAssessmentMitigations(
  assessmentId: string,
  queryParams: Partial<ListAssessmentMitigationsQuery>,
) {
  return fetch(
    `/risk-analysis/mitigations/assessments/${assessmentId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: AssessmentMitigation[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysis.getRiskAssessmentMetricsV3`
 * (legacy GET /v3/metrics).
 *
 * Unpaginated and `{id, value}` only — the one caller sent no params and got the
 * legacy default page of 15. Callers keep reading `response.data.data`.
 */
export function listRiskMetrics() {
  return fetch('/risk-analysis/metrics', { method: 'GET' }) as Promise<{
    data: { data: RiskMetricRow[] } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysis.getRiskAssessmentRiskFactorsImpactAreaV3`
 * (legacy GET /v3/impact-areas/risks) — the impact areas linked to a risk.
 *
 * Same paginated envelope, so callers keep reading
 * `response.data.data`/`response.data.meta`; `meta.totalCount` now counts
 * impact areas rather than risk links.
 */
export function listRiskImpactAreas(
  queryParams: Partial<ListRiskImpactAreasQuery>,
) {
  return fetch(
    `/risk-analysis/impact-areas?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RiskImpactAreaRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskAssessmentCertificationsV3`
 * (legacy GET /v3/certifications).
 *
 * Both the legacy route and this one use the paginated envelope
 * (`reply.paginate`), so the response shape is unchanged — callers keep
 * reading `response.data.data`/`response.data.meta`.
 */
export function getRiskCertifications(
  queryParams: Partial<ListCertificationsQuery>,
) {
  return fetch(
    `/risk-analysis/certifications?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: CertificationRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskProfileRiskScores`
 * (legacy GET /v3/risk-profiles/:companyId/blueprints/:blueprintId/risk-scores).
 *
 * The legacy route sent the risk scores flat as the response body, so callers
 * read `response.data.<field>`. This route uses `reply.apiSuccess`, so the
 * scores are one level deeper — callers must read `response.data.data.<field>`.
 */
export function getRiskProfileRiskScores(
  companyId: string,
  blueprintId: string,
) {
  return fetch(
    `/risk-analysis/risk-profiles/${companyId}/blueprints/${blueprintId}/risk-scores`,
    { method: 'GET' },
  ) as Promise<{
    data: { data: RiskScoresResource } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskLandscapeCountryData`
 * (legacy GET /v3/risk-landscape/:countryCode).
 *
 * The legacy route sent the landscape flat as the response body, so callers
 * read `response.data.<field>`. This route uses `reply.apiSuccess`, so the
 * landscape is one level deeper — callers must read `response.data.data.<field>`.
 */
export function getRiskLandscapeCountryData(countryCode: string) {
  return fetch(`/risk-analysis/risk-landscape/${countryCode}`, {
    method: 'GET',
  }) as Promise<{
    data: { data: RiskLandscapeResource } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskLandscapeRiskDetailsCompaniesData`
 * (legacy GET /v3/risk-landscape/companies).
 *
 * This route still uses `reply.paginate`, so the envelope is unchanged from
 * the legacy route — callers keep reading `response.data.data` /
 * `response.data.meta`.
 */
export function listRiskLandscapeCompanies(
  queryParams: Partial<RiskLandscapeCompaniesListQuery>,
) {
  return fetch(
    `/risk-analysis/risk-landscape/companies?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RiskLandscapeCompanyRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getCountryRiskLandscapeData`
 * (legacy GET /v3/risk-landscapes).
 *
 * Same `reply.paginate` envelope — callers keep reading `response.data.data` /
 * `response.data.meta`. The filter's `scope` must be `"global"` to lift the
 * network-only default; any other value means the caller's network.
 */
export function listRiskLandscape(
  queryParams: Partial<RiskLandscapeListQuery>,
) {
  return fetch(
    `/risk-analysis/risk-landscape?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RiskLandscapeListRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskLandscapeRiskDetailsCountriesData`
 * (legacy GET /v3/risk-landscape/countries).
 *
 * Same `reply.paginate` envelope — callers keep reading `response.data.data` /
 * `response.data.meta`. `meta.totalCount` now counts the filtered rows; legacy
 * always reported every country.
 */
export function listRiskLandscapeCountries(
  queryParams: Partial<RiskLandscapeCountriesListQuery>,
) {
  return fetch(
    `/risk-analysis/risk-landscape/countries?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: RiskLandscapeCountryRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskMapLandscapeData`
 * (legacy GET /v3/risk-landscapes/maps).
 *
 * Same bare-array body — callers keep reading `response.data` as the country
 * list. The filter's `scope` must be `"global"` to lift the network-only
 * default; `country` now actually narrows the list (legacy matched nothing).
 */
export function getRiskLandscapeMap(
  queryParams: Partial<RiskLandscapeMapQuery>,
) {
  return fetch(
    `/risk-analysis/risk-landscape/map?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: RiskLandscapeMapCountry[] | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getRiskAssessments`
 * (legacy GET /v3/assessment-blueprints).
 *
 * This route still uses `reply.paginate`, so the envelope is unchanged from
 * the legacy route — callers keep reading `response.data.data` /
 * `response.data.meta`.
 */
export function listAssessmentBlueprints(
  queryParams: Partial<AssessmentBlueprintsListQuery>,
) {
  return fetch(
    `/risk-analysis/assessment-blueprints?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: AssessmentBlueprintListRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.assessmentBlueprintsByCompany`
 * (legacy GET /v3/assessment-blueprints/:assessedCompanyId/assessments).
 *
 * This route still uses `reply.paginate`, so the envelope is unchanged from
 * the legacy route. Note `queryParams.assessedCompanyId` is also echoed into
 * the query string here (matching the legacy wrapper), but the backend only
 * reads the URL path segment — the query-string copy has no effect.
 */
export function listAssessmentBlueprintsByCompany(
  queryParams: Partial<AssessmentBlueprintsByCompanyListQuery> & {
    assessedCompanyId: string;
  },
) {
  return fetch(
    `/risk-analysis/assessment-blueprints/${queryParams.assessedCompanyId}/assessments?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: AssessmentBlueprintByCompanyRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.bulkArchiveAssessments`
 * (legacy POST /v3/assessment-blueprints/archive). Responds 204 with no body.
 */
export function bulkArchiveAssessmentBlueprints(
  body: BulkArchiveAssessmentBlueprintsBody,
) {
  return fetch('/risk-analysis/assessment-blueprints/archive', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.createRiskAssessment`
 * (legacy POST /v3/assessment-blueprints).
 *
 * The legacy route sent the blueprint flat as the response body, so callers read
 * `response.data.<field>`. This route uses `reply.apiSuccess`, so the blueprint is
 * one level deeper — callers must read `response.data.data.<field>`.
 */
export function createAssessmentBlueprint(body: CreateAssessmentBlueprintBody) {
  return fetch('/risk-analysis/assessment-blueprints', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }) as Promise<{
    data: { data: AssessmentBlueprintDetailResource } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Was meant to replace `api.riskAnalysisV3.updateRiskAssessment`
 * (legacy PATCH /v3/assessment-blueprints/:id) — except that legacy helper was
 * never actually exported from `risk-analysis-service-v3`, so the only call site
 * threw `undefined is not a function` before issuing a request. Editing a risk
 * assessment on this dashboard has been dead ever since. See DELTA-2516.
 *
 * The body is a full-column overwrite, not a merge: an omitted `goal`/`tags`
 * clears that column to NULL, so callers must send the complete blueprint.
 *
 * Responds via `reply.apiSuccess`, so callers read `response.data.data.<field>`.
 */
export function updateAssessmentBlueprint(
  id: string,
  body: UpdateAssessmentBlueprintBody,
) {
  return fetch(`/risk-analysis/assessment-blueprints/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }) as Promise<{
    data: { data: AssessmentBlueprintDetailResource } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.getAssessmentHistoryByBlueprint`
 * (legacy GET /v3/assessment-blueprints/:id/company/:companyId/history).
 *
 * The legacy route sent the history array flat as the response body, so callers
 * read `response.data`. This route uses `reply.apiSuccess`, so the array is one
 * level deeper — callers must read `response.data.data`.
 */
export function getAssessmentBlueprintHistory(
  blueprintId: string,
  companyId: string,
) {
  return fetch(
    `/risk-analysis/assessment-blueprints/${blueprintId}/company/${companyId}/history`,
    { method: 'GET' },
  ) as Promise<{
    data: { data: AssessmentBlueprintHistoryEntry[] } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.createAssessmentRiskNotes`
 * (legacy POST /v3/assessment-notes).
 */
export function createAssessmentNote(body: CreateAssessmentNoteBody) {
  return fetch('/risk-analysis/assessment-notes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.deleteAssessmentRiskNote`
 * (legacy DELETE /v3/assessment-notes/:id).
 */
export function deleteAssessmentNote(id: string) {
  return fetch(`/risk-analysis/assessment-notes/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Replaces `api.riskAnalysisV3.getAssessmentRiskNotes`
 * (legacy GET /v3/assessment-notes). Distinct from `getAssessmentNotes` above,
 * which backs the unrelated `GET /v3/assessments/:id/notes` endpoint
 * (assessmentId-only, with risk_title).
 */
export function getAssessmentRiskNotes(
  queryParams: Partial<ListAssessmentNotesQuery>,
) {
  return fetch(
    `/risk-analysis/assessment-notes?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/** Replaces `api.riskAnalysisV3.createManualMitigation` (legacy POST /v3/mitigations). */
export function createManualMitigation(body: CreateMitigationBody) {
  return fetch('/risk-analysis/mitigations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Replaces the legacy GET /v3/risks behind `api.riskAnalysisV3.riskAnalysisGetRisks`
 * — the risk catalogue, each risk with its `netRisk` per country.
 *
 * Same paginated envelope, so callers keep reading
 * `response.data.data`/`response.data.meta`. The route rejects params it does
 * not know — there is no `sort`.
 */
export function listRisks(queryParams: Partial<RisksListQuery> = {}) {
  return fetch(
    `/risk-analysis/risks?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  ) as Promise<{
    data: {
      data: RiskListRow[];
      meta: {
        count: number;
        page: number;
        pageSize: number;
        totalCount: number;
      };
    } | null;
    error: unknown;
    status: number;
  }>;
}

/**
 * Every matching product category, in one list — the category dropdowns.
 * With `isPrimary`, only the categories the company (`companyId`, else the
 * caller's) is eligible for, each flagged `isPrimary`. Replaces
 * `api.productCategoriesGet` (legacy GET /v3/product-categories); the list is
 * still `response.data.data`.
 */
export function productCategoriesList(
  queryParams: Partial<ProductCategoriesListQueryInput> = {},
) {
  return fetch(
    `/product-categories?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: ProductCategoryOption[];
      metadata: { success: boolean };
    };
    error: Error;
    status: number;
  }>;
}

/**
 * Every product property (brand, department, season) of the caller's company,
 * newest first — the product forms' pickers and the product / component list
 * filters. Replaces `api.getProductProperties` (legacy
 * GET /v3/product-properties with `isDropDown`); the list is still
 * `response.data.data`.
 */
export function productPropertiesList() {
  return fetch('/product-properties', { method: 'GET' }) as Promise<{
    data: {
      data: ProductPropertiesListResponse;
      metadata: { success: boolean };
    };
    error: Error;
    status: number;
  }>;
}

/**
 * Creates a product property (a brand, department or season) — the "create"
 * option of the product form's property pickers. Replaces
 * `api.createProductProperty` (legacy POST /v3/product-properties), which got
 * the bare record; this gets `{ data, metadata }` (201), so callers read
 * `data.data`. `companyId` must be the caller's company or a linked one.
 */
export function productPropertyCreate(body: CreateProductPropertyBody) {
  return fetch('/product-properties', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{
    data: { data: ProductProperty; metadata: { success: boolean } };
    error: Error;
    status: number;
  }>;
}

/**
 * Every product placement of the caller's company, by name — the BOM card's
 * and the BOM upload's placement options. Replaces `api.productPlacementsGet`
 * (legacy GET /v3/product-placements); the list is still `response.data.data`.
 */
export function productPlacementsList() {
  return fetch('/product-placements', { method: 'GET' }) as Promise<{
    data: {
      data: ProductPlacementsListResponse;
      metadata: { success: boolean };
    };
    error: Error;
    status: number;
  }>;
}

/**
 * Creates a product placement — the "create" option of a BOM line's placement
 * picker. Replaces `api.productPlacementsCreate` (legacy
 * POST /v3/product-placements), which got the bare record; this gets
 * `{ data, metadata }` (201), so callers read `data.data`.
 */
export function productPlacementCreate(body: CreateProductPlacementBody) {
  return fetch('/product-placements', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{
    data: { data: ProductPlacement; metadata: { success: boolean } };
    error: Error;
    status: number;
  }>;
}

/**
 * Every field of one product category, ordered by `idx`, with its parsed
 * `fieldOptions` — the product bulk import's dropdown options. Replaces
 * `api.productCategoriesFieldsGet` (legacy GET /v3/product-category-fields
 * with `isDropDown` and a `productCategoryId` filter); the list is still
 * `response.data.data`.
 */
export function productCategoryFieldsList(productCategoryId: string) {
  return fetch(
    `/product-categories/${encodeURIComponent(productCategoryId)}/fields`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: ProductCategoryFieldsListResponse;
      metadata: { success: boolean };
    };
    error: Error;
    status: number;
  }>;
}

/**
 * A page of the product types the caller's company can make, with its own
 * code for each — the constants-mapping product-type picker. Replaces
 * `api.ListProductCategoriesForMapping` (legacy
 * GET /v3/product-categories/allTypes), whose `filter` nested the name in a
 * second JSON string; this one takes `{ name }` directly. The page is still
 * `response.data.data`.
 */
export function productTypesList(
  queryParams: Partial<ProductTypesListQueryInput> = {},
) {
  return fetch(
    `/product-categories/types?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: {
      data: ProductType[];
      metadata: {
        success: boolean;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      };
    };
    error: Error;
    status: number;
  }>;
}

/**
 * Replaces `api.riskAnalysisV3.riskAnalysisAdminCreateRisk`
 * (legacy POST /v3/risks). The created risk now comes back as
 * `response.data.data`; callers only check `status`.
 */
export function createRisk(body: CreateRiskBody) {
  return fetch('/risk-analysis/risks', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.riskAnalysisAdminUpdateRisk`
 * (legacy PATCH /v3/risks/:id). Omitted fields keep their stored value, and an
 * omitted `impactAreaIds` leaves the risk's links alone. The updated risk comes
 * back as `response.data.data`; callers only check `status`.
 */
export function updateRisk(id: string, body: UpdateRiskBody) {
  return fetch(`/risk-analysis/risks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.createRiskGross` (legacy POST /v3/risk-gross).
 * `id` is not a settable field — it is always server-generated now, closing a
 * legacy bug where a caller-supplied `body.id` could override the row's
 * primary key.
 */
export function createRiskGross(body: CreateRiskGrossBody) {
  return fetch('/risk-analysis/risk-gross', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.updateRiskGross` (legacy PATCH /v3/risk-gross/:id).
 * Writes a new version of the row's risk × country and closes the one in force;
 * `riskId`/`countryCode` are not accepted — a version never moves.
 */
export function updateRiskGross(id: string, body: UpdateRiskGrossBody) {
  return fetch(`/risk-analysis/risk-gross/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.recalculateAssessment`
 * (legacy POST /v3/assessments/calculate). Responds 204 with no body.
 */
export function recalculateAssessment(body: CalculateAssessmentBody) {
  return fetch('/risk-analysis/assessments/calculate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.recalculateStandardAssessment`
 * (legacy POST /v3/assessments/calculate/standard-assessment). Responds 204.
 *
 * Only `companyIds` is sent: the endpoint resolves the caller's standard blueprint
 * server-side, so the legacy wrapper's `blueprintId` argument was always ignored.
 */
export function recalculateStandardAssessment(
  body: CalculateByStandardAssessmentBody,
) {
  return fetch('/risk-analysis/assessments/calculate/standard-assessment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Replaces `api.riskAnalysisV3.createBulkAssessment`
 * (legacy POST /v3/blueprint-companies/bulk). Responds 201 with an empty `data`.
 */
export function createBulkAssessment(body: BulkCreateBlueprintCompaniesBody) {
  return fetch('/risk-analysis/blueprint-companies/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Every company's certificates, a page at a time, in the platform's paginated
 * envelope. Replaces the legacy `GET /v3/admin/claims` (`getAllClaimsAdmin`).
 */
export function adminClaimsList(queryParams: Partial<AdminClaimsListQuery>) {
  return fetch(`/admin/claims?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Files a certificate, already APPROVED, on the company in the body. Replaces
 * the legacy `POST /v3/admin/claims` (`createClaimByAdmin`). `data` is the
 * created claim.
 */
export function adminClaimCreate(body: AdminCreateClaimBody) {
  return fetch('/admin/claims', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * One certificate of any company, with its audit trail. Replaces the legacy
 * `GET /v3/admin/claims/:id` (`claimGetByAdmin`). `data` is the claim.
 */
export function adminClaimGet(id: string) {
  return fetch(`/admin/claims/${id}`, {
    method: 'GET',
  });
}

/**
 * Archives any company's certificates. Replaces the legacy
 * `PATCH /v3/admin/claims/bulk-archive` (`certificateBulkArchive`);
 * `data.archivedClaimIds` lists the ones archived.
 */
export function adminClaimsBulkArchive(body: AdminClaimIdsBody) {
  return fetch('/admin/claims/bulk-archive', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Removes (soft-deletes) any company's certificates. Replaces the legacy
 * `PUT /v3/admin/claims/bulk-delete` (`certificateBulkRemove`);
 * `data.removedClaimIds` lists the ones removed.
 */
export function adminClaimsBulkRemove(body: AdminClaimIdsBody) {
  return fetch('/admin/claims/bulk-delete', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Restores any company's removed certificates. Replaces the legacy
 * `PATCH /v3/admin/claims/bulk-restore` (`certificateBulkRestore`);
 * `data.restoredClaimIds` lists the ones restored.
 */
export function adminClaimsBulkRestore(body: AdminClaimIdsBody) {
  return fetch('/admin/claims/bulk-restore', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Takes any company's certificates out of the archive. Replaces the legacy
 * `PATCH /v3/admin/claims/bulk-unarchive` (`certificateBulkUnArchive`);
 * `data.unarchivedClaimIds` lists the ones unarchived.
 */
export function adminClaimsBulkUnarchive(body: AdminClaimIdsBody) {
  return fetch('/admin/claims/bulk-unarchive', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * The audit-compliance dashboard's headline numbers for `companyId`'s network.
 * Replaces the legacy dashboard-analytics `GET /v1/audit-metrics/:companyId`
 * (`analytics.getAuditsMetrics`); `data` is the metrics. `filter` is the
 * JSON-encoded `AuditMetricsFilter`.
 */
export function dashboardAuditMetrics(
  companyId: string,
  queryParams: AuditMetricsQuery,
) {
  return fetch(
    `/dashboard-analytics/audits-metrics/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * The certificate-compliance dashboard's numbers for `companyId`'s suppliers.
 * Replaces the legacy dashboard-analytics
 * `GET /v1/certification-metrics/:companyId`
 * (`analytics.getCertificationsMetrics`); `data` is the metrics. `filter` is
 * the JSON-encoded `CertificationMetricsFilter`.
 */
export function dashboardCertificationMetrics(
  companyId: string,
  queryParams: CertificationMetricsQuery,
) {
  return fetch(
    `/dashboard-analytics/certification-metrics/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * The certificate-compliance dashboard's historic chart for `companyId`'s
 * suppliers: approved certificates per standard over a timeframe. Replaces the
 * legacy dashboard-analytics `GET /v1/certification-metrics/by-standard/:companyId`
 * (`analytics.getCertificationsByStandardMetrics`); `data` is
 * `{ data, totalCount }`. `filter` is the JSON-encoded
 * `CertificatesByStandardFilter`.
 */
export function dashboardCertificatesByStandard(
  companyId: string,
  queryParams: CertificationMetricsQuery,
) {
  return fetch(
    `/dashboard-analytics/certification-metrics/by-standard/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Queues the certificate-compliance export for the caller's company; the file
 * arrives by notification. Replaces the legacy dashboard-analytics
 * `POST /v1/certification-metrics/export`
 * (`analytics.exportCertificateMetricsData`); answers 204.
 */
export function dashboardCertificationExport(body: CertificationExportBody) {
  return fetch('/dashboard-analytics/certification-metrics/export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * The tracing-operations dashboard's numbers for `companyId`'s purchase
 * orders. Replaces the legacy dashboard-analytics
 * `GET /v1/tracing-metrics/:companyId` (`analytics.getTracingAnalytics`);
 * `data` is the metrics. `filter` is the JSON-encoded `TracingMetricsFilter`.
 */
export function dashboardTracingMetrics(
  companyId: string,
  queryParams: TracingMetricsQuery,
) {
  return fetch(
    `/dashboard-analytics/tracing-metrics/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Queues the tracing-operations export for the caller's company; the file
 * arrives by notification. Replaces the legacy dashboard-analytics
 * `POST /v1/tracing-metrics/export` (`analytics.exportOperationalMetricsData`);
 * answers 204.
 */
export function dashboardTracingExport(body: TracingExportBody) {
  return fetch('/dashboard-analytics/tracing-metrics/export', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * The tracing-compliance dashboard's document completion per tracing depth
 * for `companyId`'s traced orders. Replaces the legacy dashboard-analytics
 * `GET /v1/tracing-documents/completion/:companyId`
 * (`analytics.getTracingPoDocumentAnalytics`); `data` is depths 0–4.
 * `filter` is the JSON-encoded `TracingDocumentsFilter`.
 */
export function dashboardTracingDocumentsCompletion(
  companyId: string,
  queryParams: TracingDocumentsQuery,
) {
  return fetch(
    `/dashboard-analytics/tracing-documents/completion/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * A page of `companyId`'s traced orders and their documents for the
 * tracing-compliance dashboard's table: those missing a required document
 * with `isMissing`, else those missing none. Replaces the legacy
 * dashboard-analytics `GET /v1/tracing-documents/:companyId`
 * (`analytics.listTracingPoDocuments`); answers the platform's paginated
 * `{ metadata: { pagination }, data }` envelope.
 * `filter` is the JSON-encoded `TracingDocumentsListFilter`.
 */
export function dashboardTracingDocumentsList(
  companyId: string,
  queryParams: TracingDocumentsListQuery,
) {
  return fetch(
    `/dashboard-analytics/tracing-documents/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * A page of audit findings by impact area for `companyId`'s network, against
 * the period before. Replaces the legacy dashboard-analytics
 * `GET /v1/audit-metrics/impact-areas/:companyId`
 * (`analytics.getAuditsMetricsByImpactAreas`); answers the platform's
 * paginated envelope.
 */
export function dashboardAuditImpactAreas(
  companyId: string,
  queryParams: Partial<AuditImpactAreasQuery>,
) {
  return fetch(
    `/dashboard-analytics/audits-metrics/impact-areas/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Audit findings by urgency for `companyId`'s network, against the period
 * before. Replaces the legacy dashboard-analytics
 * `GET /v1/audit-metrics/urgencies/:companyId`
 * (`analytics.getAuditFindingsByUrgency`); `data` is `{ data, totalCount }`.
 */
export function dashboardAuditUrgencies(
  companyId: string,
  queryParams: AuditMetricsQuery,
) {
  return fetch(
    `/dashboard-analytics/audits-metrics/urgencies/${companyId}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Asks the certificate's provider to confirm its license or document number.
 * Replaces the legacy `POST /v3/claims/:id/verify-license-number`
 * (`verifyCertificateLicenseNumber`); `data` is the claim as
 * `GET /claims/:id` answers it.
 */
export function claimVerifyLicenseNumber(id: string) {
  return fetch(`/claims/${id}/verify-license-number`, { method: 'POST' });
}

/**
 * Comments on a certificate, filed against `companyId` (the certificate's own
 * company). Replaces the legacy `POST /v3/claims/add-comment`
 * (`claimCommentsAdd`); answers 201, `data` being the stored comment.
 */
export function claimCommentAdd(body: AddClaimCommentBody) {
  return fetch('/claims/add-comment', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Comments on any company's certificate. Replaces the legacy
 * `POST /v3/admin/claims/add-comment` (`claimCommentsAddByAdmin`); answers 201,
 * `data` being the stored comment.
 */
export function adminClaimCommentAdd(body: AdminAddClaimCommentBody) {
  return fetch('/admin/claims/add-comment', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Asks the certificate's provider to confirm its license or document number.
 * Replaces the legacy `POST /v3/admin/claims/:id/verify-license-number`
 * (`managementVerifyCertificateLicenseNumber`); `data` is the claim.
 */
export function adminClaimVerifyLicenseNumber(id: string) {
  return fetch(`/admin/claims/${id}/verify-license-number`, { method: 'POST' });
}

/**
 * Marks a certificate not verifiable, archiving what an approval would. Replaces
 * the legacy `PUT /v3/admin/claims/:id/not-verifiable` (`notVerifiableClaim`);
 * `data` is the claim.
 */
export function adminClaimNotVerifiable(id: string) {
  return fetch(`/admin/claims/${id}/not-verifiable`, { method: 'PUT' });
}

/**
 * Rejects a certificate: it goes back to `DRAFT` for the company to correct.
 * Replaces the legacy `PUT /v3/admin/claims/:id/reject` (`rejectClaim`); `data`
 * is the rejected claim.
 */
export function adminClaimReject(id: string) {
  return fetch(`/admin/claims/${id}/reject`, { method: 'PUT' });
}

/**
 * The whole comment thread of any company's certificate, internal notes
 * included, oldest first. Replaces the legacy
 * `GET /v3/admin/claims/list-comments` (`claimCommentsGetByAdmin`); `data` is
 * the array of comments.
 */
export function adminClaimCommentsList(claimId: string) {
  return fetch(
    `/admin/claims/list-comments?${getDefinedUrlSearchParams({ claimId })}`,
    { method: 'GET' },
  );
}

/**
 * Approves a certificate. Replaces the legacy `PUT /v3/admin/claims/:id/approve`
 * (`approveClaim`); `data` is the approved claim.
 */
export function adminClaimApprove(id: string) {
  return fetch(`/admin/claims/${id}/approve`, {
    method: 'PUT',
  });
}

export function adminClaimUpdate(id: string, body: IUpdateClaimBodyV2) {
  return fetch(`/admin/claims/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export const claimListStylesV2 = (
  id: string,
  queryParams?: IListStylesQueryV2,
) => {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/claims/${id}/list-styles${params}`, {
    method: 'GET',
  });
};

export const adminClaimListStylesV2 = (
  id: string,
  queryParams?: IListStylesQueryV2,
) => {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/claims/${id}/list-styles${params}`, {
    method: 'GET',
  });
};

export const styleSupplyChainMappingGet = (
  id: string,
  queryParams?: { isRemovedStatusCheck?: boolean },
) => {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/product-mapping/${id}${params}`, {
    method: 'GET',
  });
};

/**
 * Products created by the caller's company for a network-linked brand
 * company (`brandId`). Replaces the legacy `createdNetworkProductsGet`
 * wrapper that hit `v3/companies/:id/created-network-products`.
 */
export const createdNetworkProductsGet = (
  brandId: string,
  queryParams?: Partial<CreatedNetworkProductsQuery>,
) => {
  return fetch(
    `/companies/${brandId}/created-network-products?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
};

export const styleSupplyChainNodeDelete = (
  nodeId: string,
  queryParams: { supplyChainId: string },
) => {
  return fetch(
    `/product-mapping/nodes/${nodeId}?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'DELETE',
    },
  );
};

export const getProductMappingLinkInfo = (
  linkId: string,
  supplyChainId: string,
) => {
  return fetch(
    `/product-mapping/link/${linkId}?supplyChainId=${supplyChainId}`,
    {
      method: 'GET',
    },
  );
};

export const updateProductMappingLink = (
  linkId: string,
  body: {
    supplyChainId: string;
    lines: Array<{
      styleId: string;
      quantity: number;
      unit: string;
      secondaryQuantity?: number | null;
      secondaryUnit?: string | null;
    }>;
  },
) => {
  return fetch(`/product-mapping/links/${linkId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

export function cascadeTracingMyDocumentsGet(
  queryParams: Partial<ListMyTracingDocumentsQuery>,
): Promise<{
  data: ListMyTracingDocumentsResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/cascade-tracing/documents/my-documents?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

export function cascadeTracingDocumentStatisticsGet(): ApiResponse<
  TracingDocumentStatistic[]
> {
  return fetch('/cascade-tracing/documents/statistics', { method: 'GET' });
}

/** The PO/SO pairs whose orders use different attachment templates, under `data`. */
export function cascadeTracingCheckAttachmentTemplates(
  pairs: CheckAttachmentTemplatesBody,
): ApiResponse<AttachmentTemplateMismatch[]> {
  return fetch('/cascade-tracing/orders/check-attachment-templates', {
    method: 'POST',
    body: JSON.stringify(pairs),
  });
}

/** The given numbers the caller's live purchase orders already use, under `data`. */
export function cascadeTracingCheckPurchaseOrderNumbers(
  orderNumbers: CheckOrderNumbersBody,
): ApiResponse<string[]> {
  return fetch(
    '/cascade-tracing/orders/purchases/check-purchase-order-numbers',
    {
      method: 'POST',
      body: JSON.stringify(orderNumbers),
    },
  );
}

/** The companies on the given purchase orders' traces, under `data`. */
export function cascadeTracingPurchaseOrderParticipants(
  body: ListOrderParticipantsBody,
): ApiResponse<OrderParticipant[]> {
  return fetch('/cascade-tracing/orders/purchases/participants', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** The given numbers the caller's live sales orders already use, under `data`. */
export function cascadeTracingCheckSalesOrderNumbers(
  orderNumbers: CheckOrderNumbersBody,
): ApiResponse<string[]> {
  return fetch('/cascade-tracing/orders/sales/check-sales-order-numbers', {
    method: 'POST',
    body: JSON.stringify(orderNumbers),
  });
}

/** Archives or unarchives the caller's purchase and sales orders; 204. */
export function cascadeTracingToggleArchiveOrders(
  body: ToggleArchiveOrdersBody,
): ApiResponse<void> {
  return fetch('/cascade-tracing/orders/toggle-archive', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Sets the status of several of the caller's purchase orders; the orders changed under `data`. */
export function cascadeTracingPurchaseOrdersBulkPatch(
  body: BulkPatchOrdersBody,
): ApiResponse<PatchedOrder[]> {
  return fetch('/cascade-tracing/orders/purchases/bulk-patch', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Sets the status of several of the caller's sales orders; the orders changed under `data`. */
export function cascadeTracingSalesOrdersBulkPatch(
  body: BulkPatchOrdersBody,
): ApiResponse<PatchedOrder[]> {
  return fetch('/cascade-tracing/orders/sales/bulk-patch', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Queues attaching documents to several of the caller's purchase orders; 204, 409 when their templates differ. */
export function cascadeTracingPurchaseOrdersBulkDocumentsUpdate(
  body: BulkOrderDocumentsBody,
): ApiResponse<void> {
  return fetch('/cascade-tracing/orders/purchases/bulk-documents-update', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Queues attaching documents to several of the caller's sales orders; 204, 409 when their templates differ. */
export function cascadeTracingSalesOrdersBulkDocumentsUpdate(
  body: BulkOrderDocumentsBody,
): ApiResponse<void> {
  return fetch('/cascade-tracing/orders/sales/bulk-documents-update', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Deletes one of the caller's purchase orders with its tracing order; 204. */
export function cascadeTracingPurchaseOrderDelete(
  id: string,
): ApiResponse<void> {
  return fetch(`/cascade-tracing/orders/purchases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/** Deletes one of the caller's sales orders with its tracing order; 204. */
export function cascadeTracingSalesOrderDelete(id: string): ApiResponse<void> {
  return fetch(`/cascade-tracing/orders/sales/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function cascadeTracingPurchaseOrderPatch(
  id: string,
  body: PatchPurchaseOrderBody,
): Promise<{
  data: PatchPurchaseOrderResponse;
  error: Error;
  status: number;
}> {
  return fetch(`/cascade-tracing/orders/purchases/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function cascadeTracingSalesOrderPatch(
  id: string,
  body: PatchSalesOrderBody,
): Promise<{
  data: PatchSalesOrderResponse;
  error: Error;
  status: number;
}> {
  return fetch(`/cascade-tracing/orders/sales/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** 204 on success — see `LinkSalesOrderContract` for the error codes. */
export function cascadeTracingLinkSalesOrder(
  body: LinkSalesOrderBody,
): Promise<{ data: null; error: Error; status: number }> {
  return fetch('/cascade-tracing/orders/link-sales-order', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Queues a zip of a tracing link's documents, emailed to the caller; 204, 404 when there are none. */
export function cascadeTracingOrderDownloadLinkDocuments(
  linkId: string,
): ApiResponse<void> {
  return fetch(
    `/cascade-tracing/transactions/download-link-documents/${encodeURIComponent(linkId)}`,
    { method: 'GET' },
  );
}

/** Queues a zip of an order's trace documents, emailed to the caller; 204, 404 when there are none. */
export function cascadeTracingOrderDownloadOrderDocuments(
  orderId: string,
  type: DownloadOrderDocumentsQuery['type'],
): ApiResponse<void> {
  return fetch(
    `/cascade-tracing/transactions/download-order-documents/${encodeURIComponent(orderId)}?${getDefinedUrlSearchParams({ type })}`,
    { method: 'GET' },
  );
}

/** Marks a tracing document verified for the caller's company; 204, also when already verified. */
export function cascadeTracingVerifyDocument(
  tracingDocumentId: string,
): ApiResponse<void> {
  return fetch(
    `/cascade-tracing/transactions/verify-document/${encodeURIComponent(tracingDocumentId)}`,
    { method: 'POST' },
  );
}

/** Removes the caller's company's verification of a tracing document; 204, also when there was none. */
export function cascadeTracingUnverifyDocument(
  tracingDocumentId: string,
): ApiResponse<void> {
  return fetch(
    `/cascade-tracing/transactions/unverify-document/${encodeURIComponent(tracingDocumentId)}`,
    { method: 'DELETE' },
  );
}

/** Detaches a purchase order attached to a trace; the initial order's trace graph under `data`. */
export function cascadeTracingDetachOrder(body: DetachPurchaseOrderBody) {
  return fetch('/cascade-tracing/transactions/detach-existing-order', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Removes a document the caller's company uploaded to a tracing node; 204. */
export function cascadeTracingRemoveNodeDocument(
  body: RemoveNodeDocumentBody,
): ApiResponse<void> {
  return fetch('/cascade-tracing/transactions/remove-node-document', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Removes an origin order from a trace; the trace graph of `meta.orderId` under `data`. */
export function cascadeTracingRemoveOrigin(
  transactionId: string,
  body: RemoveOriginBody,
) {
  return fetch(
    `/cascade-tracing/transactions/origin/${encodeURIComponent(transactionId)}`,
    { method: 'DELETE', body: JSON.stringify(body) },
  );
}

/** Undoes a purchase-order ↔ sales-order link; 204. */
export function cascadeTracingUnlinkSalesOrder(
  body: LinkSalesOrderBody,
): Promise<{ data: null; error: Error; status: number }> {
  return fetch('/cascade-tracing/orders/unlink-sales-order', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Queues a recalculation of the orders' depth and/or document percentage; 204. */
export function cascadeTracingBulkRefreshCalculation(
  body: BulkRefreshCalculationBody,
): ApiResponse<void> {
  return fetch('/cascade-tracing/orders/bulk-refresh-calculation', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** A node of an order's trace with its company, documents and transactions; under `data`. */
export function cascadeTracingTransactionNodeGet(
  nodeId: string,
  queryParams: GetTracingNodeDetailQuery,
): ApiResponse<TracingNodeDetail> {
  return fetch(
    `/cascade-tracing/transactions/node/${encodeURIComponent(nodeId)}?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/** One tracing node, whichever company it belongs to; under `data`. */
export function cascadeTracingTransactionNodeGetUnscoped(
  nodeId: string,
): ApiResponse<TracingNode> {
  return fetch(
    `/cascade-tracing/transactions/node-unscoped/${encodeURIComponent(nodeId)}`,
    { method: 'GET' },
  );
}

/** Exports one purchase order's trace to Excel; the signed link and file name under `data`. */
export function cascadeTracingPurchaseOrderTraceExport(
  id: string,
): ApiResponse<{ url: string; filename: string }> {
  return fetch(
    `/cascade-tracing/orders/trace/purchases/${encodeURIComponent(id)}/export`,
    { method: 'GET' },
  );
}

/** Exports one sales order's trace to Excel; the signed link and file name under `data`. */
export function cascadeTracingSalesOrderTraceExport(
  id: string,
): ApiResponse<{ url: string; filename: string }> {
  return fetch(
    `/cascade-tracing/orders/trace/sales/${encodeURIComponent(id)}/export`,
    { method: 'GET' },
  );
}

/** The caller's company's whole tag catalogue, newest first; the entries under `data`. */
export function cascadeTracingOrderTagCatalogue(): ApiResponse<OrderTag[]> {
  return fetch('/cascade-tracing/orders/tags/catalogue', { method: 'GET' });
}

/** Adds a tag to the caller's company's catalogue, or answers the one it has; under `data`. */
export function cascadeTracingOrderTagCreate(
  body: CreateOrderTagBody,
): ApiResponse<OrderTag> {
  return fetch('/cascade-tracing/orders/tags', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Adds a type to the caller's company's order types; the new type under `data`. */
export function cascadeTracingOrderTypeCreate(
  body: CreateOrderTypeBody,
): ApiResponse<TracingOrderType> {
  return fetch('/cascade-tracing/orders/order-types', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cascadeTracingOrderTypesGet(): ApiResponse<TracingOrderType[]> {
  return fetch('/cascade-tracing/orders/order-types', { method: 'GET' });
}

export function cascadeTracingDocumentReplace(
  body: ReplaceTracingDocumentBody,
): ApiResponse<ReplacedTracingDocument> {
  return fetch('/cascade-tracing/documents/replace', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** 204 on success; 400 `cannot_delete_documents_from_other_companies` deletes nothing. */
export function cascadeTracingDocumentsDelete(
  body: BulkDeleteTracingDocumentsBody,
): Promise<{ data: null; error: Error; status: number }> {
  return fetch('/cascade-tracing/documents', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/** 422 with `code: 'document_is_origin_order'` for a document on an origin order. */
export function cascadeTracingDocumentPurchaseOrderGet(
  tracingDocumentId: string,
): ApiResponse<TracingDocumentPurchaseOrder> {
  return fetch(
    `/cascade-tracing/documents/${encodeURIComponent(tracingDocumentId)}/purchase-order`,
    { method: 'GET' },
  );
}

export function companyDocumentsGet(
  queryParams: Partial<ListCompanyDocumentsQuery>,
) {
  return fetch(`/company-documents?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export function companyDocumentsCancelRequest(id: string) {
  return fetch(`/company-documents/${id}/cancel-request`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

/**
 * Answers the standard `{ data, metadata }` envelope, with the companies this
 * call newly gave access to under `data.sharedWithCompanyIds`. The legacy `/v3`
 * route answered the full company records through a schema that mangled them;
 * no caller read them. Kept under its legacy name: `api-service.js` re-exports it
 * so the existing callers do not change.
 */
export function companyDocumentShare({
  id,
  companyIds,
}: {
  id: string;
  companyIds: ShareCompanyDocumentBody;
}) {
  return fetch(`/company-documents/${id}/share`, {
    method: 'PUT',
    body: JSON.stringify(companyIds),
  }) as ApiResponse<ShareCompanyDocumentResponse>;
}

export function companyDocumentsRequest(id: string) {
  return fetch(`/company-documents/${id}/request`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function companyDocumentGet(id: string) {
  return fetch(`/company-documents/${id}`, {
    method: 'GET',
  });
}

export function companyDocumentsDownload(body: string[]) {
  return fetch('/company-documents/download', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function companyDocumentsBulkArchive(
  documentIds: BulkArchiveCompanyDocumentsBody,
) {
  return fetch('/company-documents/bulk-archive', {
    method: 'PATCH',
    body: JSON.stringify(documentIds),
  });
}

/**
 * Answers the updated document itself, unwrapped, with its permissions and
 * privacy status — callers swap it straight into the table row via `response.data`.
 */
export function companyDocumentPatch(
  id: string,
  body: PatchCompanyDocumentBody,
) {
  return fetch(`/company-documents/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as ApiResponse<CompanyDocumentDetail>;
}

/**
 * Answers the document itself, unwrapped, with its permissions and privacy status.
 * The legacy `/v3` route answered 201; this answers 200. Callers only check for
 * an error status.
 */
export function companyDocumentMakePublic(body: MakeCompanyDocumentPublicBody) {
  return fetch('/company-documents/make-public', {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as ApiResponse<CompanyDocumentDetail>;
}

/**
 * Answers the created documents as a bare array (201), exactly as the legacy `/v3`
 * route did, so callers read them off `response.data[0]`.
 */
export function companyDocumentBulkCreate(
  body: BulkCreateCompanyDocumentsBody,
) {
  return fetch('/company-documents/bulk-create', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as ApiResponse<BulkCreateCompanyDocumentsResponse>;
}

export function certificationsDownload(body: string[]) {
  return fetch('/company-certifications/download', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export const styleSupplyChainExtend = (
  supplyChainId: string,
  body: {
    companyId: string;
    featuredFacilityProcessId: string;
    nodeId: string;
    lines?: Array<{
      styleId: string;
      quantity: number;
      unit: string;
      secondaryQuantity?: number | null;
      secondaryUnit?: string | null;
    }>;
  },
) => {
  return fetch(`/product-mapping/${supplyChainId}/extend`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export function cpmBulkImport(body: { fileId: string }) {
  return fetch('/product-mapping/bulk-import', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function adminPlatformFeaturesList(
  queryParams: Partial<PlatformFeaturesListQuery> = {},
) {
  return fetch(
    `/admin/platform-features?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

export function adminPlatformFeaturesGetById(id: string) {
  return fetch(`/admin/platform-features/${id}`, {
    method: 'GET',
  });
}

export function adminPlatformFeaturesPatch(
  id: string,
  body: Partial<PatchPlatformFeatureBody>,
) {
  return fetch(`/admin/platform-features/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Lists users across all companies (admin only, delegated-users path — a user
 * appears once per company they belong to). Replaces the legacy
 * `usersGetAll` wrapper that hit `/v3/admin/users/list`.
 */
export function adminUsersList(queryParams: Partial<AdminUsersListQuery>) {
  return fetch(`/admin/users/list?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Lists users who can be impersonated (admin only), filtered by company
 * name. Powers the admin "impersonate a user" search-select dropdown.
 * Replaces the legacy `usersListToImpersonateGet` wrapper that hit
 * `/v3/admin/users-to-impersonate`.
 */
export function adminUsersListToImpersonate(
  queryParams: Partial<AdminUsersListToImpersonateQuery>,
) {
  return fetch(
    `/admin/users/list-to-impersonate?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Gets a user by id, merged with their primary company (admin only).
 * Replaces the legacy `userGetByAdmin` wrapper that hit
 * `/v3/admin/users/:id`.
 */
export function adminUserGet(id: string): Promise<{
  status: number;
  data: AdminUserGetResponse;
  error?: unknown;
}> {
  return fetch(`/admin/users/${id}`, {
    method: 'GET',
  });
}

/**
 * Deletes a user (admin only, any role/company). Replaces the legacy
 * `deleteUserByAdmin` wrapper that hit `/v3/admin/users/:id`.
 */
export function adminUserDelete(id: string): Promise<{
  status: number;
  error?: unknown;
}> {
  return fetch(`/admin/users/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Fully updates a user (admin only). Replaces the legacy `userUpdateByAdmin`
 * wrapper that hit `/v3/admin/users/:id`.
 */
export function adminUserUpdate(
  id: string,
  body: Partial<AdminUserUpdateBody>,
): Promise<{
  status: number;
  data: AdminUserUpdateResponse;
  error?: unknown;
}> {
  return fetch(`/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Patches a user's `isBlocked`/`developmentTag` fields (admin only).
 * Replaces the legacy `userPatchByAdmin` wrapper that hit
 * `/v3/admin/users/:id`.
 */
export function adminUserPatch(
  id: string,
  body: Partial<AdminUserPatchBody>,
): Promise<{
  status: number;
  data: AdminUserPatchResponse;
  error?: unknown;
}> {
  return fetch(`/admin/users/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Bulk-patches multiple users' `isBlocked` field (admin only). Replaces the
 * legacy `usersPatchByAdmin` wrapper that hit `/v3/admin/users/bulk-patch`.
 */
export function adminUsersBulkPatch(body: AdminUsersBulkPatchBody): Promise<{
  status: number;
  data: AdminUsersBulkPatchResponse;
  error?: unknown;
}> {
  return fetch('/admin/users/bulk-patch', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Lists users belonging to a company (admin only). Replaces the legacy
 * `usersByCompanyId` wrapper that hit `/v3/admin/users/list-by-company/:companyId`.
 */
export function adminUsersListByCompany(companyId: string): Promise<{
  status: number;
  data: AdminUsersListByCompanyResponse;
  error?: unknown;
}> {
  return fetch(`/admin/users/list-by-company/${companyId}`, {
    method: 'GET',
  });
}

/**
 * Impersonates a user (admin only). Replaces the legacy
 * `impersonateUserByAdmin` wrapper that hit `/v3/admin/users/:id/impersonate`.
 */
export function adminUserImpersonate(
  id: string,
  queryParams: Partial<AdminUserImpersonateQuery>,
): Promise<{
  status: number;
  data: AdminUserImpersonateResponse;
  error?: unknown;
}> {
  return fetch(
    `/admin/users/${id}/impersonate?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Queues a background job that creates users inside supplier companies linked
 * to `parentCompanyId` (companies not linked are skipped) and emails each new
 * user a login link. Returns a `requestId` to poll for the Excel report.
 * Admin only.
 */
export function adminUsersBulkInvite(body: {
  parentCompanyId: string;
  role: string;
  rows: {
    companyId: string;
    email: string;
    firstName: string;
    lastName?: string | null;
  }[];
}) {
  return fetch('/admin/users/bulk-invite', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Polls the status of a queued bulk-invite request (admin only). */
export function adminUsersBulkInviteStatus(requestId: string) {
  return fetch(`/admin/users/bulk-invite/status/${requestId}`, {
    method: 'GET',
  });
}

/**
 * Creates a user with any role (admin only). Replaces the legacy
 * `userCreateByAdmin` wrapper that hit `/v3/admin/users`.
 */
export function adminUserCreate(body: Partial<AdminCreateUserBody>): Promise<{
  status: number;
  data: AdminCreateUserResponse;
  error?: unknown;
}> {
  return fetch('/admin/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Lists users scoped to the caller's network (non-admin). Supports the
 * `isDropDown`/`selectCoWorkers` modes for select inputs. Replaces the
 * legacy `usersGet` wrapper that hit `/v3/users`.
 */
export function usersList(queryParams: Partial<UsersListQuery>): Promise<{
  status: number;
  data: UsersListResponse;
  error?: unknown;
}> {
  return fetch(`/users?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Creates a user in a company (non-admin). Replaces the legacy `userCreate`
 * wrapper that hit `/v3/users`.
 */
export function createUser(body: Partial<CreateUserBody>): Promise<{
  status: number;
  data: CreateUserResponse;
  error?: unknown;
}> {
  return fetch('/users', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Updates my own record, or a user in a company I own (non-admin). Replaces
 * the legacy `userUpdate` wrapper that hit `/v3/users/:id`.
 */
export function updateUser(
  id: string,
  body: Partial<UpdateUserBody>,
): Promise<{
  status: number;
  data: UserDetailResponse;
  error?: unknown;
}> {
  return fetch(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Gets a user by id (company-owner-facing, scoped to companies linked to the
 * invoker). Replaces the legacy `userGet` wrapper that hit `/v3/users/:id` —
 * note that wrapper was still hitting `/api/users/:id` relative to
 * `VITE_API_SERVICE_URI` (which resolves to `.../api-service`), producing a
 * doubled `/api-service/api/...` path; this uses `backend.url`, which is the
 * correct root for `/api/*` routes.
 */
export function getUser(
  id: string,
  queryParams: Record<string, unknown> = {},
): Promise<{
  status: number;
  data: UserDetailResponse;
  error?: unknown;
}> {
  return fetch(`/users/${id}?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Deletes my own employee account (company owner only). Replaces the legacy
 * `deleteUser` wrapper that hit `/v3/users/:id` — same doubled-path bug as
 * `getUser` above.
 */
export function deleteUser(id: string): Promise<{
  status: number;
  data: null;
  error?: unknown;
}> {
  return fetch(`/users/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Gets the authenticated user's own profile. Replaces the legacy `meGet`
 * wrapper that hit `/v3/me`.
 */
export function meGet(): Promise<{
  status: number;
  data: MeResponse;
  error?: unknown;
}> {
  return fetch('/users/me', {
    method: 'GET',
  });
}

/**
 * Updates the authenticated user's own profile. Replaces the legacy
 * `meUpdate` wrapper that hit `/v3/me`.
 */
export function meUpdate(body: Partial<UpdateUserBody>): Promise<{
  status: number;
  data: UserDetailResponse;
  error?: unknown;
}> {
  return fetch('/users/me', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes the authenticated user's own account. Replaces the legacy
 * `meDelete` wrapper that hit `/v3/me`.
 */
export function meDelete() {
  return fetch('/users/me', {
    method: 'DELETE',
  });
}

/**
 * Patches the authenticated user's own preferences. Replaces the legacy
 * `mePreferencesPatch` wrapper that hit `/v3/me/preferences`. Response is a
 * flat key → value object (no `{data: ...}` envelope), same as before.
 */
export function mePreferencesPatch(
  preferences: Array<{ key: string; value: unknown }>,
): Promise<{
  status: number;
  data: Record<string, unknown>;
  error?: unknown;
}> {
  return fetch('/user-preferences/me', {
    method: 'PATCH',
    body: JSON.stringify(preferences),
  });
}

/**
 * Lists companies (admin only, unscoped). Pass `removed: true` to list
 * soft-deleted companies instead of active ones. Replaces the legacy
 * `adminGetCompaniesList` / `companiesGetAll` wrappers that hit
 * `/v1/admin/companies/list`, and the separate `adminRemovedCompaniesList`
 * wrapper that hit `/v1/admin/companies-removed/list`.
 */
export function adminCompaniesList(
  queryParams: Partial<AdminCompaniesListQuery>,
) {
  return fetch(`/admin/companies?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Lists companies with an active link to a platform feature (admin only).
 * Replaces the legacy `getCompaniesByPlatformFeatureId` wrapper that hit
 * `/v3/admin/companies/list-by-platform-feature-id/:id`.
 */
export function adminCompaniesListByPlatformFeatureId(
  featureId: string,
  queryParams: Partial<AdminCompaniesListByPlatformFeatureIdQuery>,
) {
  return fetch(
    `/admin/platform-features/${featureId}/companies?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

interface AdminCompaniesNetworkQueryParams {
  page?: number;
  count?: number;
  sort?: string;
  removed?: boolean;
}

/**
 * Lists the companies in a given company's network. Pass `removed: true` to
 * browse the network among soft-deleted companies instead. Replaces the
 * separate `adminCompaniesGetListNetwork` (`/admin/companies/:id/list-network`)
 * and `adminRemovedCompaniesGetListNetwork`
 * (`/admin/companies-removed/:id/list-network`) wrappers.
 */
export function adminCompaniesGetListNetwork(
  id: string,
  queryParams?: AdminCompaniesNetworkQueryParams,
): ApiResponse<AdminCompaniesNetworkResponse> {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/companies/${id}/network${params}`, {
    method: 'GET',
  });
}

/**
 * Gets a company by id. Pass `removed: true` to look up a soft-deleted
 * company instead of an active one. Replaces the separate
 * `adminRemovedCompaniesGetById` wrapper that hit
 * `/admin/companies-removed/:id`.
 */
export function adminCompaniesGetById(
  companyId: string,
  queryParams?: { removed?: boolean },
) {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/companies/${companyId}${params}`, {
    method: 'GET',
  });
}

/**
 * Row counts across the tables most relevant to a company, grouped into
 * categories for the admin "tech insights" diagnostics screen (admin only).
 * Replaces the legacy `companyTechInsightsGetByAdmin` wrapper that hit
 * `/v3/admin/companies/:id/list-tech-insights`.
 */
export function adminCompanyTechInsightsGetById(
  companyId: string,
): ApiResponse<CompanyTechInsightsResponse> {
  return fetch(`/admin/companies/${companyId}/tech-insights`, {
    method: 'GET',
  });
}

/**
 * Paginated union of purchase and sales cascade-tracing orders visible to a
 * company (admin only). Replaces the legacy
 * `cascadeTracingOrdersByCompanyGetByAdmin` wrapper that hit
 * `/v3/admin/companies/:id/list-tracing-orders`.
 */
export function adminCompanyTracingOrdersGetByAdmin(
  companyId: string,
  queryParams: Partial<AdminTracingOrdersQuery>,
): Promise<{ data: AdminTracingOrdersResponse; error: Error; status: number }> {
  return fetch(
    `/admin/companies/${companyId}/tracing-orders?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

// Response is a bare array (not the `{data, meta}` envelope) — matches the
// legacy `reply.code(200).send(result)` shape the "add company to user" modal
// reads directly.
export function adminUserCompaniesAvailableCompanies(
  userId: string,
  queryParams?: Partial<AdminAvailableCompaniesQuery>,
): Promise<{
  data: AdminAvailableCompaniesResponse;
  error: unknown;
  status: number;
}> {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/user-companies/available-companies/${userId}${params}`, {
    method: 'GET',
  });
}

// Replaces the legacy `userCompanyRemoveByAdmin` wrapper that hit
// `/v3/admin/user-companies/:id`.
export function adminUserCompaniesRemove(id: string) {
  return fetch(`/admin/user-companies/${id}`, {
    method: 'DELETE',
  });
}

// Replaces the legacy `addUserCompanyByAdmin` wrapper that hit
// `/v3/admin/user-companies`.
export function adminUserCompaniesCreate(
  body: CreateUserCompanyBody,
): Promise<{ data: CreateUserCompanyResponse; error: Error; status: number }> {
  return fetch('/admin/user-companies', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// Replaces the legacy `userCompanyPatchByAdmin` wrapper that hit
// `/v3/admin/user-companies/:id`.
export function adminUserCompaniesUpdate(
  id: string,
  body: Partial<UpdateUserCompanyBody>,
): Promise<{ data: UpdateUserCompanyResponse; error: Error; status: number }> {
  return fetch(`/admin/user-companies/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

// Replaces the legacy `userCompaniesListByAdmin` wrapper that hit
// `/v3/admin/user-companies/:userId`.
export function adminUserCompaniesList(
  userId: string,
): Promise<{ data: ListUserCompaniesResponse; error: Error; status: number }> {
  return fetch(`/admin/user-companies/${userId}`, {
    method: 'GET',
  });
}

// Replaces the legacy `getByEmailShaUnauthorized` wrapper that hit
// `/v3/user-preferences/get-by-user-email-sha-unauthorized`. `emailSha` is
// the recipient's stored `users.email_sha256`, not a plaintext email — kept
// as `email` on the wire since existing unsubscribe email links already
// encode this exact query param.
export function getUserPreferencesByEmailShaUnauthorized(
  emailSha: string,
): ApiResponse<UserPreferencesResultView> {
  return fetch(
    `/user-preferences/get-by-user-email-sha-unauthorized?email=${encodeURIComponent(emailSha)}`,
    { method: 'GET' },
  );
}

// Replaces the legacy `patchPreferencesUnauthorized` wrapper that hit
// `/v3/users/:id/patch-preferences-unauthorized`. `emailSha` is the
// recipient's stored `users.email_sha256`, kept as `email` on the wire for
// the same reason as the GET counterpart above.
export function patchUserPreferencesUnauthorized(
  userId: string,
  emailSha: string,
  body: PatchPreferencesBody,
): ApiResponse<PatchPreferencesResponseView> {
  return fetch(
    `/user-preferences/${userId}/patch-preferences-unauthorized?email=${encodeURIComponent(emailSha)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

/**
 * Fetches a company's own self-profile for a given template. Replaces the
 * legacy `getAdminCompanyProfileById` wrapper that hit
 * `/v1/admin/profiles/:type/companies/:forCompanyId`.
 */
export function adminCompaniesGetProfile(companyId: string, type: string) {
  return fetch(`/admin/companies/${companyId}/profile/${type}`, {
    method: 'GET',
  });
}

export function adminCompaniesGetCreditBalance(companyId: string) {
  return fetch(`/admin/companies/${companyId}/credits/balance`, {
    method: 'GET',
  });
}

export function adminCompaniesGetCredits(
  companyId: string,
  queryParams?: {
    page?: number;
    count?: number;
    sort?: string;
  },
) {
  const params = queryParams
    ? `?${getDefinedUrlSearchParams(queryParams)}`
    : '';
  return fetch(`/admin/companies/${companyId}/credits${params}`, {
    method: 'GET',
  });
}

export function adminCompaniesAddCredits(
  companyId: string,
  body: { amount: number; description?: string },
) {
  return fetch(`/admin/companies/${companyId}/credits`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Updates one or more of a company's admin-managed fields (isDemoCompany,
 * isClientCompany, parentId) in a single request. Removing/restoring a
 * company is a separate action — see `adminCompaniesDelete`/
 * `adminCompaniesRestore`.
 */
export function adminCompaniesPatch({
  companyId,
  body,
}: {
  companyId: string;
  body: {
    isDemoCompany?: boolean;
    isClientCompany?: boolean;
    parentId?: string | null;
  };
}) {
  return fetch(`/admin/companies/${companyId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Soft-removes a company (admin only). */
export function adminCompaniesDelete(companyId: string) {
  return fetch(`/admin/companies/${companyId}`, {
    method: 'DELETE',
  });
}

/** Restores a soft-removed company (admin only). */
export function adminCompaniesRestore(companyId: string) {
  return fetch(`/admin/companies/${companyId}/restore`, {
    method: 'POST',
  });
}

export function startAuditDigitization(auditId: string) {
  return fetch('/audits/digitization', {
    method: 'POST',
    body: JSON.stringify({ auditId }),
  });
}

export function getAuditDigitizationProgress(auditId: string) {
  return fetch(`/audits/digitization/${auditId}`, {
    method: 'GET',
  });
}

export function uploadCertificateDocument(_fileId: string): Promise<void> {
  // TODO: wire up real endpoint
  return Promise.resolve();
}

export function createDigitizedAuditSyncHistory(
  auditId: string,
  body: {
    byCompanyId: string;
    forCompanyId: string;
    onBehalfOfCompanyId: string;
    failedDatapoints: unknown[];
  },
) {
  return fetch(`/audits/digitization/${auditId}/history`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function triggerCertificateAiWorkflow(fileId: string) {
  return fetch(`/certificate-ai/${fileId}`, { method: 'POST' });
}

export function getCertificateAiWorkflow(workflowId: string) {
  return fetch(`/certificate-ai/${workflowId}`, { method: 'GET' });
}

export function cancelCertificateAiWorkflow(body: { workflowId: string }) {
  return fetch('/certificate-ai/cancel', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function getTemplatesList(
  queryParams: Partial<ListTemplatesQuery>,
): Promise<{ data: ListTemplatesResponse; error: Error; status: number }> {
  return fetch(
    `/companies/templates?${getDefinedUrlSearchParams(queryParams)}`,
    {
      method: 'GET',
    },
  );
}

/**
 * Fetches a company's profile for a given template (ABOUT, OTHER_IDS, ...).
 * Replaces the legacy `getCompanyProfileById` wrapper that hit
 * `companies-service/v1/profiles/:type/company/:forCompanyId`. Response is
 * the raw profile object (no `{data, metadata}` envelope) to match the
 * legacy shape that dozens of existing callers rely on.
 */
export function getCompanyProfile(
  templateId: string,
  forCompanyId: string,
): Promise<{ data: CompanyProfileResponse; error: Error; status: number }> {
  return fetch(
    `/companies/templates/${templateId.toLowerCase()}/profile/${forCompanyId}`,
    { method: 'GET' },
  );
}

/**
 * Bulk-fetches ABOUT profiles for a set of companies. Replaces the legacy
 * `getGlobalCompanyProfiles` wrapper that hit
 * `companies-service/v1/profiles/:profileType/companies`. Response is a raw
 * array (no envelope) to match the legacy shape existing callers rely on.
 * `facilityProcesses` on each item is intentionally a raw JSON string —
 * callers parse it themselves (see legacy callers' `JSON.parse` calls).
 */
export function getBulkCompanyProfiles(
  templateId: string,
  forCompanyIds: string,
): Promise<{ data: BulkCompanyProfileItem[]; error: Error; status: number }> {
  return fetch(
    `/companies/templates/${templateId.toLowerCase()}/profiles?forCompanyIds=${encodeURIComponent(forCompanyIds)}`,
    { method: 'GET' },
  );
}

/**
 * The admin facility-processes table, or every match with `isDropDown`.
 * Replaces the legacy `managementFacilityProcessesGet` wrapper that hit
 * `api-service/v3/admin/facility-processes`, which answered `{ data, meta }`;
 * this answers `{ data, metadata: { success, pagination } }` — adapt it for
 * `useTable` with `toTablePaginationMeta`.
 */
export function adminFacilityProcessesList(
  queryParams: Partial<AdminFacilityProcessesListQuery>,
): Promise<{
  data: {
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    data: AdminFacilityProcessRow[];
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/facility-processes?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Creates a facility process. Replaces the legacy
 * `managementFacilityProcessCreate` wrapper that hit
 * `POST api-service/v3/admin/facility-processes/`, which answered the bare
 * record; this answers `{ data, metadata }`.
 */
export function adminFacilityProcessCreate(
  body: AdminCreateFacilityProcessBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminFacilityProcess };
  error: Error;
  status: number;
}> {
  return fetch('/admin/facility-processes', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Updates a facility process. Replaces the legacy
 * `managementFacilityProcessUpdate` wrapper that hit
 * `PUT api-service/v3/admin/facility-processes/:id`, which answered the bare
 * record; this answers `{ data, metadata }`. Omitting `translations` keeps the
 * stored ones.
 */
export function adminFacilityProcessUpdate(
  id: string,
  body: AdminUpdateFacilityProcessBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminFacilityProcess };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/facility-processes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * One facility process for the admin edit drawer and translations form.
 * Replaces the legacy `managementFacilityProcessGet` wrapper that hit
 * `api-service/v3/admin/facility-processes/:id`, which answered the bare
 * entity; this answers the `{ data, metadata }` envelope.
 */
export function adminFacilityProcessGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: AdminFacilityProcess };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/facility-processes/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * The production processes (admin): a page for the management table, or every
 * row with `isDropDown: true` for the pickers. Replaces the legacy
 * `managementProductionProcessesGet` wrapper that hit
 * `api-service/v3/admin/production-processes`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function adminProductionProcessesList(
  queryParams: Partial<AdminProductionProcessesListQuery>,
): Promise<{
  data: {
    data: AdminProductionProcessRow[];
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/production-processes?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * One production process for the admin edit drawer and translations form.
 * Replaces the legacy `managementProductionProcessGet` wrapper that hit
 * `api-service/v3/admin/production-processes/:id`, which answered the bare
 * entity; this answers the `{ data, metadata }` envelope.
 */
export function adminProductionProcessGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductionProcess };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/production-processes/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * One help-center article, proxied from Intercom, for the help drawer. Public —
 * the login page opens one before sign-in. Replaces the legacy `fetchArticle`
 * wrapper that hit `api-service/v3/intercom/articles/:id`. Answers the
 * `{ data, metadata }` envelope; legacy sent the bare article.
 */
export function helpArticleGet(articleId: string): Promise<{
  data: { data: HelpArticle; metadata: { success: boolean } };
  error: Error;
  status: number;
}> {
  // Not encoded: a `#heading` anchor some callers append must stay a fragment
  // the browser drops, as it did on the legacy route.
  return fetch(`/help-articles/${articleId}`, {
    method: 'GET',
  });
}

/**
 * The caller's in-app notifications (`notificationTriggerType: 'informative'`)
 * or tasks (`'actionable'`) for the activity feed. Replaces the legacy
 * `getInAppNotifications` wrapper that hit `api-service/v3/notifications`.
 * Answers the paginated `{ data, metadata: { pagination } }` envelope
 * (1-based `pagination.page`); legacy sent `{ data, meta }`.
 */
export function notificationsList(
  queryParams: Partial<NotificationsListQuery>,
): Promise<{
  data: {
    data: AppNotificationRow[];
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(`/notifications?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Sets `status` on all of the caller's informative notifications — "mark all
 * as read". Answers 204. Replaces the legacy `changeAllNotificationsStatus`
 * wrapper that hit `PATCH api-service/v3/notifications`.
 */
export function notificationsMarkAll(body: MarkNotificationsBody) {
  return fetch('/notifications', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Sets `status` on one of the caller's notifications — mark read, or dismiss
 * or reactivate a task. Answers 204. Replaces the legacy
 * `changeNotificationStatus` wrapper that hit
 * `PATCH api-service/v3/notifications/:id`.
 */
export function notificationsMark(id: string, body: MarkNotificationsBody) {
  return fetch(`/notifications/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * The material catalogue for the pickers: a page, or every row with
 * `isDropDown: true`. Replaces the legacy `materialsGet` wrapper that hit
 * `api-service/v3/materials`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function materialsList(
  queryParams: Partial<MaterialsListQuery>,
): Promise<{
  data: {
    data: AdminMaterialRow[];
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(`/materials?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * The materials (admin): a page for the management table, or every row with
 * `isDropDown: true` for the pickers. Replaces the legacy `materialsAdminGet`
 * wrapper that hit `api-service/v3/admin/materials`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function adminMaterialsList(
  queryParams: Partial<AdminMaterialsListQuery>,
): Promise<{
  data: {
    data: AdminMaterialRow[];
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/materials?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * The v1 supply chains a company is on, as brand or supplier (admin): paged,
 * sorted and filtered; active chains only unless `filter` sets `isArchived` to
 * `1`. Replaces the legacy `supplyChainsGetUnscoped` wrapper that hit
 * `v3/admin/supplychains`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function adminSupplyChainsList(
  queryParams: Partial<AdminSupplyChainsListQuery>,
): Promise<{
  data: {
    data: AdminSupplyChainsListResponse;
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/supply-chains?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * A v1 supply chain of any brand (admin), with its nodes (each with its
 * company; removed companies flagged) and links. Replaces the legacy
 * `supplyChainGetUnscoped` wrapper that hit `v3/admin/supplychains/:id`, which
 * answered the bare record; this answers `{ data, metadata }`. 404
 * `invalid_supply_chain_id` for an unknown or removed chain.
 */
export function adminSupplyChainGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: AdminSupplyChain };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/supply-chains/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * One of the caller's own v1 supply chains, with its nodes (each with its
 * company; removed companies flagged) and links. Replaces the legacy
 * `supplyChainGet` wrapper that hit `v3/supplychains/:id`, which answered the
 * bare record; this answers `{ data, metadata }`. 404
 * `invalid_supply_chain_id` for an unknown or removed chain, or another
 * brand's.
 */
export function supplyChainGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: SupplyChain };
  error: Error;
  status: number;
}> {
  return fetch(`/supply-chains/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * The caller's own v1 supply chains: paged, sorted and filtered; active chains
 * only unless `filter` sets `isArchived` to `1`. `isDropDown` answers every
 * match in one page, for the pickers. Replaces the legacy `supplyChainsGet`
 * wrapper that hit `v3/supplychains`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function supplyChainsList(
  queryParams: Partial<SupplyChainsListQuery>,
): Promise<{
  data: {
    data: SupplyChainsListResponse;
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(`/supply-chains?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * Every company's generated tag identifications (admin): paged, sorted and
 * filtered, each with its URL redirection and linked tag. Replaces the legacy
 * `tagIdentificationsAdminGet` wrapper that hit `v3/admin/tag-identifications`.
 * Answers the paginated `{ data, metadata: { pagination } }` envelope (1-based
 * `pagination.page`); legacy sent `{ data, meta }`.
 */
export function adminTagIdentificationsList(
  queryParams: Partial<AdminTagIdentificationsListQuery>,
): Promise<{
  data: {
    data: AdminTagIdentificationsListResponse;
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/tag-identifications?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Sets where a tag identification's QR scans redirect, replacing any existing
 * redirection, or clears it with `redirectionUrl: null`. Replaces the legacy
 * `managementTagIdenficationPatch` wrapper that hit
 * `v3/admin/tag-identifications/:id`, which kept an existing redirection
 * unchanged and took `{}` as a clear. Answers `{ data, metadata }` with the
 * row as `adminTagIdentificationsList` returns it.
 */
export function adminTagIdentificationPatch(
  id: string,
  body: AdminPatchTagIdentificationBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminTagIdentificationRow };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/tag-identifications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Unlinks a tag (a row's `tagId`) from its asset (admin); for a product it
 * also clears the QR code, and the product's style gets a new one. Replaces
 * the legacy `managementTagIdenficationRemoveTag` wrapper that hit
 * `v3/admin/tag-identifications/tagged/:id`. 204, no body, also for an
 * unknown tag.
 */
export function adminTagIdentificationRemoveTag(tagId: string): Promise<{
  data: undefined;
  error: Error;
  status: number;
}> {
  return fetch(`/admin/tag-identifications/tags/${encodeURIComponent(tagId)}`, {
    method: 'DELETE',
  });
}

/**
 * One material for the admin edit drawer and translations form. Replaces the
 * legacy `materialAdminGet` wrapper that hit `api-service/v3/admin/materials/:id`,
 * which answered the bare entity; this answers the `{ data, metadata }`
 * envelope.
 */
export function adminMaterialGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: AdminMaterial };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/materials/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * Replaces a material from the admin edit drawer. Replaces the legacy
 * `materialAdminUpdate` wrapper that hit `api-service/v3/admin/materials/:id`,
 * which answered the bare entity; this answers the `{ data, metadata }`
 * envelope. A full replacement: every field left out is cleared.
 */
export function adminMaterialUpdate(
  id: string,
  body: AdminUpdateMaterialBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminMaterial };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/materials/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Updates some fields of a material: the admin table's inline edits and the
 * translations form. Replaces the legacy `materialsAdminPatch` wrapper that hit
 * `api-service/v3/admin/materials/:id`, which answered the bare entity; this
 * answers the `{ data, metadata }` envelope. A key left out keeps its stored
 * value and `null` clears it; unknown keys (the translations form sends the
 * whole record) are ignored.
 */
export function adminMaterialPatch(
  id: string,
  body: Partial<Record<keyof AdminPatchMaterialBody, unknown>>,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminMaterial };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/materials/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * One product category with its primary and secondary facility processes: the
 * edit dialog's prefill and the parent's name above a drilled-in list.
 * Replaces the legacy `managementProductCategoryGet` wrapper that hit
 * `GET api-service/v3/admin/product-categories/:id` and got the bare record;
 * this gets `{ data, metadata }`, so callers read `data.data`.
 */
export function adminProductCategoryGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductCategory };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/product-categories/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * Product categories: the admin categories table (a page, scoped by the
 * `filter`'s `parentId`) and, with `isDropDown`, every category for the admin
 * pickers. Replaces the legacy `managementProductCategoriesGet` wrapper that
 * hit `GET api-service/v3/admin/product-categories` and got `{ data, meta }`;
 * this gets `{ data, metadata: { pagination } }` (1-based page), so `data.data`
 * is still the rows.
 */
export function adminProductCategoriesList(
  queryParams: Partial<AdminProductCategoriesListQuery>,
): Promise<{
  data: {
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    data: AdminProductCategoriesListResponse;
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/product-categories?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Replaces one product category from the edit dialog (`name` required; units
 * and process lists left out are stored empty; the weight flag is kept).
 * Replaces the legacy `managementProductCategoryUpdate` wrapper that hit
 * `PUT api-service/v3/admin/product-categories/:id` and got the bare record;
 * this gets `{ data, metadata }`, so callers read `data.data`.
 */
export function adminProductCategoryUpdate(
  id: string,
  body: AdminUpdateProductCategoryBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductCategory };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/product-categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Updates the keys sent on one product category: the list's inline name, unit,
 * process and weight-flag editors. Replaces the legacy
 * `managementProductCategoryPatch` wrapper that hit
 * `PATCH api-service/v3/admin/product-categories/:id` and got the bare record;
 * this gets `{ data, metadata }`.
 */
export function adminProductCategoryPatch(
  id: string,
  body: AdminPatchProductCategoryBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductCategory };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/product-categories/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Creates a product category from the add-category dialog. Replaces the legacy
 * `managementProductCategoryCreate` wrapper that hit
 * `POST api-service/v3/admin/product-categories` and got the bare record; this
 * gets `{ data, metadata }` (201), so callers read `data.data`.
 */
export function adminProductCategoryCreate(
  body: AdminCreateProductCategoryBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductCategory };
  error: Error;
  status: number;
}> {
  return fetch('/admin/product-categories', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * One product category's fields, for its fields page (`filter` carries the
 * `productCategoryId`). Replaces the legacy `managementProductCategoryFieldsGet`
 * wrapper that hit `GET api-service/v3/admin/product-category-fields` and got
 * `{ data, meta }`; this gets `{ data, metadata: { pagination } }` (1-based
 * page), so `data.data` is still the rows.
 */
export function adminProductCategoryFieldsList(
  queryParams: Partial<AdminProductCategoryFieldsListQuery>,
): Promise<{
  data: {
    data: AdminProductCategoryFieldsListResponse;
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/product-category-fields?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Saves the add dialog on a product category's fields page. Replaces the
 * legacy `managementProductCategoryFieldCreate` wrapper that hit
 * `POST api-service/v3/admin/product-category-fields`, which answered 201 with
 * the bare record; this answers 201 with the `{ data, metadata }` envelope.
 */
export function adminProductCategoryFieldCreate(
  body: AdminCreateProductCategoryFieldBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductCategoryField };
  error: Error;
  status: number;
}> {
  return fetch('/admin/product-category-fields', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes a field from a product category's fields page. Replaces the legacy
 * `managementProductCategoryFieldDelete` wrapper that hit
 * `DELETE api-service/v3/admin/product-category-fields/:id`, which answered
 * 200 with an empty body; this answers 204.
 */
export function adminProductCategoryFieldDelete(
  id: string,
): Promise<{ data: null; error: Error; status: number }> {
  return fetch(`/admin/product-category-fields/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Saves the edit dialog on a product category's fields page. Replaces the
 * legacy `managementProductCategoryFieldUpdate` wrapper that hit
 * `PUT api-service/v3/admin/product-category-fields/:id`, which answered the
 * bare record; this answers the `{ data, metadata }` envelope. A full
 * replacement: options left out are cleared, and a stored standard field is
 * never overwritten.
 */
export function adminProductCategoryFieldUpdate(
  id: string,
  body: AdminUpdateProductCategoryFieldBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductCategoryField };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/product-category-fields/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Saves the inline index, name and standard-field cells on a product
 * category's fields page. Replaces the legacy
 * `managementProductCategoryFieldPatch` wrapper that hit
 * `PATCH api-service/v3/admin/product-category-fields/:id`, which answered the
 * bare record; this answers the `{ data, metadata }` envelope. A key left out
 * keeps its stored value; `idx` may be the digit string the cell sends.
 */
export function adminProductCategoryFieldPatch(
  id: string,
  body: Partial<Record<keyof AdminPatchProductCategoryFieldBody, unknown>>,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductCategoryField };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/product-category-fields/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Creates a material from the admin drawer. Replaces the legacy
 * `materialAdminCreate` wrapper that hit `api-service/v3/admin/materials`,
 * which answered 200 with the bare entity; this answers 201 with the
 * `{ data, metadata }` envelope.
 */
export function adminMaterialCreate(body: AdminCreateMaterialBody): Promise<{
  data: { metadata: { success: boolean }; data: AdminMaterial };
  error: Error;
  status: number;
}> {
  return fetch('/admin/materials', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Updates a production process's colours, depth level and translations
 * (admin). Replaces the legacy `managementProductionProcessUpdate` wrapper
 * that hit `api-service/v3/admin/production-processes/:id`, which answered the
 * bare entity; this answers the `{ data, metadata }` envelope. Keys outside
 * the body (the old translations form sends the whole record) are ignored.
 */
export function adminProductionProcessUpdate(
  id: string,
  body: AdminUpdateProductionProcessBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminProductionProcess };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/production-processes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Every facility process, grouped under its production process in position
 * order. Replaces the legacy `getFacilityProcesses` wrapper that hit
 * `api-service/v3/facility-processes/drop-down`, which answered a bare
 * `{ facilities }` body; this answers the standard `{ data, metadata }`
 * envelope, so callers read `response.data.data`.
 */
export function facilityProcessesDropdown(): Promise<{
  data: {
    metadata: { success: boolean };
    data: FacilityProcessesDropdownResponse;
  };
  error: Error;
  status: number;
}> {
  return fetch('/facility-processes/dropdown', { method: 'GET' });
}

/**
 * Fetches the impact-areas hierarchy for a section. Replaces the legacy
 * `getImpactAreasBySectionId` wrapper that hit
 * `companies-service/v1/impact-areas/hierarchy/:sectionId`. Response is a
 * raw array (no envelope) to match the legacy shape existing callers rely on.
 */
export function getImpactAreasHierarchy(sectionId: string | number): Promise<{
  data: ImpactAreasHierarchyResponse;
  error: Error;
  status: number;
}> {
  return fetch(`/companies/impact-areas/hierarchy/${sectionId}`, {
    method: 'GET',
  });
}

/**
 * The flat impact-area taxonomy — every row of
 * `companies_service_impact_areas`, in hierarchy order.
 *
 * Replaces the legacy `GET /api-service/v3/impact-areas?isDropDown=true` for
 * the CAPA dropdowns. These are the *modern* ids that findings and actions
 * now store; the legacy route's random ids no longer appear anywhere in the
 * database. `useImpactAreasList` maps the rows to the field names the CAPA
 * views read.
 */
export function listImpactAreas(): Promise<{
  data: { metadata: { success: boolean }; data: ImpactAreaListItem[] };
  error: Error;
  status: number;
}> {
  return fetch('/impact-areas', { method: 'GET' });
}

/**
 * Lists data point definitions merged with any existing answers for a
 * company. Replaces the legacy `listDataPointsAnswers` wrapper that hit
 * `companies-service/v1/data-points-answers/companies/:forCompanyId`.
 * Response keeps the `{data, meta}` paginated envelope to match the legacy
 * shape existing callers rely on.
 */
export function listDataPointAnswers(
  forCompanyId: string,
  queryParams: Partial<ListDataPointAnswersQuery>,
): Promise<{
  data: ListDataPointAnswersResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/companies/${forCompanyId}/data-points-answers?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

export function patchTemplateAnswer(
  templateId: string,
  body: PatchTemplateAnswerBody,
) {
  return fetch(`/companies/templates/${templateId.toLowerCase()}/answer`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function adminPatchTemplateAnswer(
  companyId: string,
  templateId: string,
  body: Omit<PatchTemplateAnswerBody, 'forCompanyId' | 'onBehalfOfCompanyId'>,
) {
  return fetch(
    `/admin/companies/${companyId}/templates/${templateId.toLowerCase()}/answer`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
  );
}

export async function companyCreate(
  body: Pick<
    CreateCompanyBody,
    | 'officialName'
    | 'facilityProcesses'
    | 'country'
    | 'connectionType'
    | 'city'
    | 'addressLine1'
  > &
    Partial<Pick<CreateCompanyBody, 'zipCode' | 'addressLine2' | 'geojson'>>,
): Promise<{
  data: CreateCompanyResponse | null;
  error: unknown;
  status: number;
}> {
  const raw = (await fetch('/companies', {
    method: 'POST',
    body: JSON.stringify(body),
  })) as {
    data: { data: CreateCompanyResponse } | null;
    error: unknown;
    status: number;
  };
  return { ...raw, data: raw.data?.data ?? null };
}

/**
 * Links two companies bidirectionally as supplier/client. Replaces the
 * legacy `companyLink` wrapper that hit `companies-service/v1/companies/link`.
 * Response keeps the legacy flat `{id, connectionType}` shape (no `data`
 * envelope) to match existing callers.
 */
export function linkCompany(body: CompanyLinkBody): Promise<{
  data: CompanyLinkResponse;
  error: Error;
  status: number;
}> {
  return fetch('/companies/link', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Accepts the Terms of Use for the caller's user/company relationship.
 * Replaces the legacy `acceptTerms` wrapper that hit `v3/users/accept-terms`.
 */
export function acceptTerms(body: AcceptUserCompanyTermsBody) {
  return fetch('/users/accept-terms', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Every other company the caller belongs to, for the account switcher.
 * Replaces the legacy `companiesListToSwitchGet` wrapper that hit
 * `v3/user-companies/companies-to-switch`.
 */
export function companiesListToSwitch(): Promise<{
  data: CompanyToSwitchRow[];
  error: Error;
  status: number;
}> {
  return fetch('/user-companies/companies-to-switch', { method: 'GET' });
}

/**
 * Distinct, sorted country codes across the caller's network — powers the
 * country filter dropdown on company-selection tables. Replaces the legacy
 * `companiesGetTableData` wrapper that hit `v3/companies/table-data`.
 */
export function companiesGetNetworkCountries(): Promise<{
  data: NetworkCountriesResponse;
  error: Error;
  status: number;
}> {
  return fetch('/companies/network-countries', { method: 'GET' });
}

/**
 * The caller company's supplier network graph, as the graph view draws it.
 * Replaces the legacy `getNetworkCompaniesGraph` wrapper that hit
 * `v3/network-companies/graph`, which answered the bare view; this answers it
 * in `{ data, metadata }`. 404 `graph_not_found` when the company has none.
 */
export function getNetworkGraph(queryParams: Partial<NetworkGraphQuery> = {}) {
  return fetch(
    `/network-companies/graph?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as ApiResponse<NetworkGraph>;
}

/**
 * Builds the caller company's supplier network graph — "generate graph" on the
 * graph view; a company that already has one gets it back unchanged. Replaces
 * the legacy `createNetworkCompaniesGraph` wrapper that hit
 * `v3/network-companies/graph`, which answered the whole graph; this answers
 * its `id` in `{ data, metadata }` with 201 — read the graph separately.
 */
export function createNetworkGraph() {
  return fetch('/network-companies/graph', {
    method: 'POST',
  }) as ApiResponse<NetworkGraphCreated>;
}

/**
 * Links two nodes on the caller company's supplier network graph. Replaces the
 * legacy `createLinkNetworkCompaniesGraph` wrapper that hit
 * `v3/network-companies/graph/:id/links`, which answered the bare link; this
 * answers `{ data, metadata }` with 201. A repeated link answers the existing
 * one; 400 `invalid_network_graph_node` unless both are different nodes of the
 * graph.
 */
export function createNetworkGraphLink(
  graphId: string,
  body: CreateNetworkGraphLinkBody,
) {
  return fetch(
    `/network-companies/graph/${encodeURIComponent(graphId)}/links`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  ) as ApiResponse<NetworkGraphLink>;
}

/**
 * Unlinks two nodes on the caller company's supplier network graph. Replaces
 * the legacy `deleteLinkNetworkCompaniesGraph` wrapper that hit
 * `v3/network-companies/graph/:graphId/links/:linkId`. Answers 204, also for
 * a link that isn't in the graph.
 */
export function deleteNetworkGraphLink(graphId: string, linkId: string) {
  return fetch(
    `/network-companies/graph/${encodeURIComponent(graphId)}/links/${encodeURIComponent(linkId)}`,
    { method: 'DELETE' },
  );
}

/**
 * Saves node positions on the caller company's supplier network graph — the
 * graph's automatic layout and node drags. Replaces the legacy
 * `patchNetworkCompaniesNodes` wrapper that hit
 * `v3/network-companies/graph/:id/nodes`. Answers 204; 400
 * `some_nodes_are_invalid` when an id isn't a node of the graph.
 */
export function patchNetworkGraphNodes(
  graphId: string,
  nodes: PatchNetworkGraphNodesBody,
) {
  return fetch(
    `/network-companies/graph/${encodeURIComponent(graphId)}/nodes`,
    {
      method: 'PATCH',
      body: JSON.stringify(nodes),
    },
  );
}

/**
 * Collaboration statuses with the number of company profiles using each
 * (admin). Replaces the legacy `getCollaborationStatuses` wrapper that hit
 * `v3/admin/collaboration-statuses`. `meta.totalCount` is now the number of
 * matching statuses; legacy sent the first row's usage count there.
 */
export function adminCollaborationStatusesList(
  queryParams?: Partial<AdminCollaborationStatusesListQuery>,
): Promise<{
  data: AdminCollaborationStatusesListResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/collaboration-statuses?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Creates a collaboration status (admin). Replaces the legacy
 * `createCollaborationStatus` wrapper that hit `v3/admin/collaboration-statuses`.
 * A duplicate name comes back as 409 `collaboration_status_already_exist`.
 */
export function adminCollaborationStatusesCreate(
  body: AdminCreateCollaborationStatusBody,
): Promise<{
  data: AdminCollaborationStatus;
  error: Error;
  status: number;
}> {
  return fetch('/admin/collaboration-statuses', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Certification rules, each with its certification's name (admin). Replaces
 * the legacy `getRules` wrapper that hit `v3/admin/rules`. Answers the
 * paginated `{ data, metadata: { pagination } }` envelope (1-based
 * `pagination.page`); legacy sent `{ data, meta }`.
 */
export function adminCertificationRulesList(
  queryParams: Partial<AdminCertificationRulesListQuery>,
): Promise<{
  data: {
    data: AdminCertificationRulesListResponse;
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/certification-rules?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Adds a certification rule (admin). Replaces the legacy `createRule` wrapper
 * that hit `v3/admin/rules`. Answers 201 with `{ data, metadata }`; legacy
 * answered 200 with the bare row.
 */
export function adminCertificationRulesCreate(
  body: AdminCreateCertificationRuleBody,
): Promise<{
  data: { data: CertificationRule; metadata: { success: boolean } };
  error: Error;
  status: number;
}> {
  return fetch('/admin/certification-rules', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces a certification rule (admin), snapshotting it into its history
 * first. A full replacement: an omitted `entityId` or `rules` is written as
 * null. Replaces the legacy `updateRule` wrapper that hit `v3/admin/rules/:id`.
 * Answers `{ data, metadata }`; legacy answered the bare row.
 */
export function adminCertificationRulesUpdate(
  id: string,
  body: AdminUpdateCertificationRuleBody,
): Promise<{
  data: { data: CertificationRule; metadata: { success: boolean } };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/certification-rules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes a certification rule (admin), snapshotting it into its history
 * first. Replaces the legacy `deleteRule` wrapper that hit
 * `v3/admin/rules/:id`. Answers 204; legacy answered 200 with an empty body.
 */
export function adminCertificationRulesDelete(id: string) {
  return fetch(`/admin/certification-rules/${id}`, {
    method: 'DELETE',
  });
}

/**
 * The caller's products as a company's page lists them — on a partner's page,
 * only what that partner supplies. Replaces `api.companyProductsGet` (legacy
 * GET /v3/companies/:id/companies-products); same `{ data, meta }` envelope
 * with a 0-based `meta.page`, so callers read it unchanged.
 */
export function companyProductsList(
  companyId: string,
  queryParams: Partial<CompanyProductsQuery> = {},
) {
  return fetch(
    `/companies/${encodeURIComponent(companyId)}/products?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: CompanyProductsResponse;
    error: Error;
    status: number;
  }>;
}

/**
 * The caller's custom questions (its own and the default ones) with what it
 * answered about a partner company — the custom-information tab. Replaces
 * `api.getQuestionsWithAnswers` (legacy
 * GET /v3/companies/:id/c2c-custom-questions); same `{ data, meta }` envelope
 * with a 0-based `meta.page`, so callers read it unchanged.
 */
export function companyC2cCustomQuestionsList(
  companyId: string,
  queryParams: Partial<C2cCompanyQuestionsListQuery> = {},
) {
  return fetch(
    `/companies/${encodeURIComponent(companyId)}/c2c-custom-questions?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: C2cCompanyQuestionsListResponse;
    error: Error;
    status: number;
  }>;
}

/**
 * A page of the caller company's custom information fields (its own and the
 * default ones) with their options — the field builder. Replaces
 * `api.c2cCustomQuestionsGet` (legacy GET /v3/companies/c2c-custom-questions/list);
 * same `{ data, meta }` envelope with a 0-based `meta.page`.
 */
export function c2cCustomQuestionsList(
  queryParams: Partial<C2cQuestionsListQuery> = {},
) {
  return fetch(
    `/c2c-custom-questions?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  ) as Promise<{
    data: C2cQuestionsListResponse;
    error: Error;
    status: number;
  }>;
}

/**
 * One custom information field the caller company sees (its own or a default
 * one) with its options. Replaces `api.c2cCustomQuestionGet` (legacy
 * GET /v3/companies/c2c-custom-questions/:id), which answered the bare
 * question; it is now `response.data.data`.
 */
export function c2cCustomQuestionGet(id: string) {
  return fetch(`/c2c-custom-questions/${encodeURIComponent(id)}`, {
    method: 'GET',
  }) as Promise<{
    data: { data: C2cQuestion };
    error: Error;
    status: number;
  }>;
}

/**
 * Adds one custom information field to the caller's company. Replaces
 * `api.c2cCustomQuestion` (legacy POST /v3/companies/c2c-custom-questions),
 * which took an array and answered a bare array; this takes one field and
 * answers 201 with it under `response.data.data`.
 */
export function c2cCustomQuestionCreate(body: C2cCreateQuestionBody) {
  return fetch('/c2c-custom-questions', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as Promise<{
    data: { data: C2cQuestion };
    error: Error;
    status: number;
  }>;
}

/**
 * Removes the caller company's own custom information fields named in `ids`
 * (a soft delete). Replaces `api.c2cCustomQuestionBulkRemove` (legacy
 * PUT /v3/companies/c2c-custom-questions/bulk-remove), which took a bare array
 * of ids and answered 200; this answers 204.
 */
export function c2cCustomQuestionsRemove(body: C2cBulkRemoveQuestionsBody) {
  return fetch('/c2c-custom-questions/bulk-remove', {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as Promise<{ data: unknown; error: Error; status: number }>;
}

/**
 * Edits one of the caller company's own custom information fields. Replaces
 * `api.c2cCustomQuestionEdit` (legacy PUT /v3/companies/c2c-custom-questions/:id),
 * which also deleted with `{ isRemoved: 1 }`; delete through
 * `c2cCustomQuestionsRemove` instead.
 */
export function c2cCustomQuestionUpdate(
  id: string,
  body: C2cUpdateQuestionBody,
) {
  return fetch(`/c2c-custom-questions/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as Promise<{
    data: { data: C2cQuestion };
    error: Error;
    status: number;
  }>;
}

/**
 * Saves the caller's answers about a partner company to custom questions —
 * the custom-information tab. Replaces `api.answerC2CQuestion` (legacy
 * POST /v3/companies/:id/c2c-custom-questions/answer), which answered a bare
 * array; the answered questions are now `response.data.data`.
 */
export function companyC2cCustomQuestionAnswersSave(
  companyId: string,
  body: C2cCompanyAnswersBody,
) {
  return fetch(
    `/companies/${encodeURIComponent(companyId)}/c2c-custom-questions/answers`,
    { method: 'PUT', body: JSON.stringify(body) },
  ) as Promise<{
    data: { data: C2cCompanyAnswersResponse };
    error: Error;
    status: number;
  }>;
}

/**
 * A page of common custom information fields (admin). Replaces the legacy
 * `c2cDefaultQuestionsGetByAdmin` wrapper that hit
 * `v3/admin/c2c-default-questions`. Lists default fields only — legacy also
 * listed the acting admin's own company's fields. Answers
 * `{ data, metadata: { pagination } }` (1-based `pagination.page`); legacy
 * answered `{ data, meta }`.
 */
export function adminC2cDefaultQuestionsList(
  queryParams: Partial<AdminC2cDefaultQuestionsListQuery>,
): Promise<{
  data: {
    data: AdminC2cDefaultQuestionsListResponse;
    metadata: { pagination: { limit: number; total: number } };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/c2c-default-questions?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * One common custom information field with its answer options (admin).
 * Replaces the legacy `c2cDefaultQuestionGetByAdmin` wrapper that hit
 * `v3/admin/c2c-default-questions/:id`. 404 for a removed or company-owned
 * question. Answers `{ data, metadata }`; legacy answered the bare row.
 */
export function adminC2cDefaultQuestionsGet(id: string): Promise<{
  data: { data: C2cQuestion };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/c2c-default-questions/${id}`, { method: 'GET' });
}

/**
 * Adds a common custom information field (admin). Replaces the legacy
 * `c2cDefaultQuestionByAdmin` wrapper that hit
 * `POST v3/admin/c2c-default-questions` with a one-item array. 409
 * `api_field_identifier_is_already_in_use` when another common field has the
 * identifier. Answers 201 with `{ data, metadata }`; legacy answered a bare
 * array.
 */
export function adminC2cDefaultQuestionsCreate(
  body: AdminCreateC2cDefaultQuestionBody,
): Promise<{ data: { data: C2cQuestion }; error: Error; status: number }> {
  return fetch('/admin/c2c-default-questions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Edits a common custom information field's title and/or hint (admin).
 * Replaces the legacy `c2cDefaultQuestionEditByAdmin` wrapper that hit
 * `PUT v3/admin/c2c-default-questions/:id`. The API identifier, type and
 * options cannot be changed, and deleting goes through
 * `adminC2cDefaultQuestionsBulkRemove`. Answers `{ data, metadata }`; legacy
 * answered the bare row.
 */
export function adminC2cDefaultQuestionsUpdate(
  id: string,
  body: AdminUpdateC2cDefaultQuestionBody,
): Promise<{ data: { data: C2cQuestion }; error: Error; status: number }> {
  return fetch(`/admin/c2c-default-questions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes (soft-removes) common custom information fields (admin). Replaces the
 * legacy `c2cDefaultQuestionBulkRemoveByAdmin` wrapper that hit
 * `v3/admin/c2c-default-questions/bulk-remove` with a bare array body. Only
 * default fields are removed; 404 when none of the ids names one. Answers 204;
 * legacy answered 200 with an empty body.
 */
export function adminC2cDefaultQuestionsBulkRemove(
  body: AdminBulkRemoveC2cDefaultQuestionsBody,
) {
  return fetch('/admin/c2c-default-questions/bulk-remove', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Renames a collaboration status (admin). Replaces the legacy
 * `updateCollaborationStatus` wrapper, which called `/admin/collaboration-statuses/:id`
 * on the api-service host — a path the legacy service never registered (it only
 * served `/v3/admin/…`), so the inline rename always 404'd. An unknown id is 404
 * `collaboration_status_not_found`; a name another status already has is 409
 * `collaboration_status_already_exist`.
 */
export function adminCollaborationStatusesUpdate(
  id: string,
  body: AdminUpdateCollaborationStatusBody,
): Promise<{
  data: AdminCollaborationStatus;
  error: Error;
  status: number;
}> {
  return fetch(`/admin/collaboration-statuses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes a collaboration status (admin) and clears it from every company
 * answer that used it. Replaces the legacy `deleteCollaborationStatus` wrapper
 * that hit `v3/admin/collaboration-statuses/:id`. 204, no body; an unknown id is
 * 404 `collaboration_status_not_found`.
 */
export function adminCollaborationStatusesDelete(id: string) {
  return fetch(`/admin/collaboration-statuses/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Creates a relationship type (admin). Replaces the legacy
 * `createRelationShipType` wrapper that hit `v3/admin/relationship-types`.
 * A duplicate name comes back as 409 `relationship_types_already_exist`.
 */
export function adminRelationshipTypesCreate(
  body: AdminCreateRelationshipTypeBody,
): Promise<{
  data: AdminRelationshipType;
  error: Error;
  status: number;
}> {
  return fetch('/admin/relationship-types', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Renames a relationship type (admin). Replaces the legacy
 * `updateRelationshipType` wrapper that hit `v3/admin/relationship-types/:id`.
 * An unknown id is 404 `relationship_type_not_found`; a name another type
 * already has is 409 `relationship_types_already_exist`.
 */
export function adminRelationshipTypesUpdate(
  id: string,
  body: AdminUpdateRelationshipTypeBody,
): Promise<{
  data: AdminRelationshipType;
  error: Error;
  status: number;
}> {
  return fetch(`/admin/relationship-types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Admin relationship-type list — each type with how many company profiles
 * use it. Replaces the legacy `getRelationShipTypes` wrapper that hit
 * `v3/admin/relationship-types`.
 */
export function adminRelationshipTypesList(
  queryParams?: Partial<RelationshipTypesListQuery>,
): Promise<{
  data: AdminRelationshipTypesListResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/relationship-types?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Collaboration statuses for the status filters and pickers (every logged-in
 * network member). Replaces the legacy `getUserCollaborationStatuses` wrapper
 * that hit `v3/collaboration-statuses`. Rows are `{ id, name }`, and the
 * envelope is the platform's `{ metadata: { pagination }, data }`.
 */
export function collaborationStatusesList(
  queryParams?: Partial<CollaborationStatusesListQuery>,
): Promise<{
  data: CollaborationStatusesListResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/companies/collaboration-statuses?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Relationship types (BRANCH, VENDOR, DIRECT_SUPPLIER, …) for the
 * relationship-type filters and pickers. Replaces the legacy
 * `getUserRelationShipTypes` wrapper that hit `v3/relationship-types`.
 */
export function relationshipTypesList(
  queryParams?: Partial<RelationshipTypesListQuery>,
): Promise<{
  data: RelationshipTypesListResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/companies/relationship-types?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Users belonging to any of the given companies the caller may see (their own
 * and the ones they are linked to — other ids are ignored). `notAdmin: true`
 * additionally excludes Retraced-staff roles and projects `email`.
 * Replaces the legacy `userCompaniesListByCompanyIds` wrapper that hit
 * `v3/user-companies/list-by-company-ids`.
 */
export function userCompaniesListByCompanyIds(
  queryParams: Partial<ListUserCompaniesByCompanyIdsQuery>,
): Promise<{
  data: UserCompanyByCompanyIdRow[];
  error: Error;
  status: number;
}> {
  return fetch(
    `/user-companies/list-by-company-ids?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Unlinks two companies bidirectionally. Replaces the legacy `companyUnLink`
 * wrapper that hit `companies-service/v1/companies/unlink`.
 */
export function unlinkCompany(body: CompanyUnlinkBody) {
  return fetch('/companies/unlink', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Unlinks multiple companies from the caller, bidirectionally. Replaces the
 * legacy `companyBulkUnLink` wrapper that hit
 * `companies-service/v1/companies/bulk-unlink`.
 */
export function bulkUnlinkCompany(body: CompanyBulkUnlinkBody) {
  return fetch('/companies/bulk-unlink', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Lists companies in the caller's network. Replaces the legacy
 * `getCompaniesList` wrapper that hit `companies-service/v1/companies`.
 */
export function listCompanies(queryParams: Partial<CompaniesListQuery>) {
  return fetch(`/companies?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

export async function getAssetAccessControl(assetId: string): Promise<{
  data: AssetAccessControlResponse | null;
  error: unknown;
  status: number;
}> {
  const raw = (await fetch(`/assets/${assetId}/access-control`, {
    method: 'GET',
  })) as {
    data: { data: AssetAccessControlResponse } | null;
    error: unknown;
    status: number;
  };
  return { ...raw, data: raw.data?.data ?? null };
}

export function shareAsset(body: { assetId: string; companyId: string }) {
  const { assetId, companyId } = body;
  return fetch(`/assets/${assetId}/shares`, {
    method: 'POST',
    body: JSON.stringify({ companyId }),
  });
}

/**
 * A page of the company's material constants mappings, in the platform's
 * paginated envelope. Replaces the legacy `GET /v3/constants-mapping/materials`
 * (`getMaterialsConstantMapping`).
 */
export function constantsMappingMaterialsList(
  queryParams: Partial<MaterialMappingsListQuery> = {},
) {
  return fetch(
    `/constants-mapping/materials?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Maps the company's own material codes to Retraced materials, skipping codes
 * it has already mapped. Replaces the legacy
 * `POST /v3/constants-mapping/materials` (`materialsConstantMappingCreate`).
 */
export function constantsMappingMaterialsCreate(
  body: CreateMaterialMappingsBody,
) {
  return fetch('/constants-mapping/materials', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Changes a material mapping's code and, when given, its name; `data` is the
 * updated mapping. Replaces the legacy
 * `PATCH /v3/constants-mapping/materials/:id` (`materialsConstantMappingPatch`).
 */
export function constantsMappingMaterialsUpdate(
  id: string,
  body: UpdateMaterialMappingBody,
) {
  return fetch(`/constants-mapping/materials/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes those of `ids` that are the company's material mappings; others are
 * skipped. Replaces the legacy `DELETE /v3/constants-mapping/materials`
 * (`materialsConstantMappingDelete`).
 */
/**
 * A page of the company's product-type constants mappings, in the platform's
 * paginated envelope. Replaces the legacy
 * `GET /v3/constants-mapping/product-types` (`getProductCategoriesConstantMapping`).
 */
export function constantsMappingProductTypesList(
  queryParams: Partial<ProductTypeMappingsListQuery> = {},
) {
  return fetch(
    `/constants-mapping/product-types?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Maps the company's own product-type codes to Retraced product types,
 * skipping codes it has already mapped. Replaces the legacy
 * `POST /v3/constants-mapping/product-types` (`productCategoriesConstantMappingCreate`).
 */
export function constantsMappingProductTypesCreate(
  body: CreateProductTypeMappingsBody,
) {
  return fetch('/constants-mapping/product-types', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Changes a product-type mapping's code and, when given, its name; `data` is
 * the updated mapping. Replaces the legacy
 * `PATCH /v3/constants-mapping/product-types/:id` (`productCategoriesConstantMappingPatch`).
 */
export function constantsMappingProductTypesUpdate(
  id: string,
  body: UpdateProductTypeMappingBody,
) {
  return fetch(`/constants-mapping/product-types/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes those of `ids` that are the company's product-type mappings; others
 * are skipped. Replaces the legacy `DELETE /v3/constants-mapping/product-types`
 * (`productCategoriesConstantMappingDelete`).
 */
export function constantsMappingProductTypesDelete(
  body: DeleteProductTypeMappingsBody,
) {
  return fetch('/constants-mapping/product-types', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export function constantsMappingMaterialsDelete(
  body: DeleteMaterialMappingsBody,
) {
  return fetch('/constants-mapping/materials', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/**
 * Lists allowed origins across every company (admin): paged, sorted and
 * filtered, or every matching row on one page with `isDropDown`. Replaces the
 * legacy `allowedOriginsGetAll` wrapper that hit `v3/admin/allowed-origins/list`.
 * Answers the paginated `{ data, metadata: { pagination } }` envelope (1-based
 * `pagination.page`); legacy sent `{ data, meta }`.
 */
export function adminAllowedOriginsList(
  queryParams: Partial<AdminAllowedOriginsListQuery>,
): Promise<{
  data: AdminAllowedOriginsListResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/allowed-origins?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Reads one allowed origin with its company name, whichever company owns it
 * (admin). Replaces the legacy `getAllowedOrigin` wrapper that hit
 * `v3/admin/allowed-origins/:id`. 404 `allowed_origin_not_found` for an
 * unknown id. Answers `{ data, metadata }`; legacy sent the bare row.
 */
export function adminAllowedOriginGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: AdminAllowedOrigin };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/allowed-origins/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

/**
 * Creates an allowed origin, for any company or none (admin). Replaces the
 * legacy `createAllowedOrigin` wrapper that hit `v3/admin/allowed-origins`. An
 * origin that already exists comes back as 409 `allowed_origin_exists`.
 * Answers `{ data, metadata }` with 201; legacy sent the bare row.
 */
export function adminAllowedOriginCreate(
  body: AdminCreateAllowedOriginBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminAllowedOrigin };
  error: Error;
  status: number;
}> {
  return fetch('/admin/allowed-origins', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Replaces an allowed origin's fields, whichever company owns it (admin);
 * absent optional fields are written as null. Replaces the legacy
 * `updateAllowedOrigin` wrapper that hit `v3/admin/allowed-origins/:id`. 404
 * `allowed_origin_not_found` for an unknown id, 409 `allowed_origin_exists`
 * when another allowed origin has the origin. Answers `{ data, metadata }`;
 * legacy sent the bare row.
 */
export function adminAllowedOriginUpdate(
  id: string,
  body: AdminUpdateAllowedOriginBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminAllowedOrigin };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/allowed-origins/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes one allowed origin, whichever company owns it (admin). Replaces the
 * legacy `deleteAllowedOrigin` wrapper that hit `v3/admin/allowed-origins/:id`.
 * 404 `allowed_origin_not_found` for an unknown id. Answers 204 with no body.
 */
export function adminAllowedOriginDelete(
  id: string,
): Promise<{ data: undefined; error: Error; status: number }> {
  return fetch(`/admin/allowed-origins/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Deletes allowed origins by id, whichever company owns them (admin). Replaces
 * the legacy `bulkDeleteAllowedOrigins` wrapper that sent a PUT to
 * `v3/admin/allowed-origins/bulk-delete`. Unknown ids are skipped; 404
 * `allowed_origins_not_found` when none exists. Answers 204 with no body.
 */
export function adminAllowedOriginsBulkDelete(
  body: AdminBulkDeleteAllowedOriginsBody,
): Promise<{ data: undefined; error: Error; status: number }> {
  return fetch('/admin/allowed-origins', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/**
 * Every company's documents (admin): paged, sorted and filtered. Replaces the
 * legacy `getDocuments` wrapper that hit `v3/admin/company-documents`. Answers
 * the paginated `{ data, metadata: { pagination } }` envelope (1-based
 * `pagination.page`); legacy sent `{ data, meta }`. The `type` filter takes
 * comma-separated document type ids.
 */
export function adminCompanyDocumentsList(
  queryParams: Partial<AdminCompanyDocumentsListQuery>,
): Promise<{
  data: AdminCompanyDocumentsListResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/company-documents?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * The global fiber chain-of-custody models (admin), each with its materials
 * and facility processes: paged, sorted and filtered by name. Replaces the
 * legacy `managementFiberCocModelsGet` wrapper that hit
 * `v3/admin/fiber/coc-models`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function adminFiberCocModelsList(
  queryParams: Partial<AdminFiberCocModelsQuery>,
): Promise<{
  data: AdminFiberCocModelsResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/fiber/coc-models?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Replaces a fiber chain-of-custody model's materials and/or facility
 * processes (admin) — the cell editors on the CoC models page. A key left out
 * keeps its stored set; neither may be sent empty. Replaces the legacy
 * `managementFiberCocModelPatch` wrapper that hit
 * `v3/admin/fiber/coc-models/:id`. Answers `{ data, metadata }`; legacy sent
 * the bare model.
 */
export function adminFiberCocModelUpdate(
  id: string,
  body: AdminUpdateFiberCocModelBody,
) {
  return fetch(`/admin/fiber/coc-models/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminFiberCocModel>;
}

/**
 * Creates a global fiber production-process template (admin) — "new process"
 * on the fiber production processes page. Replaces the legacy
 * `managementFiberProductionProcessCreate` wrapper that hit
 * `v3/admin/fiber/production-processes`. Answers 201 with
 * `{ data, metadata }`; legacy sent the bare record.
 */
export function adminFiberProductionProcessCreate(
  body: AdminCreateFiberProductionProcessBodyInput,
) {
  return fetch('/admin/fiber/production-processes', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminFiberProductionProcess>;
}

/**
 * The fiber production factors (admin), paged — both tabs of the
 * production-factors page, narrowed by `isProductLevelOnly` or
 * `isProductTypeOnly`. Replaces the legacy `managementProductionFactorsGet`
 * wrapper that hit `v3/admin/fiber/production-factors`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`)
 * with the real total; legacy sent `{ data, meta }` with `meta.totalCount`
 * always 0.
 */
export function adminFiberProductionFactorsList(
  queryParams: Partial<AdminFiberProductionFactorsQueryInput>,
): Promise<{
  data: AdminFiberProductionFactorsResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/fiber/production-factors?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Creates one fiber production factor per facility process for a category
 * (admin) — the create drawer on the production-factors page. Replaces the
 * legacy `managementProductionFactorsCreate` wrapper that hit
 * `v3/admin/fiber/production-factors/create-by-facility-processes`. Answers
 * 201 with the new factors in `{ data, metadata }`; legacy sent a bare array.
 */
export function adminFiberProductionFactorsCreate(
  body: AdminCreateFiberProductionFactorsBody,
) {
  return fetch('/admin/fiber/production-factors', {
    method: 'POST',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminFiberProductionFactor[]>;
}

/**
 * Edits some fields of a fiber production factor (admin) — the inline edits
 * on both tabs of the production-factors page. A key left out keeps its
 * stored value. Replaces the legacy `managementProductionFactorsPatch` wrapper
 * that hit `v3/admin/fiber/production-factors/:id`. Answers
 * `{ data, metadata }`; legacy sent the bare record.
 */
export function adminFiberProductionFactorPatch(
  id: string,
  body: AdminPatchFiberProductionFactorBody,
) {
  return fetch(`/admin/fiber/production-factors/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminFiberProductionFactor>;
}

/**
 * The production factor (wastage band) of a product category and facility
 * process (admin), falling back to a level-2 category's parent — the hint in
 * the fiber production-process drawer. Replaces the legacy
 * `managementFiberProductionFactorsGet` wrapper that hit
 * `v3/admin/fiber/production-factors/:productCategoryId`. Answers
 * `{ data, metadata }` with `data: null` when there is none; legacy sent the
 * bare record or an empty body.
 */
export function adminFiberProductionFactorGet({
  productCategoryId,
  facilityProcessId,
}: AdminFiberProductionFactorQuery & { productCategoryId: string }) {
  return fetch(
    `/admin/fiber/production-factors/${encodeURIComponent(productCategoryId)}?${getDefinedUrlSearchParams({ facilityProcessId })}`,
    { method: 'GET' },
  ) as ApiResponse<AdminFiberProductionFactor | null>;
}

/**
 * The global fiber production-process templates the caller's company owns
 * (admin), paged, sorted by `name`, `facilityProcessId` or `updatedAt` and
 * filtered by `name` and `facilityProcessId`. Replaces the legacy
 * `managementFiberProductionProcessesGet` wrapper that hit
 * `v3/admin/fiber/production-processes`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function adminFiberProductionProcessesList(
  queryParams: Partial<AdminFiberProductionProcessesQuery>,
): Promise<{
  data: AdminFiberProductionProcessesResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/fiber/production-processes?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Saves a fiber production process the caller's company owns (admin) — the
 * edit drawer on the fiber production processes page. The four fields are
 * required; an image left out is kept, so the drawer, which never sends one,
 * no longer drops it. Replaces the legacy `managementFiberProductionProcessUpdate`
 * wrapper that hit `v3/admin/fiber/production-processes/:id`. Answers
 * `{ data, metadata }`; legacy sent the bare record.
 */
export function adminFiberProductionProcessUpdate(
  id: string,
  body: AdminUpdateFiberProductionProcessBody,
) {
  return fetch(`/admin/fiber/production-processes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminFiberProductionProcess>;
}

/**
 * Edits some fields of a fiber production process the caller's company owns
 * (admin) — the inline name edit on the fiber production processes page. A
 * key left out keeps its stored value. Replaces the legacy
 * `managementFiberProductionProcessPatch` wrapper that hit
 * `v3/admin/fiber/production-processes/:id`. Answers `{ data, metadata }`;
 * legacy sent the bare record.
 */
export function adminFiberProductionProcessPatch(
  id: string,
  body: AdminPatchFiberProductionProcessBody,
) {
  return fetch(`/admin/fiber/production-processes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminFiberProductionProcess>;
}

/**
 * One fiber production process by id (admin), of any company — the edit
 * drawer on the fiber production processes page. Replaces the legacy
 * `managementFiberProductionProcessGet` wrapper that hit
 * `v3/admin/fiber/production-processes/:id`. Answers `{ data, metadata }`;
 * legacy sent the bare record.
 */
export function adminFiberProductionProcessGet(id: string) {
  return fetch(`/admin/fiber/production-processes/${encodeURIComponent(id)}`, {
    method: 'GET',
  }) as ApiResponse<AdminFiberProductionProcess>;
}

/**
 * Every fiber setting (admin), ordered by key — the fiber settings page.
 * Replaces the legacy `managementFiberSettingsGet` wrapper that hit
 * `v3/admin/fiber/settings`. Not paginated: answers `{ data, metadata }`;
 * legacy sent a bare array.
 */
export function adminFiberSettingsList() {
  return fetch('/admin/fiber/settings', {
    method: 'GET',
  }) as ApiResponse<AdminFiberSetting[]>;
}

/**
 * Replaces one fiber setting's facility processes (admin) — the value editor
 * on the fiber settings page. The row is named by `key` in the body. Replaces
 * the legacy `managementFiberSettingPatch` wrapper that hit
 * `v3/admin/fiber/settings`. Answers `{ data, metadata }`; legacy sent the
 * bare setting.
 */
export function adminFiberSettingUpdate(body: AdminUpdateFiberSettingBody) {
  return fetch('/admin/fiber/settings', {
    method: 'PATCH',
    body: JSON.stringify(body),
  }) as ApiResponse<AdminFiberSetting>;
}

/**
 * The three development tags, each with the users who hold it (admin).
 * Replaces the legacy `adminDevelopmentTags` wrapper that hit
 * `v3/admin/development-tags`. Takes no parameters and is not paginated:
 * answers `{ data, metadata }`; legacy wrapped the same rows in `{ data, meta }`.
 */
export function adminDevelopmentTagsList(): Promise<{
  data: AdminDevelopmentTagsResponse;
  error: Error;
  status: number;
}> {
  return fetch('/admin/development-tags', { method: 'GET' });
}

/**
 * The features gated behind development tags (admin): paged, sorted and
 * filtered. Replaces the legacy `adminDevelopmentFeatureTagsGet` wrapper that
 * hit `v3/admin/development-tags/list-feature-tags`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }` with a `totalCount` that was always 0.
 */
export function adminDevelopmentTagFeaturesList(
  queryParams: Partial<AdminDevelopmentTagFeaturesQuery>,
): Promise<{
  data: AdminDevelopmentTagFeaturesResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/development-tags/features?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Every company's archived documents (admin), one row per archiving company:
 * same paging, sort and filter as `adminCompanyDocumentsList`. Replaces the
 * legacy `getArchivedDocuments` wrapper that hit
 * `v3/admin/company-documents-archived`. Answers the paginated
 * `{ data, metadata: { pagination } }` envelope (1-based `pagination.page`);
 * legacy sent `{ data, meta }`.
 */
export function adminCompanyDocumentsArchivedList(
  queryParams: Partial<AdminCompanyDocumentsListQuery>,
): Promise<{
  data: AdminArchivedCompanyDocumentsListResponse;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/company-documents/archived?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * Takes documents out of the archive (admin), one `(documentId, companyId)`
 * archive row per item. Replaces the legacy `companyDocumentBulkRestore`
 * wrapper that hit `v3/admin/company-documents/bulk-restore`. Answers
 * `{ data: { restored }, metadata }`; legacy sent an empty 200.
 */
export function adminCompanyDocumentsBulkRestore(
  body: AdminBulkRestoreCompanyDocumentsBody,
): Promise<{
  data: {
    metadata: { success: boolean };
    data: AdminBulkRestoreCompanyDocumentsResponse;
  };
  error: Error;
  status: number;
}> {
  return fetch('/admin/company-documents/bulk-restore', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Creates a webshop (admin). Replaces the legacy `createWebshop` wrapper that
 * hit `v3/admin/webshops`. A shop id that is already taken comes back as 409
 * `shop_id_must_be_unique`. Answers `{ data, metadata }`; legacy sent the bare
 * row.
 */
export function adminWebshopCreate(body: AdminCreateWebshopBody): Promise<{
  data: { metadata: { success: boolean }; data: AdminWebshop };
  error: Error;
  status: number;
}> {
  return fetch('/admin/webshops', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes a webshop (admin). Replaces the legacy `webshopRemove` wrapper that
 * hit `v3/admin/webshops/:id`. An unknown id is 404 `webshop_not_found`.
 */
export function adminWebshopDelete(id: string): Promise<{
  data: undefined;
  error: Error;
  status: number;
}> {
  return fetch(`/admin/webshops/${id}`, { method: 'DELETE' });
}

/**
 * Updates a webshop (admin). Replaces the legacy `webshopUpdate` wrapper that
 * hit `v3/admin/webshops/:id`. A field left out keeps its value and `null`
 * clears it; `newId` renames the shop. An unknown id is 404 `webshop_not_found`,
 * a `newId` another webshop already holds is 409 `shop_id_must_be_unique`.
 * Answers `{ data, metadata }`; legacy sent the bare row.
 */
export function adminWebshopUpdate(
  id: string,
  body: AdminUpdateWebshopBody,
): Promise<{
  data: { metadata: { success: boolean }; data: AdminWebshop };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/webshops/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/**
 * Reads one webshop (admin). Replaces the legacy `webshopGet` wrapper that hit
 * `v3/admin/webshops/:id`. An unknown id is 404 `webshop_not_found`. Answers
 * `{ data, metadata }`; legacy sent the bare row.
 */
export function adminWebshopGet(id: string): Promise<{
  data: { metadata: { success: boolean }; data: AdminWebshop };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/webshops/${id}`, { method: 'GET' });
}

/**
 * The webshop roster's filter options (admin): the companies that own a
 * webshop and every platform in use. Replaces the legacy `webshopsConstantsGet`
 * wrapper that hit `v3/admin/webshops/list-constants`. A webshop without a
 * platform no longer adds a blank entry to `shopPlatforms`. Answers
 * `{ data, metadata }`; legacy sent the bare `{ companies, shopPlatforms }`.
 */
export function adminWebshopConstants(): Promise<{
  data: { metadata: { success: boolean }; data: AdminWebshopConstantsResponse };
  error: Error;
  status: number;
}> {
  return fetch('/admin/webshops/list-constants', { method: 'GET' });
}

/**
 * The admin webshop roster (admin). Replaces the legacy `webshopsGet` wrapper
 * that hit `v3/admin/webshops/list`, which answered `{ data, meta: { count,
 * page, pageSize, totalCount } }`; this answers `{ data, metadata: { success,
 * pagination } }` where `pagination.page` is 1-based, `limit` the page size and
 * `total` the match count. The legacy `isDropDown` mode is gone.
 */
export function adminWebshopsList(
  queryParams?: Partial<AdminWebshopsListQuery>,
): Promise<{
  data: {
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
    data: AdminWebshopListRow[];
  };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/webshops?${getDefinedUrlSearchParams(queryParams)}`, {
    method: 'GET',
  });
}

/**
 * A page of classification data fields for the admin Data Fields table, or —
 * with `isDropDown` — all of them, for the data field options on the admin Link
 * Classifications page. `page` is 0-based. Replaces the legacy
 * `getClassificationDataFields` wrapper that hit
 * `api-service/v3/admin/classification-data-fields`, which answered
 * `{ data, meta }`; this answers `{ data, metadata: { pagination } }` with a
 * 1-based `pagination.page`.
 */
export function adminClassificationDataFieldsList(
  queryParams: Partial<AdminClassificationDataFieldsListQuery>,
): Promise<{
  data: {
    data: AdminClassificationDataFieldsListResponse;
    metadata: {
      pagination: { page: number; limit: number; total: number };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/classification-data-fields?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * A page of data-field ↔ impact-area links for one entity tab of the admin
 * Link Classifications page, each with its data field's title and its impact
 * area's section and name. `page` is 0-based. Replaces the legacy
 * `getClassificationImpactAreas` wrapper that hit
 * `api-service/v3/admin/classification-impact-areas`, which answered
 * `{ data, meta }` with the joined columns as `title` / `categoryHierarchyId` /
 * `categoryDescription`; this answers `{ data, metadata: { pagination } }` with
 * a 1-based `pagination.page`.
 */
export function adminClassificationImpactAreasList(
  queryParams: Partial<AdminClassificationImpactAreasListQuery>,
): Promise<{
  data: {
    data: AdminClassificationImpactAreasListResponse;
    metadata: {
      pagination: { page: number; limit: number; total: number };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/classification-impact-areas?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * The data fields linked to an impact area or to any area below it — what the
 * admin Company Dynamic Tabs editor offers for a view of that area. 404 for an
 * unknown impact area; an empty list when nothing is linked. Replaces the
 * legacy `getFieldsByCategoryId` wrapper that hit
 * `api-service/v3/admin/classification-impact-areas/fields/:categoryId`, which
 * answered the bare array (and 404 when nothing was linked); this answers
 * `{ data, metadata }`.
 */
export function adminImpactAreaDataFields(impactAreaId: string): Promise<{
  data: {
    data: AdminImpactAreaDataFieldsResponse;
    metadata: { success: boolean };
  } | null;
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/classification-impact-areas/data-fields/${encodeURIComponent(impactAreaId)}`,
    { method: 'GET' },
  );
}

/**
 * Links a data field to an impact area from the create dialog on the admin
 * Link Classifications page. 409 `impact_area_to_datafield_link_exists` when
 * the pair is already linked; 400 when no data field or impact area is picked.
 * Replaces the legacy `createClassificationImpactArea` wrapper that hit
 * `POST api-service/v3/admin/classification-impact-areas` and got 200 with the
 * bare row (and stored an unpicked impact area as NULL); this gets 201 with
 * `{ data, metadata }`.
 */
export function adminCreateClassificationImpactArea(
  body: AdminClassificationImpactAreaCreateBody,
): Promise<{
  data: {
    data: ClassificationImpactArea;
    metadata: { success: boolean };
  };
  error: Error;
  status: number;
}> {
  return fetch('/admin/classification-impact-areas', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Saves a cell edited in the admin Link Classifications table: only the keys
 * sent are written; an empty string or null clears `relationType` /
 * `description`. 409 `impact_area_to_datafield_link_exists` when the link is
 * moved onto a data field and impact area that are already linked. Replaces
 * the legacy `patchClassificationImpactArea` wrapper that hit
 * `PATCH api-service/v3/admin/classification-impact-areas/:id`, which answered
 * the pre-edit row merged with the body; this answers `{ data, metadata }`
 * with the link as stored.
 */
export function adminPatchClassificationImpactArea(
  id: string,
  body: AdminClassificationImpactAreaPatchBody,
): Promise<{
  data: {
    data: ClassificationImpactArea;
    metadata: { success: boolean };
  };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/classification-impact-areas/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Deletes data-field ↔ impact-area links from the admin Link Classifications
 * page, dropping the cached classification views they fed; unknown ids are
 * skipped. Replaces the legacy `bulkDeleteClassificationImpactArea` wrapper
 * that sent a bare array to
 * `DELETE api-service/v3/admin/classification-impact-areas/bulk-delete` and got
 * 200; this sends `{ ids }` and gets 204 with no body.
 */
export function adminBulkDeleteClassificationImpactAreas(
  body: AdminClassificationImpactAreasBulkDeleteBody,
): Promise<{ data: null; error: Error; status: number }> {
  return fetch('/admin/classification-impact-areas', {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

/**
 * Saves a cell edited in the admin Data Fields table: only the keys sent are
 * written, and an empty string or null clears `title`/`description`. Replaces
 * the legacy `patchClassificationDataFields` wrapper that hit
 * `PATCH api-service/v3/admin/classification-data-fields/:id`, which answered
 * the pre-edit row merged with the body; this answers `{ data, metadata }`
 * with the field as stored.
 */
export function adminPatchClassificationDataField(
  id: string,
  body: AdminClassificationDataFieldPatchBody,
): Promise<{
  data: {
    data: ClassificationDataField;
    metadata: { success: boolean };
  };
  error: Error;
  status: number;
}> {
  return fetch(`/admin/classification-data-fields/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * The distinct entities classification data fields belong to, alphabetically —
 * the tabs on the admin Link Classifications page and the entity options on the
 * admin Data Fields table. Replaces the legacy `getClassificationEntities`
 * wrapper that hit `api-service/v3/admin/classification-data-fields/entities`,
 * which answered a bare `[{ entity }]`; this answers `{ data: string[], metadata }`.
 */
export function adminClassificationEntities(): Promise<{
  data: {
    data: AdminClassificationEntitiesResponse;
    metadata: { success: boolean };
  };
  error: Error;
  status: number;
}> {
  return fetch('/admin/classification-data-fields/entities', { method: 'GET' });
}

/**
 * A company's dynamic profile tabs config for the admin Company Dynamic Tabs
 * editor — or the platform-wide one, with `companyId: 'ALL'`, when the company
 * has none. Replaces the legacy `getCompanyDynamicTabsConfig` wrapper that hit
 * `api-service/v3/admin/classification-company-dynamic-tabs/:companyId`, which
 * answered the bare `{ companyId, configData }`; this wraps it in
 * `{ data, metadata }`. 404 when neither exists.
 */
export function adminDynamicCompanyTabs(companyId: string): Promise<{
  data: {
    data: AdminDynamicCompanyTabsConfig;
    metadata: { success: boolean };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/ux-configs/dynamic-company-tabs/${encodeURIComponent(companyId)}`,
    { method: 'GET' },
  );
}

/**
 * Saves `body`, whole, as the company's own dynamic profile tabs config (`ALL`
 * saves the platform-wide one), creating it when the company has none.
 * Replaces the legacy `setCompanyDynamicTabsConfig` wrapper that hit
 * `PUT api-service/v3/admin/classification-company-dynamic-tabs/:companyId`,
 * which answered the bare `{ companyId, configData }`; this wraps it in
 * `{ data, metadata }`.
 */
export function adminSaveDynamicCompanyTabs(
  companyId: string,
  body: AdminDynamicCompanyTabsBody,
): Promise<{
  data: {
    data: AdminDynamicCompanyTabsConfig;
    metadata: { success: boolean };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/ux-configs/dynamic-company-tabs/${encodeURIComponent(companyId)}`,
    { method: 'PUT', body: JSON.stringify(body) },
  );
}

/**
 * Which of `companyIds` already have their own dynamic profile tabs config, so
 * the admin Company Dynamic Tabs editor can warn before overwriting them.
 * Replaces the legacy `checkDynamicTabDuplicatedConfiguration` wrapper that hit
 * `api-service/v3/admin/classification-company-dynamic-tabs/duplication`, which
 * answered the bare array; this wraps it in `{ data, metadata }`.
 */
export function adminDynamicCompanyTabsConfiguredCompanyIds(
  queryParams: AdminConfiguredCompanyIdsQuery,
): Promise<{
  data: {
    data: AdminConfiguredCompanyIdsResponse;
    metadata: { success: boolean };
  };
  error: Error;
  status: number;
}> {
  return fetch(
    `/admin/ux-configs/dynamic-company-tabs/configured-company-ids?${getDefinedUrlSearchParams(queryParams)}`,
    { method: 'GET' },
  );
}

/**
 * The whole certification catalogue, ordered by name, for the certificate
 * pickers. Replaces the legacy `certificatesGet` wrapper that hit
 * `api-service/v3/certifications`, which answered `{ data, meta }`; this
 * answers `{ data, metadata: { pagination } }` with one page holding every
 * row. Items carry only `id`, `name` and `type` — the legacy rules,
 * description, link and logo were never read.
 */
export function certificationsList(): Promise<{
  data: {
    data: CertificationOption[];
    metadata: {
      success: boolean;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  };
  error: Error;
  status: number;
}> {
  return fetch('/certifications', { method: 'GET' });
}
